import React, { createContext, Fragment, FormEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  type MarketSession,
  type MarketStatus,
  SESSION_TONE,
  useMarketStatus,
  formatTimeToOpen,
  nowInET,
} from './market-time'
import { createRoot } from 'react-dom/client'
import { I18nProvider, useI18n, getLanguage, setLanguageChangeHandler } from './i18n'
import type { MessageKey } from './i18n'
import { authDestination } from './auth-next'
import { BriefExecutiveLayer, briefSectionAnchor, type BriefBriefing, type BriefCoverage } from './BriefExecutiveLayer'
import './styles.css'
import { API_BASE, api, isSignedIn, navigate, setSignedIn, silentRefresh, type ApiError } from './api/client'
import { setEventTier, track } from './api/events'
import { EventCyclePage } from './pages/EventCyclePage'
import { fetchCycle, type CycleEvent, type CycleOverlay } from './api/cycle'
import { HoldingsPanel } from './pages/HoldingsPanel'
import { JournalPage } from './pages/JournalPage'
import { LedgerPage } from './pages/LedgerPage'
import { fetchLedgerStatus, type LedgerStatus } from './api/ledger'

const GOOGLE_CLIENT_ID = '418771450014-pl4e4q6p7ufk3tj67n238cbeibc218r5.apps.googleusercontent.com'

// Apple "Sign in with Apple" Services ID (a.k.a. web client id). Must match
// the Services ID registered in the Apple Developer portal and the audience
// added to admin_settings.auth.apple.service_id on the backend.
const APPLE_SERVICE_ID = 'com.navoption.signin.web'
const APPLE_REDIRECT_URI = 'https://navoption.com/auth/apple/return'

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initCodeClient: (config: {
            client_id: string
            scope: string
            ux_mode?: 'popup' | 'redirect'
            redirect_uri?: string
            callback: (response: { code?: string; error?: string; error_description?: string }) => void
          }) => { requestCode: () => void }
        }
      }
    }
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string
          scope: string
          redirectURI: string
          state?: string
          nonce?: string
          usePopup?: boolean
        }) => void
        signIn: () => Promise<{
          authorization: { id_token: string; code: string; state?: string }
          user?: { email?: string; name?: { firstName?: string; lastName?: string } }
        }>
      }
    }
  }
}

type Me = {
  email?: string
  tier?: string
  expires_at?: string
  user_id?: string
  id?: string
  trial_used?: boolean
  pro_mode_enabled?: boolean
  calendar_enabled?: boolean
  iv_evaluation_enabled?: boolean
  earnings_card_enabled?: boolean
}

type BillingPlansPayload = {
  plans?: BillingPlan[]
  pro_mode_enabled?: boolean
}

// ─── Entitlement helper (item 1) ────────────────────────────────────────────
// Single source of truth for Pro gating. When commerce is OFF
// (proModeEnabled=false) every signed-in user gets Pro-equivalent access.
// isPro = !proModeEnabled || tier === 'pro'
function makeEntitlement(proModeEnabled: boolean, tier: string) {
  const isPro = !proModeEnabled || tier === 'pro'
  return {
    isPro,
    proModeEnabled,
    /** True when the user needs to upgrade to access a Pro feature */
    requiresUpgrade: !isPro && proModeEnabled,
  }
}

type CalendarEventType = 'earnings' | 'exdiv' | 'option_expiry' | 'macro'
type CalendarImpact = 'H' | 'M' | 'L'

type CalendarEvent = {
  date: string
  time?: string
  type: CalendarEventType
  symbol?: string
  impact: CalendarImpact
  label: string
  meta?: Record<string, unknown>
}

type IVTermPoint = { tenor_days: number; atm_iv_pct: number }
type IVvsRVRow = {
  window_days: number
  rv_pct: number
  iv_tenor_days: number
  iv_pct: number
  premium_pp: number
}

type IVEvaluation = {
  symbol: string
  as_of: string
  term_structure?: IVTermPoint[]
  term_shape?: 'contango' | 'backwardation' | 'flat' | ''
  iv_vs_rv: IVvsRVRow[]
  summary: string
}

type EarningsWeekCardData = {
  symbol: string
  next_earnings_at: string
  session: string
  iv_crush?: {
    past_quarters_used: number
    pre_avg_pct: number
    post_avg_pct: number
    compression_pp: number
  } | null
  implied_move?: { pct: number; upper: number; lower: number; spot: number } | null
  realized_move?: {
    past_quarters_used: number
    avg_pct: number
    min_pct: number
    max_pct: number
    diff_from_implied_pp: number
  } | null
  position?: {
    shares: number
    avg_cost: number
    open_options: Array<{ side: string; strike: number; expiry: string; qty: number }>
    strike_to_implied_upper_diff?: number | null
  } | null
}

type ScanRow = {
  id: string
  symbol: string
  side: string
  change: string
  underlying: number | null
  strike: number | null
  premium: number | null
  volume: number | null
  oi: number | null
  expiration: string
  dte: number | null
  delta: number | null
  iv: string
  gap: string
  apy: string
  risk: string
  updated: string
}

type Sector = {
  name: string
  count: number
}

type LiveQuote = {
  price?: number
  changePct?: number
  netChange?: number
}

type TickerSnapshot = {
  symbol?: string
  underlying_price?: number
  lastPrice?: number
  netChange?: number
  netPercentChange?: number
}

type WheelScoreDriver = {
  kind?: 'positive' | 'neutral' | 'caution' | string
  factor?: string
  text?: string
}

type WheelScore = {
  score: number
  recommendation_level?: string
  risk_breakdown?: {
    delta_risk?: number
    liquidity_risk?: number
    volatility_risk?: number
  }
  explanation?: {
    summary?: string
    drivers?: WheelScoreDriver[]
  }
}

function wheelScoreGrade(score?: WheelScore | null): string {
  if (!score) return ''
  return score.recommendation_level || (
    score.score >= 90 ? 'S' :
    score.score >= 75 ? 'A' :
    score.score >= 60 ? 'B' :
    'C'
  )
}

function wheelScoreClass(score?: WheelScore | null): string {
  const grade = wheelScoreGrade(score)
  if (grade === 'S' || grade === 'A') return 'score-pill score-pill--strong'
  if (grade === 'B') return 'score-pill score-pill--medium'
  if (grade === 'C') return 'score-pill score-pill--low'
  return 'score-pill'
}

function wheelScoreTooltip(score?: WheelScore | null): string {
  if (!score) return ''
  const driverText = score.explanation?.drivers
    ?.map((d) => d.text)
    .filter(Boolean)
    .join(' | ')
  return [score.explanation?.summary, driverText].filter(Boolean).join(' | ')
}

const LiveQuotesContext = createContext<Record<string, LiveQuote>>({})

function useLiveQuotes(): Record<string, LiveQuote> {
  return useContext(LiveQuotesContext)
}

function useLiveQuote(symbol?: string | null): LiveQuote | undefined {
  const map = useContext(LiveQuotesContext)
  if (!symbol) return undefined
  return map[symbol.toUpperCase()]
}


function useCalendarEvents(days = 30, enabled = true): CalendarEvent[] {
  const [events, setEvents] = useState<CalendarEvent[]>([])
  useEffect(() => {
    if (!enabled) { setEvents([]); return }
    let cancelled = false
    api<{ items: CalendarEvent[] }>(`/v1/calendar/events?days=${days}`)
      .then((r) => { if (!cancelled) setEvents(r?.items ?? []) })
      .catch(() => { if (!cancelled) setEvents([]) })
    return () => { cancelled = true }
  }, [days, enabled])
  return events
}

function useIVEvaluation(symbol: string | undefined, enabled: boolean): IVEvaluation | null {
  const [data, setData] = useState<IVEvaluation | null>(null)
  useEffect(() => {
    if (!enabled || !symbol) { setData(null); return }
    let cancelled = false
    api<IVEvaluation | null>(`/v1/iv/evaluation?symbol=${encodeURIComponent(symbol)}`)
      .then((r) => {
        if (cancelled) return
        const e = r as IVEvaluation | null
        const hasContent = !!e && (
          (Array.isArray(e.term_structure) && e.term_structure.length > 0) ||
          (Array.isArray(e.iv_vs_rv) && e.iv_vs_rv.length > 0) ||
          !!e.summary
        )
        setData(hasContent ? e : null)
      })
      .catch(() => { if (!cancelled) setData(null) })
    return () => { cancelled = true }
  }, [symbol, enabled])
  return data
}

function useEarningsWeekCard(symbol: string | undefined, enabled: boolean): EarningsWeekCardData | null {
  const [data, setData] = useState<EarningsWeekCardData | null>(null)
  useEffect(() => {
    if (!enabled || !symbol) { setData(null); return }
    let cancelled = false
    api<EarningsWeekCardData | null>(`/v1/earnings/week-card?symbol=${encodeURIComponent(symbol)}`)
      .then((r) => {
        if (cancelled) return
        const e = r as EarningsWeekCardData | null
        const hasContent = !!e && typeof e.next_earnings_at === 'string' && e.next_earnings_at.length > 0
        setData(hasContent ? e : null)
      })
      .catch(() => { if (!cancelled) setData(null) })
    return () => { cancelled = true }
  }, [symbol, enabled])
  return data
}

function useFlash(value: number | null | undefined): 'flash-up' | 'flash-down' | '' {
  const [flash, setFlash] = useState<'flash-up' | 'flash-down' | ''>('')
  const prevRef = useRef<number | null | undefined>(undefined)
  useEffect(() => {
    if (value == null || !Number.isFinite(value)) return
    const prev = prevRef.current
    prevRef.current = value
    if (prev == null || prev === value) return
    setFlash(value > prev ? 'flash-up' : 'flash-down')
    const t = window.setTimeout(() => setFlash(''), 700)
    return () => window.clearTimeout(t)
  }, [value])
  return flash
}

function LiveQuotesProvider({ children }: { children: React.ReactNode }) {
  const marketStatus = useMarketStatus()
  const snapshotFallbackAllowed = marketStatus.session === 'regular'
  const [quotes, setQuotes] = useState<Record<string, LiveQuote>>({})
  const pendingRef = useRef<Record<string, LiveQuote> | null>(null)
  const flushTimerRef = useRef<number | null>(null)
  const snapshotTimerRef = useRef<number | null>(null)
  const snapshotAbortRef = useRef<AbortController | null>(null)
  const snapshotLoadingRef = useRef(false)
  const snapshotLoadedRef = useRef(false)
  const liveQuoteSeenRef = useRef(false)

  useEffect(() => {
    const wsUrl = API_BASE.replace(/^http/, 'ws') + '/v1/ws/quotes_stream'
    let ws: WebSocket | null = null
    let closed = false
    let retry = 0
    liveQuoteSeenRef.current = false
    snapshotLoadedRef.current = false

    const flush = () => {
      flushTimerRef.current = null
      const pending = pendingRef.current
      pendingRef.current = null
      if (!pending) return
      setQuotes((current) => {
        const next: Record<string, LiveQuote> = { ...current }
        let changed = false
        for (const sym in pending) {
          const merged = { ...current[sym], ...pending[sym] }
          const prev = current[sym]
          if (!prev || prev.price !== merged.price || prev.changePct !== merged.changePct || prev.netChange !== merged.netChange) {
            next[sym] = merged
            changed = true
          }
        }
        return changed ? next : current
      })
    }

    const pickNumber = (...candidates: Array<string | undefined>): number | undefined => {
      for (const raw of candidates) {
        if (raw == null || raw === '') continue
        const n = Number(raw)
        if (Number.isFinite(n)) return n
      }
      return undefined
    }

    const cancelSnapshotFallback = () => {
      if (snapshotTimerRef.current != null) {
        window.clearTimeout(snapshotTimerRef.current)
        snapshotTimerRef.current = null
      }
      if (snapshotAbortRef.current) {
        snapshotAbortRef.current.abort()
        snapshotAbortRef.current = null
      }
    }

    const mergeQuotes = (partialBySymbol: Record<string, LiveQuote>, preferExisting = false) => {
      setQuotes((current) => {
        const next: Record<string, LiveQuote> = { ...current }
        let changed = false
        for (const sym in partialBySymbol) {
          const merged = preferExisting
            ? { ...partialBySymbol[sym], ...current[sym] }
            : { ...current[sym], ...partialBySymbol[sym] }
          const prev = current[sym]
          if (!prev || prev.price !== merged.price || prev.changePct !== merged.changePct || prev.netChange !== merged.netChange) {
            next[sym] = merged
            changed = true
          }
        }
        return changed ? next : current
      })
    }

    const loadSnapshotFallback = async () => {
      if (!snapshotFallbackAllowed || liveQuoteSeenRef.current || snapshotLoadedRef.current || snapshotLoadingRef.current) return
      snapshotLoadingRef.current = true
      const controller = new AbortController()
      snapshotAbortRef.current = controller
      const fetchOne = async (sym: string) => {
        if (liveQuoteSeenRef.current || controller.signal.aborted) return null
        const row = await api<TickerSnapshot>(`/api/stock/${encodeURIComponent(sym)}`, { signal: controller.signal })
        const price = row.lastPrice ?? row.underlying_price
        if (!Number.isFinite(price as number) || (price as number) <= 0) return null
        const quote: LiveQuote = { price }
        if (Number.isFinite(row.netPercentChange as number)) quote.changePct = row.netPercentChange
        if (Number.isFinite(row.netChange as number)) quote.netChange = row.netChange
        return { sym: (row.symbol || sym).toUpperCase(), quote }
      }
      // Bound concurrency: each /api/stock call hits DuckDB, so firing all 16
      // symbols at once stampeded the backend into 502s. A pool of 4 keeps the
      // fan-out gentle (and the server-side per-symbol cache absorbs the rest).
      const fallbackQuotes: Record<string, LiveQuote> = {}
      const queue = [...TICKER_SYMBOLS]
      const worker = async () => {
        for (;;) {
          if (liveQuoteSeenRef.current || controller.signal.aborted) return
          const sym = queue.shift()
          if (!sym) return
          try {
            const item = await fetchOne(sym)
            if (item) fallbackQuotes[item.sym] = item.quote
          } catch { /* skip this symbol */ }
        }
      }
      try {
        await Promise.all(Array.from({ length: 4 }, worker))
        if (!liveQuoteSeenRef.current && Object.keys(fallbackQuotes).length > 0) {
          snapshotLoadedRef.current = true
          mergeQuotes(fallbackQuotes, true)
        }
      } finally {
        if (snapshotAbortRef.current === controller) snapshotAbortRef.current = null
        snapshotLoadingRef.current = false
      }
    }

    const scheduleSnapshotFallback = (delayMs = 4000) => {
      if (!snapshotFallbackAllowed || liveQuoteSeenRef.current || snapshotLoadedRef.current || snapshotTimerRef.current != null) return
      snapshotTimerRef.current = window.setTimeout(() => {
        snapshotTimerRef.current = null
        void loadSnapshotFallback()
      }, delayMs)
    }

    const connect = () => {
      if (closed) return
      try {
        ws = new WebSocket(wsUrl)
      } catch {
        scheduleSnapshotFallback(0)
        return
      }
      scheduleSnapshotFallback()
      ws.onopen = () => {
        retry = 0
        try { ws?.send(JSON.stringify({ token: '' })) } catch {}
      }
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data) as Record<string, string>
          const sym = String(data.symbol || '').toUpperCase()
          if (!sym) return
          const partial: LiveQuote = {}
          const last = pickNumber(data.last_price, data.regular_last_price, data.mark_price)
          if (last != null && last > 0) partial.price = last
          const pct = pickNumber(data.net_percent_change)
          if (pct != null) partial.changePct = pct
          const net = pickNumber(data.net_change)
          if (net != null) partial.netChange = net
          if (!partial.price && partial.changePct == null && partial.netChange == null) return
          liveQuoteSeenRef.current = true
          cancelSnapshotFallback()
          if (!pendingRef.current) pendingRef.current = {}
          pendingRef.current[sym] = { ...pendingRef.current[sym], ...partial }
          if (flushTimerRef.current == null) {
            flushTimerRef.current = window.setTimeout(flush, 200)
          }
        } catch {}
      }
      ws.onclose = () => {
        if (closed) return
        retry = Math.min(retry + 1, 6)
        window.setTimeout(connect, 1000 * retry)
      }
      ws.onerror = () => { try { ws?.close() } catch {} }
    }
    connect()
    return () => {
      closed = true
      if (flushTimerRef.current != null) window.clearTimeout(flushTimerRef.current)
      cancelSnapshotFallback()
      try { ws?.close() } catch {}
    }
  }, [snapshotFallbackAllowed])

  return <LiveQuotesContext.Provider value={quotes}>{children}</LiveQuotesContext.Provider>
}

type StockSnapshot = {
  symbol?: string
  sector?: string
  underlying_price?: number
  lastPrice?: number
  netChange?: number
  netPercentChange?: number
  openPrice?: number
  highPrice?: number
  lowPrice?: number
  closePrice?: number
  totalVolume?: number
  five_day_change_pct?: number
  fifty_two_week_low?: number
  fifty_two_week_high?: number
  last_option_dte?: number
  last_collected?: string
}

type SortKey = keyof Pick<ScanRow, 'symbol' | 'underlying' | 'strike' | 'premium' | 'volume' | 'oi' | 'expiration' | 'delta' | 'iv' | 'apy' | 'risk' | 'updated'>

type SortState = {
  key: SortKey
  dir: 'asc' | 'desc'
}

type StrategyRow = {
  symbol: string
  sector?: string
  price?: number
  change?: string
  signals?: string[]
  next_action?: string
  next_action_detail?: string
  data_gaps?: string[]
  csp_apy?: number
  csp_rank?: number
  csp_strike?: number
  csp_iv?: number
  csp_volume?: number
  csp_oi?: number
  cc_apy?: number
  cc_rank?: number
  cc_strike?: number
  cc_iv?: number
  cc_volume?: number
  cc_oi?: number
}

type WorkspaceCounts = {
  watchlist: number
  positions: number
  alerts: number
  presets: number
}

type BillingPlan = {
  id: string
  name: string
  kicker?: string
  badge?: string
  description?: string
  price_label: string
  price_subtext?: string
  cta_label?: string
  highlighted?: boolean
  features?: string[]
  footnote?: string
  trial_days?: number
  paid?: boolean
  stripe_price_id?: string
}

// Push is server-initiated and has no request to read ?lang= from, so the
// choice made in the header has to reach the user row. Failures are ignored on
// purpose: a signed-out visitor has no row to write to, and the switch itself
// must never appear to fail because of it.
setLanguageChangeHandler((next) => {
  api('/v1/me/language', { method: 'PUT', body: JSON.stringify({ language: next }) }).catch(() => {})
})

// apiCached layers a session-scoped TTL cache on top of `api()`. Use for
// public endpoints that are safe to re-show for a short window: homepage
// market-insights, live wheel ideas, etc. Cache is keyed by path + body
// hash so different POST payloads don't collide. Within the same browser
// tab, navigating back to / inside the TTL window skips the network call.
const apiCacheStore = new Map<string, { exp: number; v: unknown }>()
async function apiCached<T>(path: string, init: RequestInit = {}, ttlMs = 60_000): Promise<T> {
  const bodyKey = typeof init.body === 'string' ? init.body : ''
  const key = `${init.method || 'GET'} ${path} ${bodyKey}`
  // 1. In-memory map — survives in-tab navigation.
  const hit = apiCacheStore.get(key)
  if (hit && hit.exp > Date.now()) return hit.v as T
  // 2. sessionStorage — survives soft reload.
  try {
    const raw = sessionStorage.getItem(`apicache:${key}`)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed.exp === 'number' && parsed.exp > Date.now()) {
        apiCacheStore.set(key, parsed)
        return parsed.v as T
      }
    }
  } catch { /* sessionStorage may be unavailable in private mode */ }
  const v = await api<T>(path, init)
  const entry = { exp: Date.now() + ttlMs, v }
  apiCacheStore.set(key, entry)
  try { sessionStorage.setItem(`apicache:${key}`, JSON.stringify(entry)) } catch { /* ignore quota */ }
  return v
}

function formatHistoryDate(value: string | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  const date = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  return `${date} ${time}`
}

// Deterministic, non-sequential display id for history rows. Same input always
// yields the same id (stable across renders), but rows are scrambled enough
// that the sequence reveals nothing about insertion order or count.
function shortDisplayId(seed: string, prefix = 'NVO'): string {
  let h1 = 0x811c9dc5 >>> 0
  let h2 = 0xdeadbeef >>> 0
  for (let i = 0; i < seed.length; i++) {
    const c = seed.charCodeAt(i)
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0
    h2 = Math.imul(h2 ^ c, 0x85ebca6b) >>> 0
  }
  h1 = (h1 ^ (h1 >>> 13)) >>> 0
  h2 = (h2 ^ (h2 >>> 16)) >>> 0
  const body = (h1.toString(36) + h2.toString(36)).toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 8).padEnd(8, '0')
  return `${prefix}-${body}`
}

function normalizeRoute(path: string): string {
  return path.replace(/\/+$/, '') || '/'
}

// The screen the user came from. Captured here because navigate() pushes the
// new URL before the next page mounts, and C14 entry_point needs the old one.
// Null means this URL was opened directly.
let previousRoute: string | null = null

function useRoute() {
  const [route, setRoute] = useState(window.location.pathname || '/')
  const current = useRef(normalizeRoute(window.location.pathname || '/'))
  useEffect(() => {
    const onPop = () => {
      const next = window.location.pathname || '/'
      previousRoute = current.current
      current.current = normalizeRoute(next)
      setRoute(next)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  return normalizeRoute(route)
}

function symbolEntryPoint(): 'find' | 'today' | 'portfolio' | 'market' | 'deep_link' {
  const from = previousRoute
  if (from == null) return 'deep_link'
  if (from === '/scan' || from === '/scanner' || from.startsWith('/screener/')) return 'find'
  if (from === '/') return 'today'
  if (from === '/positions') return 'portfolio'
  return 'market'
}

function analyzeEntryPoint(): 'symbol_detail' | 'earnings_week' | 'portfolio' {
  const from = previousRoute
  if (from === '/positions') return 'portfolio'
  if (from === '/earnings') return 'earnings_week'
  return 'symbol_detail'
}

function App() {
  const route = useRoute()
  const { t } = useI18n()
  const [signedIn, setSignedInState] = useState(isSignedIn())
  const [tier, setTier] = useState<string>('free')
  const [trialUsed, setTrialUsed] = useState(false)
  const [meLoaded, setMeLoaded] = useState(false)
  const [configLoaded, setConfigLoaded] = useState(false)
  const [proModeEnabled, setProModeEnabled] = useState<boolean>(true)
  const [calendarEnabled, setCalendarEnabled]         = useState<boolean>(false)
  const [ivEvalEnabled, setIvEvalEnabled]             = useState<boolean>(false)
  const [earningsCardEnabled, setEarningsCardEnabled] = useState<boolean>(false)

  useEffect(() => {
    document.documentElement.dataset.theme = 'dark'
    const title = route === '/scan' || route === '/scanner'
      ? t('app.title.scan')
      : route === '/pricing'
        ? t('app.title.pricing')
        : route === '/account'
          ? t('app.title.account')
          : t('app.title.default')
    document.title = title
  }, [route, t])

  useEffect(() => {
    let cancelled = false
    api<BillingPlansPayload>('/v1/billing/plans')
      .then((data) => {
        if (cancelled) return
        if (typeof data.pro_mode_enabled === 'boolean') setProModeEnabled(data.pro_mode_enabled)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setConfigLoaded(true)
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!signedIn) { setTier('free'); setTrialUsed(false); setMeLoaded(true); setCalendarEnabled(false); setIvEvalEnabled(false); setEarningsCardEnabled(false); return }
    setMeLoaded(false)
    api<Me>('/v1/me')
      .then((me) => {
        setTier(me.tier || 'free')
        setTrialUsed(!!me.trial_used)
        if (typeof me.pro_mode_enabled === 'boolean') setProModeEnabled(me.pro_mode_enabled)
        setCalendarEnabled(!!me.calendar_enabled)
        setIvEvalEnabled(!!me.iv_evaluation_enabled)
        setEarningsCardEnabled(!!me.earnings_card_enabled)
      })
      .catch((err) => {
        setTier('free')
        setTrialUsed(false)
        setCalendarEnabled(false)
        setIvEvalEnabled(false)
        setEarningsCardEnabled(false)
        if ((err as ApiError).status === 401) {
          setSignedIn(false)
          setSignedInState(false)
        }
      })
      .finally(() => setMeLoaded(true))
  }, [signedIn])

  // C14 envelope tier, read from the same state the UI shows.
  useEffect(() => {
    setEventTier(signedIn && meLoaded ? (tier === 'pro' ? 'pro' : 'free') : 'unknown')
  }, [signedIn, meLoaded, tier])

  const onAuthChange = (value: boolean) => {
    setSignedIn(value)
    setSignedInState(value)
  }

  // When commerce is OFF, every signed-in user gets the same access as Pro.
  const isPro = !proModeEnabled || tier === 'pro'
  const showProUI = proModeEnabled

  return (
    <LiveQuotesProvider>
    <div className="app">
      {configLoaded && showProUI && tier !== 'pro' && <PromoBar signedIn={signedIn} />}
      <TickerBar />
      <Nav signedIn={signedIn} route={route} tier={tier} showProUI={showProUI} />
      <main>
        {route === '/' && <HomePage showProUI={showProUI} proModeEnabled={proModeEnabled} signedIn={signedIn} />}
        {(route === '/scan' || route === '/scanner') && <ScanPage signedIn={signedIn} />}
        {route === '/earnings' && <EarningsPage />}
        {route === '/uoa' && <UOAPage proModeEnabled={proModeEnabled} signedIn={signedIn} />}
        {route === '/watchlist' && <WatchlistPage signedIn={signedIn} isPro={isPro} calendarEnabled={calendarEnabled} />}
        {route === '/positions' && <PositionsPage signedIn={signedIn} isPro={isPro} proModeEnabled={proModeEnabled} calendarEnabled={calendarEnabled} />}
        {route === '/analyze' && (
          signedIn ? <AnalyzePage signedIn={signedIn} isPro={isPro} proModeEnabled={proModeEnabled} ivEvalEnabled={ivEvalEnabled} earningsCardEnabled={earningsCardEnabled} />
            : <AnonymousGate
                title={t('gate.ai.title')}
                body={t('gate.ai.body')}
                primaryLabel={t('common.signInCreate')}
                destination="analyze"
              />
        )}
        {route === '/news' && <NewsPage signedIn={signedIn} />}
        {route === '/events' && <EventCyclePage signedIn={signedIn} />}
        {route === '/journal' && (
          signedIn ? <JournalPage />
            : <AnonymousGate
                title={t('journal.title')}
                body={t('journal.subtitle')}
                primaryLabel={t('common.signInCreate')}
                destination="journal"
              />
        )}
        {route === '/ledger' && (
          signedIn ? <LedgerPage />
            : <AnonymousGate
                title={t('ledger.title')}
                body={t('ledger.off.body')}
                primaryLabel={t('common.signInCreate')}
                destination="ledger"
              />
        )}
        {route === '/brief' && (
          signedIn ? <BriefPage />
            : <AnonymousGate
                title={t('gate.brief.title')}
                body={t('gate.brief.body')}
                primaryLabel={t('common.signInCreate')}
                destination="brief"
              />
        )}
        {(() => {
          const sym = symbolFromRoute(route)
          return sym ? <SymbolDetailPage symbol={sym} signedIn={signedIn} /> : null
        })()}
        {route === '/wheel' && <RedirectToHome />}
        {route === '/screener/covered-calls' && <CoveredCallsLanding signedIn={signedIn} />}
        {route === '/screener/cash-secured-puts' && <CashSecuredPutsLanding signedIn={signedIn} />}
        {route === '/tools' && <ToolsIndexPage />}
        {route.startsWith('/tools/') && route.length > '/tools/'.length && <CalculatorRoute slug={route.slice('/tools/'.length)} />}
        {route === '/pricing' && proModeEnabled && <PricingPage isPro={isPro} signedIn={signedIn} trialUsed={trialUsed} trialKnown={meLoaded} proModeEnabled={proModeEnabled} />}
        {route === '/pricing' && !proModeEnabled && <RedirectToHome />}
        {route === '/login' && <AuthPage mode="login" onAuthChange={onAuthChange} />}
        {route === '/register' && <AuthPage mode="register" onAuthChange={onAuthChange} />}
        {route === '/account' && <AccountPage onAuthChange={onAuthChange} proModeEnabled={proModeEnabled} />}
        {route === '/learn' && <LearnPage />}
        {route === '/insights' && <InsightsPage />}
        {route === '/terms' && <TermsPage proModeEnabled={proModeEnabled} />}
        {route === '/privacy' && <PrivacyPage />}
        {route === '/contact' && <SupportPage />}
        {!route.startsWith('/s/') && !route.startsWith('/tools/') && !['/', '/scan', '/scanner', '/earnings', '/uoa', '/brief', '/watchlist', '/positions', '/analyze', '/news', '/events', '/journal', '/ledger', '/wheel', '/screener/covered-calls', '/screener/cash-secured-puts', '/tools', '/learn', '/insights', '/pricing', '/login', '/register', '/account', '/terms', '/privacy', '/contact'].includes(route) && <NotFound />}
      </main>
      <Footer proModeEnabled={proModeEnabled} />
    </div>
    </LiveQuotesProvider>
  )
}

function Link({ to, children, className }: { to: string; children: React.ReactNode; className?: string }) {
  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        if (to.startsWith('http') || to.startsWith('mailto:')) return
        e.preventDefault()
        navigate(to)
      }}
    >
      {children}
    </a>
  )
}

// AnonymousGate renders the sign-in CTA shown when an unsigned-in web
// visitor reaches a feature that's reserved for signed-in users (Scan,
// Analyze, screener pages). Free tier already unlocks every gated route
// — the bar is just "be a user, not a tourist".
function AnonymousGate({ title, body, primaryLabel, destination }: {
  title: string
  body: string
  primaryLabel: string
  destination: 'analyze' | 'brief' | 'journal' | 'ledger'
}) {
  const { t } = useI18n()
  return (
    <div className="page" style={{ maxWidth: 540, margin: '64px auto', textAlign: 'center', padding: '32px 24px' }}>
      <h1 style={{ marginBottom: 12 }}>{title}</h1>
      <p className="muted" style={{ marginBottom: 24 }}>{body}</p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Link to={`/login?next=${destination}`} className="button button--primary">{primaryLabel}</Link>
        <Link to="/" className="button button--secondary">{t('common.backHome')}</Link>
      </div>
    </div>
  )
}

function PromoBar({ signedIn }: { signedIn: boolean }) {
  const { t } = useI18n()
  const go = () => {
    // Stash the code so it survives the login round-trip — the AuthPage's
    // `next` parameter only carries a route name, not a query string.
    try { sessionStorage.setItem('pendingPromoCode', 'LAUNCH30') } catch {}
    navigate(signedIn ? '/account?code=LAUNCH30#promo-code-input' : '/login?next=account')
  }
  return (
    <button type="button" className="promo-bar" onClick={go}>
      <span className="promo-bar__badge">{t('promo.badge')}</span>
      <span className="promo-bar__text">
        {t('promo.text')}
      </span>
      <span className="promo-bar__cta">{signedIn ? t('promo.cta.account') : t('promo.cta.signIn')}</span>
    </button>
  )
}

const TICKER_SYMBOLS = [
  'SPY', 'QQQ', 'IWM', 'DIA',
  'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'NVDA', 'TSLA', 'AMD', 'AVGO', 'NFLX',
  'GLD', 'TLT',
]

function TickerBar() {
  const { t } = useI18n()
  // Marquee uses translateX(-50%) so the track must be at least 2× viewport
  // wide. Even at 4K (~3840px viewport) and ~180px per item, 16 symbols × 3
  // copies = 48 items ≈ 8600px guarantees no half-empty edge.
  const items = [...TICKER_SYMBOLS, ...TICKER_SYMBOLS, ...TICKER_SYMBOLS]
  return (
    <div className="ticker" aria-label={t('aria.marketSnapshot')}>
      <div className="ticker__track">
        {items.map((sym, idx) => (
          <TickerItem key={`${sym}-${idx}`} sym={sym} />
        ))}
      </div>
    </div>
  )
}

function MarketStatusPill() {
  const { language, t } = useI18n()
  const status = useMarketStatus()
  const label = status.session === 'regular'
    ? t('market.open')
    : status.session === 'preMarket'
      ? t('market.preMarket')
      : status.session === 'afterHours'
        ? t('market.afterHours')
        : t('market.closed')
  const tone = SESSION_TONE[status.session]
  let suffix = ''
  if (status.nextOpen && status.session !== 'regular') {
    suffix = formatTimeToOpen(new Date(), status.nextOpen, language)
  }
  return (
    <span className={`market-pill market-pill-${tone}`} title={suffix || label}>
      <span className="market-pill-dot" />
      <span className="market-pill-label">{label}</span>
      {suffix && <span className="market-pill-suffix">{suffix}</span>}
    </span>
  )
}

function TickerItem({ sym }: { sym: string }) {
  const live = useLiveQuote(sym)
  const flash = useFlash(live?.price)
  const hasPrice = Number.isFinite(live?.price as number)
  const hasChg = Number.isFinite(live?.changePct as number)
  return (
    <span className="ticker__item">
      <strong>{sym}</strong>
      <span className={flash}>{hasPrice ? `$${(live!.price as number).toFixed(2)}` : '—'}</span>
      {hasChg ? (
        <em className={(live!.changePct as number) < 0 ? 'down' : 'up'}>
          {`${(live!.changePct as number) >= 0 ? '+' : ''}${(live!.changePct as number).toFixed(2)}%`}
        </em>
      ) : null}
    </span>
  )
}

// One grouped nav entry: a trigger plus the links it holds.
//
// The header carries fifteen destinations when signed in, which is more than
// a single row can hold at any readable size, so the secondary ones live
// behind three groups. The trigger reports active when the current route is
// one of its children — otherwise a user inside a group would see no highlight
// anywhere in the header and lose their place.
type NavItem = { to: string; label: string; match?: (route: string) => boolean }

function NavGroup({ label, items, route }: { label: string; items: NavItem[]; route: string }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const isActive = items.some((i) => (i.match ? i.match(route) : route === i.to))

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Route changes close the panel — navigating inside a group should not leave
  // the menu hanging open over the page you just asked for.
  useEffect(() => { setOpen(false) }, [route])

  return (
    <div className="nav-group" ref={ref}>
      <button
        type="button"
        className={`nav-group__trigger ${isActive ? 'is-active' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${label} — ${t('aria.navGroup')}`}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <span className="nav-group__caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="nav-group__panel">
          {items.map((i) => (
            <Link
              key={i.to}
              to={i.to}
              className={(i.match ? i.match(route) : route === i.to) ? 'is-active' : undefined}
            >
              {i.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

// The symbol page carried no entry point of its own — it was reachable only by
// clicking a row somewhere else. One box in the header, in the same place on
// desktop and on a phone (the header wraps, the box wraps with it).
function NavSymbolSearch() {
  const { t } = useI18n()
  const [value, setValue] = useState('')
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const sym = value.trim().toUpperCase()
    if (!sym) return
    setValue('')
    navigate(`/s/${encodeURIComponent(sym)}`)
  }
  return (
    <form className="nav__search" onSubmit={submit} role="search">
      <input
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t('nav.search.placeholder')}
        aria-label={t('nav.search.label')}
      />
    </form>
  )
}

function Nav({ signedIn, route, tier, showProUI }: { signedIn: boolean; route: string; tier: string; showProUI: boolean }) {
  const { language, setLanguage, t } = useI18n()
  const active = (target: string) => {
    if (target === '/') return route === '/' || route === '/wheel'
    if (target === '/scan') return route === '/scan' || route === '/scanner'
    return route === target
  }

  return (
    <header className="nav">
      <div className="brand-row">
        <Link to="/" className="brand">
          <img src="/img/navoption-nav-mark.svg" alt="" />
          <span>NavOption</span>
          <em>{t('brand.subtitle')}</em>
        </Link>
        <MarketStatusPill />
        <NavSymbolSearch />
      </div>
      <nav className="nav__links" aria-label={t('aria.mainNav')}>
        {/* Flat: the destinations a Wheel trader opens daily. Everything else
            is one click deeper — see NavGroup for why. */}
        <Link to="/" className={active('/') ? 'is-active' : undefined}>{t('nav.research')}</Link>
        {signedIn && <Link to="/brief" className={active('/brief') ? 'is-active' : undefined}>{t('nav.brief')}</Link>}
        <Link to="/scan" className={active('/scan') ? 'is-active' : undefined}>{t('nav.scan')}</Link>
        <Link to="/analyze" className={active('/analyze') ? 'is-active' : undefined}>{t('nav.analyze')}</Link>
        {signedIn && <Link to="/positions" className={active('/positions') ? 'is-active' : undefined}>{t('nav.positions')}</Link>}
        <NavGroup label={t('nav.markets')} route={route} items={[
          { to: '/earnings', label: t('nav.earnings') },
          { to: '/uoa', label: t('nav.uoa') },
          ...(signedIn ? [{ to: '/news', label: t('nav.news') }] : []),
          { to: '/events', label: t('nav.events') },
        ]} />
        <NavGroup label={t('nav.tools')} route={route} items={[
          { to: '/tools', label: t('nav.wheelTools'), match: (r) => r.startsWith('/tools') || r.startsWith('/screener/') },
          ...(signedIn ? [{ to: '/watchlist', label: t('nav.watchlist') }, { to: '/journal', label: t('nav.journal') }, { to: '/ledger', label: t('nav.ledger') }] : []),
        ]} />
        <NavGroup label={t('nav.learn')} route={route} items={[
          { to: '/learn', label: t('nav.curriculum') },
          { to: '/insights', label: t('nav.notes') },
        ]} />
        {showProUI && tier !== 'pro' && <Link to="/pricing" className={active('/pricing') ? 'is-active' : undefined}>{t('nav.pricing')}</Link>}
        <Link to={signedIn ? '/account' : '/login'} className={`nav__account ${active(signedIn ? '/account' : '/login') ? 'is-active' : ''}`}>
          {signedIn ? t('nav.account') : t('nav.login')}
          {signedIn && tier === 'pro' && <span className={`tier-badge tier-badge--${tier}`}>{tier.toUpperCase()}</span>}
        </Link>
        <div className="language-switcher" role="group" aria-label={t('aria.language')}>
          <button type="button" className={language === 'en' ? 'is-active' : ''} onClick={() => setLanguage('en')}>{t('language.en')}</button>
          <button type="button" className={language === 'zh' ? 'is-active' : ''} onClick={() => setLanguage('zh')}>{t('language.zh')}</button>
        </div>
      </nav>
    </header>
  )
}

// ─── Live homepage widgets ──────────────────────────────────────────────


// LiveWheelIdeas fetches the top 6 cash-secured-put recommendations and
// renders them as cards directly on the homepage. Lets visitors see the
// actual product output before clicking into Scan.
type WheelIdea = {
  symbol: string
  strike: number
  underlying_price?: number
  expiration: string
  dte: number
  delta: number
  APY: number
  IV: number
  option_symbol: string
  score?: WheelScore | null
  tv_recommend_all?: number
}
function LiveWheelIdeas() {
  const [ideas, setIdeas] = useState<WheelIdea[]>([])
  const [loading, setLoading] = useState(true)
  const [side, setSide] = useState<'put' | 'call'>('put')
  useEffect(() => {
    let alive = true
    setLoading(true)
    apiCached<WheelIdea[]>('/api/wheel/recommendations', {
      method: 'POST',
      body: JSON.stringify({ side }),
    }).then((d) => {
      if (!alive) return
      setIdeas((d || []).slice(0, 6))
      setLoading(false)
    }).catch(() => {
      if (!alive) return
      setIdeas([])
      setLoading(false)
    })
    return () => { alive = false }
  }, [side])

  return (
    <section className="section live-ideas">
      <SectionHead
        kicker="Live from the scanner"
        title="What the wheel is paying right now."
        body="Top picks pulled live from the NavOption option chain. Click any card for the full thesis."
      />
      <div className="live-ideas__tabs">
        <button className={side === 'put' ? 'is-active' : ''} onClick={() => setSide('put')}>Cash-Secured Puts</button>
        <button className={side === 'call' ? 'is-active' : ''} onClick={() => setSide('call')}>Covered Calls</button>
      </div>
      {loading ? (
        <div className="live-ideas__loading">Loading today’s ideas…</div>
      ) : ideas.length === 0 ? (
        <div className="live-ideas__loading">No ideas right now — markets may be closed. Try again in a few minutes.</div>
      ) : (
        <div className="live-ideas__grid">
          {ideas.map((r, idx) => {
            const u = r.underlying_price ?? 0
            const distancePct = u > 0 ? ((r.strike - u) / u) * 100 : null
            const tv = tvVerdict(r.tv_recommend_all)
            const grade = wheelScoreGrade(r.score)
            return (
              <Link key={`${r.option_symbol || r.symbol}-${r.expiration}-${idx}`} to={`/s/${r.symbol}`} className="live-idea-card">
                <div className="live-idea-card__head">
                  <span className="live-idea-card__sym">{r.symbol}</span>
                  {r.score && (
                    <span className={wheelScoreClass(r.score)} title={wheelScoreTooltip(r.score)}>
                      {grade ? `${grade} ` : ''}{Math.round(r.score.score ?? 0)}
                    </span>
                  )}
                </div>
                <div className="live-idea-card__strike">
                  {r.strike > 0 ? (
                    <>
                      ${r.strike.toFixed(0)} {side === 'put' ? 'put' : 'call'}
                      {distancePct != null && (
                        <small className={distancePct >= 0 ? 'up' : 'down'}>
                          {' '}{distancePct >= 0 ? '+' : ''}{distancePct.toFixed(1)}%
                        </small>
                      )}
                    </>
                  ) : (
                    <span className="muted" style={{ fontWeight: 500 }}>Sign in to see strike</span>
                  )}
                </div>
                <div className="live-idea-card__apy">{fmtPct(r.APY ?? 0)}<small> APY</small></div>
                <div className="live-idea-card__meta">
                  <span><GlossaryTerm term="dte">{r.dte}d</GlossaryTerm></span>
                  <span><GlossaryTerm term="delta">Δ {r.delta.toFixed(2)}</GlossaryTerm></span>
                  <span><GlossaryTerm term="iv">IV {fmtPct(r.IV ?? 0)}</GlossaryTerm></span>
                </div>
                {tv && (
                  <div className="live-idea-card__tv">
                    <span className={tv.cls}>Tech read: {tv.label}</span>
                  </div>
                )}
              </Link>
            )
          })}
        </div>
      )}
      <p className="live-ideas__footnote">
        APY = annualized headline yield (premium ÷ collateral × 365/DTE) assuming the put expires worthless.
        Your <em>expected</em> return is lower once assignment scenarios are weighted — see <Link to="/learn#first-csp" className="link-like">Module 3</Link>.
      </p>
      <div className="live-ideas__cta">
        <Link to="/scan" className="button button--secondary">See all ideas →</Link>
      </div>
    </section>
  )
}

// FAQ accordion — short, 6 items.
function HomeFAQ() {
  const items = [
    { q: 'Is this free?', a: 'Yes, the core scanner, stock analysis, watchlist, and position tracker are free. Paid Pro tier unlocks extras (insider history, peer comparison, and volatility bands) — clearly marked on the pricing page.' },
    { q: 'Do you place trades for me?', a: 'No. NavOption is research-only. We never connect to your broker. You see the trade here, you place it yourself.' },
    { q: 'What stocks do you cover?', a: 'US stocks and ETFs with liquid options — the universe that actually supports clean wheel trades. Thinly-traded names are skipped on purpose.' },
    { q: 'What does the stock analysis review?', a: 'It reviews available filings and market data for cash flow, debt, revenue, and price volatility. Results identify risks found by the checks and show which data was unavailable. “No major issues found” applies only to the checks performed; it is not a valuation or trading recommendation.' },
    { q: 'Is the wheel actually profitable?', a: 'Returns depend on the stock, strike, volatility, assignment, and trade management. The strategy can lag strong rallies and lose money in sharp declines; Learn shows the trade-offs and loss scenarios.' },
    { q: 'iOS app?', a: 'Yes. The iOS app syncs your watchlist + positions with the web. Free on the App Store.' },
  ]
  const [open, setOpen] = useState<number | null>(0)
  return (
    <section className="section home-faq">
      <SectionHead kicker="Common questions" title="The things people ask before signing up." />
      <div className="home-faq__list">
        {items.map((it, i) => (
          <div key={i} className={`home-faq__item ${open === i ? 'is-open' : ''}`}>
            <button onClick={() => setOpen(open === i ? null : i)}>
              <span>{it.q}</span>
              <em>{open === i ? '−' : '+'}</em>
            </button>
            {open === i && <p>{it.a}</p>}
          </div>
        ))}
      </div>
    </section>
  )
}

// C1/C10/C12: density carries only the readings the server computed for that
// predicate. rv21_pct/prev_rv21_pct belong to vol_alert, value_usd to a long
// call. Anything absent is left off the row — never printed as 0.
type AttentionDensity = {
  shares?: number | null
  contracts?: number | null
  amount_usd?: number | null
  value_usd?: number | null
  vs_strike_pct?: number | null
  rv21_pct?: number | null
  prev_rv21_pct?: number | null
}

// Dates on things you actually hold. Not affected by dismiss, so it lives at
// the top level of the response rather than inside items.
type AttentionNextDate = {
  kind: 'earnings' | 'ex_div' | 'expiry'
  symbol: string
  date: string
  days_until_trading?: number | null
  label: string
}

type AttentionItem = {
  key?: string
  predicate?: string
  kind: 'assignment_pending' | 'breach' | 'roll' | 'uncovered' | 'earnings' | 'ex_div' | 'day_move'
    | 'missing_cost' | 'vol_alert' | 'long_call_expiring'
  symbol: string
  title: string
  reason?: string
  date?: string
  days_until?: number
  implied_move_pct?: number | null
  density?: AttentionDensity | null
  is_new?: boolean
  dismissed?: boolean
  source: 'watchlist' | 'position' | 'holding' | 'both' | 'market'
  position_id?: string | null
  holding_id?: string | null
  action?: string
}

type AttentionResponse = { as_of: string; items: AttentionItem[]; next_dates?: AttentionNextDate[] }

function attentionKindIcon(it: AttentionItem): string {
  switch (it.kind) {
    case 'assignment_pending': return '📬'
    case 'breach': return '⚠️'
    case 'roll': return '⏳'
    case 'uncovered': return '🔓'
    case 'earnings': return '📅'
    case 'ex_div': return '💰'
    case 'day_move': return '📉'
    case 'missing_cost': return '✏️'
    case 'vol_alert': return '🌡️'
    case 'long_call_expiring': return '📈'
    default: return '•'
  }
}

function attentionLink(it: AttentionItem): string {
  switch (it.kind) {
    case 'assignment_pending':
    case 'breach':
    case 'roll': return '/positions'
    // Holdings live inside the positions page; web has no /holdings route.
    case 'uncovered':
    case 'missing_cost':
    // T10 points at the long call itself, which also lives on /positions.
    case 'long_call_expiring': return '/positions'
    default: return `/s/${it.symbol}`
  }
}

function attentionDensityText(t: (key: MessageKey) => string, it: AttentionItem): string {
  const d = it.density
  if (!d) return ''
  const parts: string[] = []
  if (d.shares != null) parts.push(t(d.shares === 1 ? 'attention.density.share' : 'attention.density.shares').replace('{n}', d.shares.toLocaleString()))
  if (d.contracts != null) parts.push(t(d.contracts === 1 ? 'attention.density.contract' : 'attention.density.contracts').replace('{n}', String(d.contracts)))
  // value_usd and amount_usd are different readings (market value vs the money
  // the predicate is about), so a row that carries both shows both.
  const usdText = (v: number) => (v < 0 ? `-${formatNotional(-v)}` : formatNotional(v))
  if (d.value_usd != null) parts.push(usdText(d.value_usd))
  if (d.amount_usd != null) parts.push(usdText(d.amount_usd))
  if (d.vs_strike_pct != null) parts.push(t('attention.density.vsStrike').replace('{v}', d.vs_strike_pct.toFixed(1)))
  return parts.join(' · ')
}

function attentionRvText(t: (key: MessageKey) => string, it: AttentionItem): string {
  const d = it.density
  if (!d || d.rv21_pct == null || d.prev_rv21_pct == null) return ''
  return t('attention.rv21').replace('{v}', d.rv21_pct.toFixed(1)).replace('{p}', d.prev_rv21_pct.toFixed(1))
}

// "Wed Oct 15" — the weekday is part of the reading: a date band is scanned for
// which session it lands on, not just which number.
function attentionDayLabel(ymd: string, language: string): string {
  const d = new Date(`${ymd.slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return ymd
  return d.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  })
}

// One macro event and the positions that span it. The subject is the event, not
// a single position, so this never renders the same row the change feed does.
type EventExposure = {
  event: CycleEvent
  count: number
  // Notional is Σ strike × 100 × qty over the spanning option positions. Null
  // when no spanning overlay resolved to a position we hold numbers for.
  notional: number | null
  furthestDate: string
}

// Reverse-aggregates `spans_event_id`, which the server already computes per
// overlay and which no surface has ever read back the other way round.
function eventExposures(events: CycleEvent[], overlays: CycleOverlay[], positions: Position[]): EventExposure[] {
  const byId = new Map<string, CycleEvent>(events.map((e) => [e.id, e]))
  const posById = new Map<string, Position>(positions.map((pos) => [pos.id, pos]))
  const groups = new Map<string, EventExposure>()
  for (const o of overlays) {
    const ev = o.spans_event_id ? byId.get(o.spans_event_id) : undefined
    if (!ev) continue
    const cur = groups.get(ev.id) || { event: ev, count: 0, notional: null, furthestDate: o.date }
    cur.count += 1
    if (o.date > cur.furthestDate) cur.furthestDate = o.date
    const pos = o.position_id ? posById.get(o.position_id) : undefined
    if (pos) cur.notional = (cur.notional ?? 0) + pos.strike * 100 * pos.qty
    groups.set(ev.id, cur)
  }
  return [...groups.values()].sort((a, b) => a.event.occurs_at.localeCompare(b.event.occurs_at))
}

// DailyAttention is the signed-in "what needs my attention today"
// checklist: risk events on the symbols you hold. /v1/attention/today
// carries only those; the collapsed unusual-flow section reads the UOA
// feed directly and keeps its market-magnitude order. Hidden while
// loading and when there is nothing actionable, so the home page stays
// quiet for fresh accounts.
function DailyAttention() {
  const { language, t } = useI18n()
  const [risk, setRisk] = useState<AttentionItem[] | null>(null)
  const [nextDates, setNextDates] = useState<AttentionNextDate[]>([])
  const [flow, setFlow] = useState<UOAAlert[]>([])
  // Market dates and cross-event exposure both come from one 14-day cycle
  // payload: the events for the date band, the overlays for the exposure.
  const [marketEvents, setMarketEvents] = useState<CycleEvent[]>([])
  const [overlays, setOverlays] = useState<CycleOverlay[]>([])
  const [positions, setPositions] = useState<Position[]>([])
  // The ledger is opt-in: a 409 (or any failure) means there is no account
  // card to show, and the home page says so in one tile instead of guessing.
  const [ledger, setLedger] = useState<LedgerStatus | 'off' | null>(null)
  useEffect(() => {
    let cancelled = false
    fetchLedgerStatus()
      .then((st) => { if (!cancelled) setLedger(st) })
      .catch(() => { if (!cancelled) setLedger('off') })
    // 14 days, impact H only, at most 2 — a longer list is a financial
    // calendar, which the user already has a better one of.
    fetchCycle(14)
      .then((c) => {
        if (cancelled) return
        setMarketEvents(c.events || [])
        setOverlays(c.overlays || [])
      })
      .catch(() => { if (!cancelled) { setMarketEvents([]); setOverlays([]) } })
    api<{ positions: Position[] }>('/v1/positions')
      .then((r) => { if (!cancelled) setPositions(r.positions || []) })
      .catch(() => { if (!cancelled) setPositions([]) })
    api<AttentionResponse>(`/v1/attention/today?lang=${language}`)
      .then((res) => {
        if (cancelled) return
        setRisk((res.items || []).filter((it) => !it.dismissed))
        setNextDates(res.next_dates || [])
      })
      .catch(() => { if (!cancelled) setRisk([]) })
    // Today's flow, narrowed to symbols you hold or watch. Server order is
    // market magnitude and is never re-sorted here (see UOA feed rule).
    api<UOAResponse>('/v1/uoa/today?window_days=1&limit=100')
      .then((res) => { if (!cancelled) setFlow((res.alerts || []).filter((a) => a.watchlisted || a.has_position)) })
      .catch(() => { if (!cancelled) setFlow([]) })
    return () => { cancelled = true }
  }, [language])
  const marketDates = marketEvents.filter((e) => e.impact === 'H').slice(0, 2)
  const exposures = eventExposures(marketEvents, overlays, positions)
  // A "Not now" press removes the row locally after the server records it —
  // the key is the server's own opaque item key and goes on the wire as sent.
  const dismiss = async (key: string) => {
    // The row goes only after the server recorded it: a row that reappears on
    // the next load is worse than a press that visibly did nothing.
    await api(`/v1/attention/items/${key}/dismiss`, { method: 'POST' })
    setRisk((prev) => (prev || []).filter((it) => it.key !== key))
  }
  const nothing = risk != null && risk.length === 0 && flow.length === 0 && nextDates.length === 0
    && marketDates.length === 0 && exposures.length === 0
  if (!risk || (nothing && (ledger === null || ledger === 'off'))) return null
  // Earnings on a stock you hold is a different sentence from earnings on a
  // watchlist name: the server sends source=holding, the copy comes from here.
  // That sentence carries the countdown itself, so it replaces the when-chip —
  // both go through whenText so earnings day reads "today", never "0d".
  const whenText = (it: AttentionItem) => {
    if (it.days_until === 0) return t('attention.when.today')
    return it.days_until != null ? t('attention.when.inDays').replace('{d}', String(it.days_until)) : ''
  }
  const heldEarnings = (it: AttentionItem) => it.kind === 'earnings' && it.source === 'holding' && it.days_until != null
  const itemTitle = (it: AttentionItem) => {
    if (!heldEarnings(it)) return it.title
    const line = t('attention.holding.earnings').replace('{sym}', it.symbol).replace('{when}', whenText(it))
    if (it.implied_move_pct == null) return line
    return `${line} · ${t('attention.impliedMove').replace('{v}', String(Math.round(it.implied_move_pct)))}`
  }
  const renderFlowItem = (row: UOAAlert, idx: number) => (
    <li key={`${row.symbol}-${row.expiration}-${row.strike}-${row.side}-${idx}`}>
      <Link to="/uoa" className="attention-item">
        <span className="attention-item__kind" aria-hidden>⚡</span>
        <span className="attention-item__title">
          {row.symbol} {row.side.toUpperCase()} · {t('attention.flow.baseline').replace('{v}', row.multiple.toFixed(1))}
          <span className="attention-flow__tag">{t(row.has_position ? 'attention.flow.held' : 'attention.flow.watched')}</span>
        </span>
        <span className="attention-item__when">{row.notional > 0 ? formatNotional(row.notional) : ''}</span>
      </Link>
    </li>
  )
  const newBadge = (it: AttentionItem) => (it.is_new ? <span className="attention-item__new">{t('attention.new')}</span> : null)
  const dismissButton = (it: AttentionItem) => (it.key ? (
    <button type="button" className="attention-dismiss" onClick={() => { void dismiss(it.key!) }}>{t('attention.dismiss')}</button>
  ) : null)
  // RV21 crossing 25% is a market-wide reading with no position to open, so the
  // primary action is the SOP risk check itself, expanded in place. The check
  // questions and the closing line live in the server's reason (localize.go).
  const renderVolAlert = (it: AttentionItem, idx: number) => {
    const rv = attentionRvText(t, it)
    return (
      <li key={`${it.kind}-${it.symbol}-${idx}`}>
        <details className="attention-check">
          <summary className="attention-item">
            <span className="attention-item__kind" aria-hidden>{attentionKindIcon(it)}</span>
            {newBadge(it)}
            <span className="attention-item__body">
              <span className="attention-item__title">{it.title}</span>
              {rv && <span className="attention-item__density">{rv}</span>}
            </span>
            <span className="attention-item__when">{t('attention.volCheck.open')}</span>
          </summary>
          {it.reason && <p className="attention-check__reason">{it.reason}</p>}
        </details>
        {dismissButton(it)}
      </li>
    )
  }
  const renderItem = (it: AttentionItem, idx: number) => {
    if (it.kind === 'vol_alert') return renderVolAlert(it, idx)
    const density = attentionDensityText(t, it)
    return (
      <li key={`${it.kind}-${it.symbol}-${it.position_id || ''}-${idx}`}>
        <Link to={attentionLink(it)} className="attention-item">
          <span className="attention-item__kind" aria-hidden>{attentionKindIcon(it)}</span>
          {newBadge(it)}
          <span className="attention-item__body">
            <span className="attention-item__title">{itemTitle(it)}</span>
            {density && <span className="attention-item__density">{density}</span>}
          </span>
          <span className="attention-item__when">{heldEarnings(it) ? '' : whenText(it)}</span>
        </Link>
        {dismissButton(it)}
      </li>
    )
  }
  // One NVDA earnings never appears twice on one screen: a date already stated
  // as a change-feed row is dropped from the date band. Client-side only.
  const ownDates = nextDates.filter((d) => !risk.some((it) => it.symbol === d.symbol && it.kind === d.kind))
  // The handbook's daily check reads top-down: account limits first, then
  // whether any rule asks for action today — and if none does, not trading is
  // the answer, stated as such rather than as an empty list.
  const status = ledger && ledger !== 'off' ? ledger : null
  const breaches = status ? status.account_card.triggers.filter((tr) => tr.breached) : []
  const tone = breaches.length > 0 ? 'breach' : risk.length > 0 ? 'act' : 'clear'
  const verdict = tone === 'breach' ? t('attention.verdict.breach')
    : tone === 'act' ? t(risk.length === 1 ? 'attention.verdict.items1' : 'attention.verdict.items').replace('{n}', String(risk.length))
      : t('attention.verdict.clear')
  // The nearest H event gets a tile, together with the positions that span it;
  // everything else joins one date-ordered agenda instead of three bands.
  const nextEvent = marketDates[0]
  const nextExposure = nextEvent ? exposures.find((x) => x.event.id === nextEvent.id) : undefined
  type AgendaRow = { date: string; icon: string; label: string; days?: number | null; to?: string }
  const agenda: AgendaRow[] = [
    ...ownDates.map((d) => ({
      date: d.date, icon: d.kind === 'earnings' ? '📅' : d.kind === 'ex_div' ? '💰' : '⏳',
      label: d.label, days: d.days_until_trading, to: `/s/${encodeURIComponent(d.symbol)}`,
    })),
    ...marketDates.slice(1).map((e) => ({ date: e.occurs_at, icon: '🗓', label: e.label, days: e.days_until_trading })),
    ...exposures.filter((x) => x !== nextExposure).map((x) => ({
      date: x.event.occurs_at, icon: '🎯', to: '/events', days: x.event.days_until_trading,
      label: [x.event.label,
        t(x.count === 1 ? 'attention.exposure.position' : 'attention.exposure.positions').replace('{n}', String(x.count)),
        ...(x.notional != null ? [t('attention.exposure.notional').replace('{v}', formatNotional(x.notional))] : []),
        t('attention.exposure.furthest').replace('{d}', attentionDayLabel(x.furthestDate, language))].join(' · '),
    })),
  ].sort((a, b) => a.date.localeCompare(b.date))
  const renderAgendaRow = (r: AgendaRow, idx: number) => {
    const body = (
      <>
        <span className="attention-agenda__date">{attentionDayLabel(r.date, language)}</span>
        <span className="attention-item__kind" aria-hidden>{r.icon}</span>
        <span className="attention-item__title">{r.label}</span>
        <span className="attention-item__when">{r.days != null ? t('attention.inTradingDays').replace('{n}', String(r.days)) : ''}</span>
      </>
    )
    return (
      <li key={`${r.date}-${r.label}-${idx}`}>
        {r.to ? <Link to={r.to} className="attention-item">{body}</Link>
          : <span className="attention-item attention-item--static">{body}</span>}
      </li>
    )
  }
  // Worst stress row by share of budget; a row without a budget ranks by loss.
  const worstStress = status?.account_card.stress.slice()
    .sort((a, b) => (b.budget ? b.loss / b.budget : 0) - (a.budget ? a.loss / a.budget : 0) || b.loss - a.loss)[0]
  // Budgets are round user-entered numbers ($12.5K); formatNotional's whole-K
  // rounding would print that as $13K and contradict the breach line above.
  const money = (v: number) => (v >= 1000 ? `$${(v / 1000).toFixed(1).replace(/\.0$/, '')}K` : `$${Math.round(v)}`)
  const ratio = (v: number | null | undefined, pct = false) => (v == null ? '—' : pct ? `${Math.round(v * 100)}%` : `${v.toFixed(2)}×`)
  return (
    <section className="attention-card">
      <div className="attention-card__box">
        <div className="attention-card__head">
          <h2>{t('attention.today').replace('{d}', attentionDayLabel(nowInET().ymd, language))}</h2>
          <span className="muted">{t('attention.source')}</span>
        </div>
        <p className={`attention-verdict attention-verdict--${tone}`}>
          <span className="attention-verdict__dot" aria-hidden />
          <span>
            {verdict}
            {tone === 'clear' && <span className="attention-verdict__sub">{t('attention.verdict.clearSub')}</span>}
          </span>
        </p>
        {breaches.length > 0 && (
          <ul className="attention-list attention-breaches">
            {breaches.map((b) => (
              <li key={b.key}><Link to="/ledger" className="attention-item"><span className="attention-item__title">{b.why}</span><span className="attention-item__when">{t('attention.breach.next')}</span></Link></li>
            ))}
          </ul>
        )}
        {/* The rows the verdict counts sit right under it, so the number and
            its content are read together; tiles and dates are context. */}
        {risk.length > 0 && <ul className="attention-list attention-act">{risk.map(renderItem)}</ul>}
        <div className="attention-tiles">
          <Link to="/events" className="attention-tile">
            <span className="attention-tile__label">{t('attention.tile.nextEvent')}</span>
            <span className="attention-tile__value">{nextEvent ? nextEvent.label : t('attention.tile.noEvent')}</span>
            {nextEvent && (
              <span className="attention-tile__sub">
                {attentionDayLabel(nextEvent.occurs_at, language)}
                {nextEvent.days_until_trading != null && ` · ${t('attention.inTradingDays').replace('{n}', String(nextEvent.days_until_trading))}`}
                {nextExposure && (
                  <span className="attention-tile__line">
                    {t(nextExposure.count === 1 ? 'attention.tile.spans1' : 'attention.tile.spans').replace('{n}', String(nextExposure.count))}
                    {nextExposure.notional != null && ` · ${t('attention.exposure.notional').replace('{v}', formatNotional(nextExposure.notional))}`}
                  </span>
                )}
              </span>
            )}
          </Link>
          {status && (
            <Link to="/ledger" className="attention-tile">
              <span className="attention-tile__label">{t('attention.tile.scenario')}</span>
              <span className={`attention-tile__value attention-scenario--${status.framework_card.scenario}`}>
                {status.framework_card.scenario} · {status.framework_card.label}
              </span>
              <span className="attention-tile__sub">{t(`attention.scenario.means.${status.framework_card.scenario}` as MessageKey)}</span>
              <span className="attention-conds">
                {status.framework_card.conditions.map((c) => (
                  <span key={c.key} className={`attention-cond attention-cond--${c.state}`} title={c.why || undefined}>
                    {c.state === 'pass' ? '✓ ' : c.state === 'fail' ? '✗ ' : '? '}{t(`ledger.cond.${c.key}` as MessageKey)}
                  </span>
                ))}
              </span>
            </Link>
          )}
          {status && (
            <Link to="/ledger" className="attention-tile">
              <span className="attention-tile__label">{t('attention.tile.account')}</span>
              <span className="attention-tile__value" title={t('attention.tile.elHint')}>
                {t('attention.tile.exposure').replace('{v}', ratio(status.account_card.e))} · {t('attention.tile.margin').replace('{v}', ratio(status.account_card.l, true))}
              </span>
              {worstStress && (
                <span className={`attention-tile__sub${worstStress.breach ? ' attention-tile__sub--breach' : ''}`}>
                  {worstStress.budget != null
                    ? t('attention.tile.stress').replace('{v}', money(worstStress.loss)).replace('{b}', money(worstStress.budget))
                    : `${worstStress.name} · ${money(worstStress.loss)}`}
                </span>
              )}
            </Link>
          )}
          {ledger === 'off' && (
            <Link to="/ledger" className="attention-tile attention-tile--muted">
              <span className="attention-tile__label">{t('attention.tile.account')}</span>
              <span className="attention-tile__value">{t('attention.tile.ledgerOff')}</span>
              <span className="attention-tile__sub">{t('attention.tile.ledgerOffSub')}</span>
            </Link>
          )}
        </div>
        {agenda.length > 0 && (
          <div>
            <p className="attention-next__head">{t('attention.col.next')}</p>
            <ul className="attention-list">{agenda.map(renderAgendaRow)}</ul>
            <Link to="/events" className="attention-next__link">{t('attention.fullCalendar')}</Link>
          </div>
        )}
        {flow.length > 0 && (
          <details className="attention-flow">
            <summary>{t('attention.flow').replace('{n}', String(flow.length))}</summary>
            <ul className="attention-list">{flow.map(renderFlowItem)}</ul>
          </details>
        )}
      </div>
    </section>
  )
}

function HomePage({ showProUI, proModeEnabled, signedIn }: { showProUI?: boolean; proModeEnabled?: boolean; signedIn?: boolean }) {
  const { t } = useI18n()
  // Signed in, the home page is a workspace: today's check and the live scan.
  // The landing sections below are for visitors deciding whether to sign up.
  if (signedIn) {
    return (
      <>
        <MacroStrip />
        <DailyAttention />
        <LiveWheelIdeas />
      </>
    )
  }
  return (
    <>
      <section className="hero">
        <div className="hero__copy">
          <span className="eyebrow">{t('home.eyebrow')}</span>
          <h1>{t('home.headline')}</h1>
          <p>
            {t('home.body')}
          </p>
          <div className="actions">
            <Link className="button button--primary" to="/scan">{t('home.actions.scan')}</Link>
            <Link className="button button--secondary" to="/analyze">{t('home.actions.analyze')}</Link>
            {showProUI && <Link className="button button--ghost" to="/pricing">{t('home.actions.pricing')}</Link>}
          </div>
        </div>
        <DashboardPreview />
      </section>

      <LiveWheelIdeas />

      <WheelVsBuyHoldViz />

      <WheelTimelineViz />

      <section className="section">
        <SectionHead kicker="What you get" title="Pick trades you can actually stomach." />
        <div className="feature-grid">
          <Feature title="Yield with the risk in plain sight" icon="fa-chart-line" body="Every option idea shows the annualized return next to assignment odds, time-to-expiration, expected volatility, and how easy it is to get filled. Never just the premium." />
          <Feature title="Stock risks explained clearly" icon="fa-filter-circle-xmark" body="Review cash flow, debt, revenue, and price volatility. The analysis shows the figures behind each finding and calls out missing data." />
          <Feature title="One place from idea to exit" icon="fa-list-check" body="Browse ideas, review a stock, save it, then track the position with assignment-risk alerts and rolling suggestions. Web and iOS stay in sync." />
        </div>
      </section>

      <section className="section section--flush" id="research-workflow">
        <SectionHead
          kicker="How it works"
          title="Four steps from idea to managed trade."
          body="A focused wheel-trader workflow: see what's paying, pressure-test the trade, layer on today's market context, then track the position with roll-aware management."
        />
        <div className="stage-grid">
          <Stage n="01" icon="fa-magnifying-glass-chart" title="Browse" body="See the top cash-secured puts and covered calls across the option universe we track — filtered by listed annualized premium, risk, and days to expiration." />
          <Stage n="02" icon="fa-filter-circle-xmark" title="Analyze" body="Review cash flow, debt, revenue, and price volatility for any symbol. The result shows risks found by the checks and which data was available." />
          <Stage n="03" icon="fa-bolt" title="Context" body="Earnings dates, unusual options flow (big institutional bets), and the news that actually matters for your symbols — on one page." />
          <Stage n="04" icon="fa-rotate" title="Manage" body="Log your put or call. Watch the assignment risk live. When the expiration gets close, we rank the best rolls for you." />
        </div>
      </section>

      <section className="wheel-workspace">
        <SectionHead kicker="Inside the app" title="Eight tools, one wheel-trading workflow." />
        <div className="module-grid">
          <Module title="Idea Browser" accent="teal" body="The top cash-secured-put and covered-call ideas across the market, ranked by risk-adjusted yield." items={['Annual yield, distance to assignment, days to expiration, fill-ability.', 'Quality Score = yield adjusted for risk + liquidity, with the reasons listed.', 'Beginner-friendly presets: Conservative · High-yield · Covered call.']} />
          <Module title="Stock Analysis" accent="blue" body="Review cash flow, debt, revenue, and price volatility." items={proModeEnabled
            ? ['Each check shows its result and the figure behind it.', 'The analysis flags risks and identifies data it could not check.', 'Pro adds insider history, peer comparison, and a volatility band.']
            : ['Each check shows its result and the figure behind it.', 'The analysis flags risks and identifies data it could not check.', 'Insider history, peer comparison, and volatility band included.']} />
          <Module title="Stock Page" accent="amber" body="Everything about one stock on a single page." items={['Full options chain — puts and calls in two tabs.', 'Insider transactions: net shares + net $ + most recent 12.', 'What big funds own in recent 13F filings.']} />
          <Module title="Earnings Watch" accent="rose" body="Earnings dates 3–30 days ahead, with the trade context you need." items={['Estimated EPS, estimated revenue, year-over-year growth.', 'Expected move size — what options traders priced in.', 'Setup label: calm / watch / strong, so you know what to skip.']} />
          <Module title="Unusual Flow" accent="amber" body="Where institutional money is unusually active today." items={proModeEnabled
            ? ['Today’s volume vs. the 7-day average, per strike.', 'Side (call / put), how strong the signal is, why it tripped.', 'Free tier sees a teaser; Pro sees the strike prices.']
            : ['Today’s volume vs. the 7-day average, per strike.', 'Side (call / put), how strong the signal is, why it tripped.']} />
          <Module title="News" accent="rose" body="The market headlines that matter for your symbols." items={['Relevance label (high / medium / normal).', 'A "why it matters" line on each item.', 'Click any ticker to jump to its stock page.']} />
          <Module title="Watchlist" accent="green" body="Your saved symbols, in sync across web and iOS." items={proModeEnabled
            ? ['Free: up to 10 symbols. Pro: unlimited.', 'Add or remove from any stock page in one tap.', 'Drives what shows in News and Earnings Watch.']
            : ['Unlimited saved symbols while paid plans are paused.', 'Add or remove from any stock page in one tap.', 'Drives what shows in News and Earnings Watch.']} />
          <Module title="Position Tracker" accent="teal" body="Track every put and call you sell, from open to close." items={['Live days-to-expiration, stock price gap, assignment risk.', 'Running profit/loss estimate per leg.', 'When expiration gets close, ranked roll suggestions appear.']} />
        </div>
      </section>

      <section className="section">
        <SectionHead
          kicker="Why it helps"
          title="Less option-chain noise. More strategy discipline."
          body="The product does not promise returns or place trades. It helps compare setups consistently and pressure-test the thesis before you commit capital."
        />
        <div className="proof-grid">
          <Proof label="Yield math" value="Premium / DTE / collateral" body="Quality Score weights APY against delta, liquidity, and IV — explained per row with cautions." />
          <Proof label="Risk discipline" value="Assignment + event aware" body="Upcoming earnings, expected move, volatility, insider trades, and big-fund holdings sit next to the yield number." />
          <Proof label="Stock analysis" value="Clear, rule-based findings" body="Shows risks found and unavailable data. A result with no major issues applies only to the completed checks; it is not a forecast or valuation." />
          <Proof label="Workflow" value="Web ↔ iOS sync" body="Watchlist + Positions sync both ways. Same account, same saved workspace." />
        </div>
      </section>

      <HonestPositioning />

      <HomeFAQ />
    </>
  )
}

// WheelVsBuyHoldViz is a diagram, not a measurement: it shows how the two
// paths differ in mechanism. No year, index, period, or percentage is
// claimed anywhere in it, and the axes carry no labels for that reason.
function WheelVsBuyHoldViz() {
  // Payoff at expiry, not a track record: the x axis is where the stock ends
  // up, the y axis is profit or loss on the same 100 shares. Covered call =
  // shares plus the premium, flat above the strike. No dates, no totals.
  const cost = 100, strike = 110, premium = 3
  const pMin = 80, pMax = 125, yMin = -20, yMax = 25
  const x = (p: number) => 60 + ((p - pMin) / (pMax - pMin)) * 510
  const y = (v: number) => 180 - ((v - yMin) / (yMax - yMin)) * 150
  const shares = `M ${x(pMin)} ${y(pMin - cost)} L ${x(pMax)} ${y(pMax - cost)}`
  const covered = `M ${x(pMin)} ${y(pMin - cost + premium)} L ${x(strike)} ${y(strike - cost + premium)} L ${x(pMax)} ${y(strike - cost + premium)}`
  return (
    <section className="section vs-spy">
      <SectionHead
        kicker="How the two paths differ"
        title="Shares alone vs. shares with a call sold."
        body="Holding the shares owns the whole move, up or down. Selling a call against them gives up part of a rally in exchange for the option premium you collect along the way, and if you end up owning the shares, it keeps selling calls on them."
      />
      <div className="vs-spy__card">
        <svg viewBox="0 0 600 220" className="vs-spy__svg" role="img" aria-label="Profit or loss at expiry: shares alone rise and fall with the stock, shares with a call sold flatten above the strike.">
          <line x1="60" y1={y(0)} x2="570" y2={y(0)} stroke="rgba(255,255,255,0.18)" />
          <line x1={x(strike)} y1="30" x2={x(strike)} y2="185" stroke="rgba(255,255,255,0.18)" strokeDasharray="3,3" />
          <text x={x(strike) + 6} y="42" fontSize="10" fill="rgba(255,255,255,0.55)">Strike</text>
          <path d={shares} stroke="#94a3b8" strokeWidth="2" fill="none" />
          <path d={covered} stroke="#34d399" strokeWidth="2" fill="none" />
          <text x="60" y="205" fontSize="10" fill="rgba(255,255,255,0.5)">Stock price at expiry →</text>
          <text x="60" y="24" fontSize="10" fill="rgba(255,255,255,0.5)">Profit / loss at expiry</text>
        </svg>
        <div className="vs-spy__legend">
          <span><i className="dot dot--gray" /> Shares</span>
          <span><i className="dot dot--green" /> Covered call</span>
        </div>
      </div>
    </section>
  )
}

// WheelTimelineViz illustrates one full wheel cycle on a representative
// stock — 90 days, three cash events. Not a backtest, an illustration —
// kept honest so a savvy reader doesn't dismiss the page on first scroll.
function WheelTimelineViz() {
  // Three premium pulses + one optional assignment at day 90.
  return (
    <section className="section wheel-viz">
      <SectionHead kicker="Sample cycle" title="A typical 90-day wheel cycle on AAPL." body="Real numbers from today's option chain. Top track: puts expire worthless. Bottom track: assignment branch when the stock dips below your strike." />
      <div className="wheel-viz__card">
        <svg viewBox="0 0 600 220" className="wheel-viz__svg" aria-hidden>
          {/* baseline */}
          <line x1="40" y1="170" x2="580" y2="170" stroke="rgba(255,255,255,0.2)" />
          {/* time labels */}
          <text x="40" y="195" fill="rgba(255,255,255,0.55)" fontSize="11">Day 0</text>
          <text x="200" y="195" fill="rgba(255,255,255,0.55)" fontSize="11">Day 30</text>
          <text x="370" y="195" fill="rgba(255,255,255,0.55)" fontSize="11">Day 60</text>
          <text x="540" y="195" fill="rgba(255,255,255,0.55)" fontSize="11">Day 90</text>
          {/* events */}
          {[
            { x: 60, label: 'Sell $200 put', amount: '+$250', desc: 'Premium hits the account today' },
            { x: 230, label: 'Put expires worthless', amount: '$250 kept', desc: 'Stock held above $200 — start the next cycle' },
            { x: 400, label: 'Sell $200 put', amount: '+$260', desc: 'Slightly higher IV → bigger premium this time' },
            { x: 560, label: 'Put expires worthless', amount: '$510 banked', desc: '90-day total on $20K collateral · ~2.6% / ~10.3% APY' },
          ].map((e, i) => (
            <g key={i}>
              <line x1={e.x} y1="170" x2={e.x} y2="60" stroke="rgba(52,211,153,0.4)" strokeWidth="1.5" />
              <circle cx={e.x} cy="60" r="6" fill="#34d399" />
              <text x={e.x} y="45" textAnchor="middle" fill="#34d399" fontSize="13" fontWeight="600">{e.amount}</text>
              <text x={e.x} y="28" textAnchor="middle" fill="rgba(255,255,255,0.78)" fontSize="11">{e.label}</text>
              {e.desc && <text x={e.x} y="148" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10">{e.desc}</text>}
            </g>
          ))}
        </svg>
        <p className="wheel-viz__caveat">
          Top track: puts expire worthless, you keep $510 on $20K (~10.3% APY). Bottom track shows the assignment branch.
        </p>

        {/* Assignment branch — the case the previous version of this page hid in fine print */}
        <svg viewBox="0 0 600 160" className="wheel-viz__svg" style={{ marginTop: 8 }} aria-hidden>
          <line x1="40" y1="120" x2="580" y2="120" stroke="rgba(255,255,255,0.2)" />
          <text x="40" y="142" fill="rgba(255,255,255,0.55)" fontSize="11">Day 0</text>
          <text x="200" y="142" fill="rgba(255,255,255,0.55)" fontSize="11">Day 30</text>
          <text x="370" y="142" fill="rgba(255,255,255,0.55)" fontSize="11">Day 60</text>
          <text x="540" y="142" fill="rgba(255,255,255,0.55)" fontSize="11">Day 90</text>
          {[
            { x: 70, label: 'Sell $200 put', amount: '+$250', color: '#34d399', desc: '' },
            { x: 230, label: 'Stock dips to $190', amount: 'Assigned', color: '#fbbf24', desc: '100 sh @ $200' },
            { x: 395, label: 'Sell $210 call', amount: '+$180', color: '#34d399', desc: 'Now in CC leg' },
            { x: 540, label: 'Mark-to-market', amount: '−$570', color: '#f87171', desc: 'Paper loss on shares' },
          ].map((e, i) => (
            <g key={i}>
              <line x1={e.x} y1="120" x2={e.x} y2="55" stroke={e.color} strokeOpacity="0.5" strokeWidth="1.5" />
              <circle cx={e.x} cy="55" r="6" fill={e.color} />
              <text x={e.x} y="40" textAnchor="middle" fill={e.color} fontSize="13" fontWeight="600">{e.amount}</text>
              <text x={e.x} y="22" textAnchor="middle" fill="rgba(255,255,255,0.78)" fontSize="11">{e.label}</text>
              {e.desc && <text x={e.x} y="98" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="10">{e.desc}</text>}
            </g>
          ))}
        </svg>
        <p className="wheel-viz__caveat">
          Bottom track: AAPL drops 3% past your strike. You’re assigned at $200, sell a $210 call for another $180,
          but you’re mark-to-market down ~$570 on the shares. This is the trade-off you’re accepting when you sell the
          put. Honest stress-test scenarios in <Link to="/learn#crash" className="link-like">Module 8</Link>.
        </p>
      </div>
    </section>
  )
}

function HonestPositioning() {
  return (
    <section className="section honest-positioning">
      <SectionHead kicker="What we don’t do" title="The honest list — so you know what you’re getting." />
      <div className="honest-grid">
        <div>
          <h4>We don’t connect to your brokerage</h4>
          <p>Research-only. You see the trade here, you place it in your broker. No keys, no trade execution, no surprises.</p>
        </div>
        <div>
          <h4>We don’t promise returns</h4>
          <p>Results depend on the stock, strike, volatility, assignment, and trade management. The wheel can lag strong rallies and lose money in sharp declines. <Link to="/learn#wheel-vs-buyhold" className="link-like">See the comparison</Link>.</p>
        </div>
        <div>
          <h4>We don’t hide the losses</h4>
          <p>Every Symbol Detail page shows the loss scenarios alongside the yield. The Learn section walks through what happens in a March-2020-style crash.</p>
        </div>
        <div>
          <h4>We don’t auto-train ML on your data</h4>
          <p>Stock analysis runs general-purpose models with explicit prompts. We don’t fine-tune on user behavior and we don’t build a profile of you.</p>
        </div>
      </div>
    </section>
  )
}

function DashboardPreview() {
  return (
    <aside className="preview-panel" aria-label="Options income research preview">
      <div className="panel-top">
        <span>Research board</span>
        <strong>NVDA · today</strong>
      </div>
      <PreviewRow label="Scan" value="NVDA 32D CSP $170" meta="APY 38% · Score 78" hot />
      <PreviewRow label="Analyze" value="Stock analysis" meta="No major issues found · 6/6 checks" />
      <PreviewRow label="Earnings" value="Reports in 9d" meta="Implied move ±6.8%" />
      <PreviewRow label="UOA" value="3 unusual strikes" meta="4.2× baseline" />
      <PreviewRow label="Position" value="Watching DTE / gap%" meta="Roll drawer ready" />
    </aside>
  )
}

const WATCHLIST_KEY = 'navoption.web.watchlist'

function readWatchlist(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(WATCHLIST_KEY) || '[]')
    return Array.isArray(raw) ? raw.map(String) : []
  } catch {
    return []
  }
}

// Cash a single contract ties up: a cash-secured put needs strike × 100 in
// collateral, a covered call needs shares you already own instead of cash.
// The side comes from the scan that produced the rows — ScanRow.side falls
// back to "Put" whenever the backend omits the column, so it cannot be used.
function cashPerContract(row: ScanRow, side: 'put' | 'call'): number | null {
  if (side === 'call') return null
  return row.strike == null ? null : row.strike * 100
}

function cashText(value: number | null, hide: boolean, sharesLabel: string) {
  if (hide) return 'Locked'
  return value == null ? sharesLabel : `$${Math.round(value).toLocaleString()}`
}

function Scanner({ initialSide, signedIn = true }: { initialSide?: 'put' | 'call'; signedIn?: boolean }) {
  const { t } = useI18n()
  const anonymous = !signedIn
  const initialUrlSymbol = (() => {
    if (anonymous) return ''
    if (typeof window === 'undefined') return ''
    const q = new URLSearchParams(window.location.search)
    return (q.get('symbol') || '').toUpperCase()
  })()
  const initialUrlSide = (() => {
    if (initialSide === 'put' || initialSide === 'call') return initialSide
    if (typeof window === 'undefined') return 'put' as const
    const q = new URLSearchParams(window.location.search)
    const s = (q.get('side') || '').toLowerCase()
    return s === 'call' ? 'call' : 'put'
  })()
  const [side, setSide] = useState<'put' | 'call'>(initialUrlSide)
  const [mode, setMode] = useState<'contract' | 'symbol'>(initialUrlSymbol ? 'symbol' : 'contract')
  const [symbol, setSymbol] = useState(initialUrlSymbol)
  const [minDte, setMinDte] = useState(30)
  const [maxDte, setMaxDte] = useState(60)
  const [minDelta, setMinDelta] = useState(initialUrlSide === 'call' ? 0.15 : -0.3)
  const [maxIv, setMaxIv] = useState(120)
  const [maxCash, setMaxCash] = useState<number | null>(null)
  const [maxDelta, setMaxDelta] = useState<number | null>(null)
  const [minApy, setMinApy] = useState(0)
  const [minOi, setMinOi] = useState(100)
  const [minVolume, setMinVolume] = useState(10)
  const [includeLeverage, setIncludeLeverage] = useState(true)
  const [sector, setSector] = useState('all')
  const [sectors, setSectors] = useState<Sector[]>([])
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<ScanRow[]>([])
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<SortState>({ key: 'apy', dir: 'desc' })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [watchlist, setWatchlist] = useState<string[]>(readWatchlist)
  const [drawerSymbol, setDrawerSymbol] = useState<string | null>(null)
  const [message, setMessage] = useState('Run a scan against the live backend.')
  const [relaxHint, setRelaxHint] = useState<{ field: string; suggested: number; rows: number } | null>(null)
  const [overviewRows, setOverviewRows] = useState<StrategyRow[]>([])
  const [overviewLoading, setOverviewLoading] = useState(false)
  const [overviewSort, setOverviewSort] = useState<'csp_rank' | 'cc_rank' | 'symbol'>('csp_rank')
  const [overviewFilter, setOverviewFilter] = useState<'all' | 'signals'>('all')
  const [overviewSector, setOverviewSector] = useState('all')
  const [overviewDte, setOverviewDte] = useState('35-55')
  const liveQuotes = useLiveQuotes()

  const buildScanPayload = (): Record<string, unknown> => {
    if (anonymous) {
      return {
        minDte: 30,
        maxDte: 60,
        minDelta: side === 'call' ? 0.3 : -0.3,
        maxIV: 120,
        include_leverage: 1,
        side,
        minVolume: 10,
        minOI: 100,
        minAPY: 0,
        sortBy: 'SCORE',
      }
    }
    const payload: Record<string, unknown> = {
      minDte,
      maxDte,
      minDelta,
      maxIV: maxIv,
      include_leverage: includeLeverage ? 1 : 0,
      side,
      minVolume,
      minOI: minOi,
      minAPY: minApy,
      sortBy: 'SCORE',
    }
    if (maxDelta != null) payload.maxDelta = maxDelta
    if (symbol.trim()) payload.symbolSearch = symbol.trim().toUpperCase()
    if (!symbol.trim() && sector !== 'all') payload.sectors = [sector]
    return payload
  }

  const toggleSide = (next: 'put' | 'call') => {
    if (anonymous) return
    if (next === side) return
    setSide(next)
    setMinDelta((prev) => {
      const abs = Math.abs(prev || 0.3)
      return next === 'call' ? abs : -abs
    })
  }

  const lastPayloadRef = useRef<string>('')

  const scan = async (e?: FormEvent) => {
    e?.preventDefault()
    setLoading(true)
    setMessage('Scanning live option chains...')
    try {
      const payload = buildScanPayload()
      lastPayloadRef.current = JSON.stringify(payload)
      const data = await api<{ headers?: string[]; data?: unknown[][]; relax_hint?: { field: string; suggested: number; rows: number } }>('/filter_options', {
        method: 'POST',
        body: lastPayloadRef.current,
      })
      const normalized = normalizeScan(data)
      setRows(normalized)
      // /filter_options caps the result set at 100 rows.
      track('scan_run', { side, result_count: normalized.length, truncated: normalized.length >= 100 })
      // apy_min is not a user-exposed filter on web, so an apy_min hint would
      // render an Apply button that can't act — drop those hints here.
      const hint = data.relax_hint ?? null
      setRelaxHint(normalized.length === 0 && hint && hint.field !== 'apy_min' ? hint : null)
      setPage(1)
      setMessage(
        normalized.length
          ? `${normalized.length} live candidates loaded.`
          : 'No candidates matched these filters.',
      )
    } catch (err) {
      setRows([])
      setRelaxHint(null)
      setMessage((err as Error).message || 'Scan failed.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    scan()
    // Initial scan should use default filters once (symbol pre-filled from ?symbol= if present).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    api<{ sectors?: Sector[] }>('/api/sectors')
      .then((data) => setSectors((data.sectors || []).slice(0, 14)))
      .catch(() => setSectors([]))
  }, [])

  useEffect(() => {
    if (mode !== 'symbol') return
    const [min, max] = (anonymous ? '35-55' : overviewDte).split('-')
    setOverviewLoading(true)
    api<StrategyRow[]>(`/api/strategy-overview?min_dte=${min}&max_dte=${max}`)
      .then((data) => setOverviewRows(Array.isArray(data) ? data : []))
      .catch(() => setOverviewRows([]))
      .finally(() => setOverviewLoading(false))
  }, [mode, overviewDte, anonymous])

  const filteredRows = useMemo(() => {
    const q = anonymous ? '' : query.trim().toUpperCase()
    return rows
      .filter((row) => !q || row.symbol.includes(q))
      .filter((row) => {
        if (maxCash == null) return true
        const cash = cashPerContract(row, side)
        return cash == null || cash <= maxCash
      })
      .sort((a, b) => compareRows(a, b, sort))
  }, [rows, query, sort, anonymous, maxCash, side])

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize))
  const safePage = Math.min(page, pageCount)
  const visibleRows = filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize)

  const setSortKey = (key: SortKey) => {
    setSort((current) => ({
      key,
      dir: current.key === key && current.dir === 'desc' ? 'asc' : 'desc',
    }))
  }

  const toggleWatch = (id: string) => {
    setWatchlist((current) => {
      const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
      localStorage.setItem(WATCHLIST_KEY, JSON.stringify(next))
      return next
    })
  }

  // Item 7: apply a single relax-hint filter change before re-running scan
  const applyRelaxHint = (hint: { field: string; suggested: number }) => {
    switch (hint.field) {
      case 'iv_max': setMaxIv(hint.suggested); break
      case 'apy_min': /* minAPY is not user-exposed in filter state — no-op */ break
      case 'dte': setMaxDte(hint.suggested); break
      case 'delta': setMinDelta(hint.suggested); break
    }
    setRelaxHint(null)
  }

  const relaxHintCopy = (field: string, suggested: number, rows: number): string => {
    switch (field) {
      case 'iv_max': return `Raising the IV cap to ${suggested} would show ${rows} contract${rows !== 1 ? 's' : ''}.`
      case 'apy_min': return `Lowering the minimum APY to ${suggested}% would show ${rows} contract${rows !== 1 ? 's' : ''}.`
      case 'dte': return `Extending the max DTE to ${suggested} days would show ${rows} contract${rows !== 1 ? 's' : ''}.`
      case 'delta': return `Adjusting the delta threshold to ${suggested} would show ${rows} contract${rows !== 1 ? 's' : ''}.`
      default: return `Relaxing filters would show ${rows} contract${rows !== 1 ? 's' : ''}.`
    }
  }

  const applyPreset = (name: 'safe' | 'high' | 'cc' | 'clear') => {
    if (anonymous) return
    setSymbol('')
    setSector('all')
    if (name === 'safe') {
      setSide('put')
      setMinDte(30)
      setMaxDte(45)
      setMinDelta(-0.2)
      setMaxIv(45)
      setIncludeLeverage(false)
    } else if (name === 'high') {
      setSide('put')
      setMinDte(14)
      setMaxDte(45)
      setMinDelta(-0.4)
      setMaxIv(150)
      setIncludeLeverage(true)
    } else if (name === 'cc') {
      setSide('call')
      setMinDte(21)
      setMaxDte(45)
      setMinDelta(0.2)
      setMaxIv(120)
      setIncludeLeverage(true)
    } else {
      setMinDte(30)
      setMaxDte(60)
      setMinDelta(side === 'call' ? 0.3 : -0.3)
      setMaxIv(120)
      setIncludeLeverage(true)
    }
  }

  return (
    <section className="scanner" id="scanner">
      <SectionHead kicker="Live scanner" title="Options income scanner." />
      <div className="mode-toggle" role="tablist" aria-label="Scanner view mode">
        <button type="button" className={mode === 'contract' ? 'is-active' : ''} onClick={() => setMode('contract')}>
          <i className="fa-solid fa-table-list" /> By Contract
        </button>
        <button type="button" className={mode === 'symbol' ? 'is-active' : ''} onClick={() => setMode('symbol')}>
          <i className="fa-solid fa-layer-group" /> By Symbol
        </button>
      </div>
      {mode === 'symbol' ? (
        <SymbolOverview
          rows={overviewRows}
          loading={overviewLoading}
          sectors={sectors}
          sort={overviewSort}
          setSort={setOverviewSort}
          filter={overviewFilter}
          setFilter={setOverviewFilter}
          sector={overviewSector}
          setSector={setOverviewSector}
          dte={overviewDte}
          setDte={setOverviewDte}
          openSymbol={setDrawerSymbol}
          quotes={liveQuotes}
          hideStrikes={anonymous}
          filtersLocked={anonymous}
        />
      ) : (
        <>
      <div className="side-toggle" role="tablist" aria-label="Option side">
        <button type="button" className={side === 'put' ? 'is-active' : ''} onClick={() => toggleSide('put')} disabled={anonymous}>
          Cash-Secured Put
        </button>
        <button type="button" className={side === 'call' ? 'is-active' : ''} onClick={() => toggleSide('call')} disabled={anonymous}>
          Covered Call
        </button>
      </div>
      <div className="scanner-presets">
        <button type="button" onClick={() => applyPreset('safe')} disabled={anonymous}>Conservative CSP</button>
        <button type="button" onClick={() => applyPreset('high')} disabled={anonymous}>High-Vol CSP</button>
        <button type="button" onClick={() => applyPreset('cc')} disabled={anonymous}>Covered Call</button>
        <button type="button" onClick={() => applyPreset('clear')} disabled={anonymous}>Clear Filters</button>
      </div>
      <div className="sector-strip" aria-label="Sector filters">
        <button className={sector === 'all' ? 'is-active' : ''} type="button" onClick={() => setSector('all')} disabled={anonymous}>All</button>
        {sectors.map((item) => (
          <button
            className={sector === item.name ? 'is-active' : ''}
            type="button"
            key={item.name}
            onClick={() => setSector(item.name)}
            disabled={anonymous}
          >
            {item.name} <span>{item.count}</span>
          </button>
        ))}
      </div>
      <form className="scanner-form" onSubmit={scan}>
        <label>
          Symbol
          <input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} placeholder="Optional ticker" disabled={anonymous} />
        </label>
        <label>
          Min DTE
          <input type="number" value={minDte} onChange={(e) => setMinDte(Number(e.target.value))} disabled={anonymous} />
        </label>
        <label>
          Max DTE
          <input type="number" value={maxDte} onChange={(e) => setMaxDte(Number(e.target.value))} disabled={anonymous} />
        </label>
        <label>
          Min Delta
          <input type="number" step="0.01" value={minDelta} onChange={(e) => setMinDelta(Number(e.target.value))} disabled={anonymous} />
        </label>
        <label>
          Max IV
          <input type="number" value={maxIv} onChange={(e) => setMaxIv(Number(e.target.value))} disabled={anonymous} />
        </label>
        <label>
          {t('scan.maxCash')}
          {side === 'call' && <small className="muted"> {t('scan.maxCash.putsOnly')}</small>}
          <input
            type="number"
            min="0"
            step="100"
            value={side === 'call' ? '' : maxCash ?? ''}
            onChange={(e) => { setMaxCash(e.target.value.trim() === '' ? null : Number(e.target.value)); setPage(1) }}
            disabled={anonymous || side === 'call'}
          />
        </label>
        <label className="switch-label">
          Lev. ETFs
          <button
            type="button"
            className={`switch ${includeLeverage ? 'is-on' : ''}`}
            onClick={() => setIncludeLeverage((value) => !value)}
            aria-pressed={includeLeverage}
            disabled={anonymous}
          >
            <span />
          </button>
        </label>
        <button className="button button--primary" type="submit" disabled={loading || anonymous}>{loading ? 'Scanning...' : 'Scan'}</button>
      </form>
      <p className="muted small scanner-form__note">{t('scan.maxCash.note')}</p>
      <div className="table-toolbar">
        <p className="scanner-message">{message}</p>
        <label>
          Search
          <input value={query} onChange={(e) => { setQuery(e.target.value); setPage(1) }} placeholder="Filter loaded symbols" disabled={anonymous} />
        </label>
        <label>
          Rows
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
      </div>
      <div className="scanner-table-wrap">
        <table className="scanner-table">
          <thead>
            <tr>
              <th aria-label="Watchlist" />
              <SortableTh label="Symbol" column="symbol" sort={sort} onSort={setSortKey} align="left" />
              <SortableTh label="Strike" column="strike" sort={sort} onSort={setSortKey} />
              <th className="text-right">{t('scan.cash')}</th>
              <SortableTh label="Premium" column="premium" sort={sort} onSort={setSortKey} />
              <SortableTh label="Volume" column="volume" sort={sort} onSort={setSortKey} />
              <SortableTh label="OI" column="oi" sort={sort} onSort={setSortKey} />
              <SortableTh label="Expiration" column="expiration" sort={sort} onSort={setSortKey} align="left" />
              <SortableTh label="Delta" column="delta" sort={sort} onSort={setSortKey} />
              <SortableTh label="IV" column="iv" sort={sort} onSort={setSortKey} />
              <SortableTh label="APY%" column="apy" sort={sort} onSort={setSortKey} />
              <SortableTh label="Risk" column="risk" sort={sort} onSort={setSortKey} />
              <SortableTh label="Updated" column="updated" sort={sort} onSort={setSortKey} align="left" />
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, idx) => (
              <tr key={row.id}>
                <td>
                  <button className="star-btn" type="button" onClick={() => toggleWatch(row.id)} aria-label={`Watch ${row.symbol}`}>
                    <i className={`${watchlist.includes(row.id) ? 'fa-solid' : 'fa-regular'} fa-star`} />
                  </button>
                </td>
                <td className="symbol-cell">
                  {/* rank_position is 1-based across pages, in the order shown. */}
                  <button type="button" onClick={() => {
                    track('scan_result_opened', { symbol: row.symbol, side, rank_position: (safePage - 1) * pageSize + idx + 1 })
                    setDrawerSymbol(row.symbol)
                  }}>{row.symbol}</button>
                  <SymbolPriceLine
                    fallbackPrice={row.underlying}
                    fallbackChange={row.change}
                    live={liveQuotes[row.symbol]}
                  />
                </td>
                <td className="mono text-right">{strikeText(row.strike, anonymous)} {!anonymous && <small>{row.gap ? `(${row.gap})` : ''}</small>}</td>
                <td className="mono text-right">{cashText(cashPerContract(row, side), anonymous, t('scan.cash.shares'))}</td>
                <td className="mono text-right">{money(row.premium)}</td>
                <td className="mono text-right">{compact(row.volume)} <Liquidity value={row.volume} /></td>
                <td className="mono text-right">{compact(row.oi)}</td>
                <td className="mono">{shortExpiration(row.expiration)} <span className="dte-badge">{row.dte ?? '-'}d</span></td>
                <td className={`mono text-right ${Number(row.delta || 0) < 0 ? 'down' : 'up'}`}>{number(row.delta, 2)}</td>
                <td className="mono text-right up">{row.iv || '-'}</td>
                <td className="mono text-right up">{row.apy || '-'}</td>
                <td className="text-center"><span className={riskClass(row.risk)}>{riskLabel(row.risk)}</span></td>
                <td className="mono muted">{timeLabel(row.updated)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && visibleRows.length === 0 && <LoadingState variant="table" rows={8} label="Loading scan..." />}
        {!loading && visibleRows.length === 0 && (
          <div className="scanner-empty">
            <p>No contracts match the current filters.</p>
            {relaxHint ? (
              <p className="relax-hint">
                {relaxHintCopy(relaxHint.field, relaxHint.suggested, relaxHint.rows)}
                {' '}
                <button
                  type="button"
                  className="button button--ghost button--small"
                  onClick={() => {
                    applyRelaxHint(relaxHint)
                    scan()
                  }}
                >Apply</button>
              </p>
            ) : (
              <p><button type="button" className="button button--ghost button--small" onClick={() => {
                setMaxIv(120); setMinDte(30); setMaxDte(60); setMinDelta(side === 'call' ? 0.15 : -0.3); scan()
              }}>Reset filters</button></p>
            )}
          </div>
        )}
      </div>
      <div className="pager">
        <span>{filteredRows.length} contracts · page {safePage} / {pageCount}</span>
        <div>
          <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={safePage <= 1}>Prev</button>
          <button type="button" onClick={() => setPage((value) => Math.min(pageCount, value + 1))} disabled={safePage >= pageCount}>Next</button>
        </div>
      </div>
      </>
      )}
      {drawerSymbol && <SymbolDrawer symbol={drawerSymbol} onClose={() => setDrawerSymbol(null)} hideStrikes={anonymous} />}
    </section>
  )
}

function SymbolOverview({
  rows,
  loading,
  sectors,
  sort,
  setSort,
  filter,
  setFilter,
  sector,
  setSector,
  dte,
  setDte,
  openSymbol,
  quotes,
  hideStrikes = false,
  filtersLocked = false,
}: {
  rows: StrategyRow[]
  loading: boolean
  sectors: Sector[]
  sort: 'csp_rank' | 'cc_rank' | 'symbol'
  setSort: (value: 'csp_rank' | 'cc_rank' | 'symbol') => void
  filter: 'all' | 'signals'
  setFilter: (value: 'all' | 'signals') => void
  sector: string
  setSector: (value: string) => void
  dte: string
  setDte: (value: string) => void
  openSymbol: (symbol: string) => void
  quotes: Record<string, LiveQuote>
  hideStrikes?: boolean
  filtersLocked?: boolean
}) {
  const filtered = useMemo(() => {
    const activeSector = filtersLocked ? 'all' : sector
    const activeFilter = filtersLocked ? 'all' : filter
    const scoped = rows.filter((row) => (activeSector === 'all' || row.sector === activeSector) && (activeFilter === 'all' || (row.signals || []).length > 0))
    return [...scoped].sort((a, b) => {
      if (sort === 'symbol') return a.symbol.localeCompare(b.symbol)
      return Number(b[sort] || 0) - Number(a[sort] || 0)
    })
  }, [rows, sector, filter, sort, filtersLocked])

  const signalCount = filtered.filter((row) => (row.signals || []).length > 0).length
  const cspMedian = median(filtered.map((row) => row.csp_apy || 0).filter(Boolean))
  const ccMedian = median(filtered.map((row) => row.cc_apy || 0).filter(Boolean))
  const cspActions = filtered.filter((row) => row.next_action === 'CSP entry review').length
  const ccActions = filtered.filter((row) => row.next_action === 'Covered-call review').length
  const fullWheelActions = filtered.filter((row) => row.next_action === 'Full wheel shortlist').length

  return (
    <div className="symbol-overview">
      <div className="overview-toolbar">
        <label>Sort
          <select value={sort} onChange={(e) => setSort(e.target.value as 'csp_rank' | 'cc_rank' | 'symbol')} disabled={filtersLocked}>
            <option value="csp_rank">CSP Score</option>
            <option value="cc_rank">CC Score</option>
            <option value="symbol">Symbol</option>
          </select>
        </label>
        <label>Sector
          <select value={sector} onChange={(e) => setSector(e.target.value)} disabled={filtersLocked}>
            <option value="all">All sectors</option>
            {sectors.map((item) => <option key={item.name} value={item.name}>{item.name}</option>)}
          </select>
        </label>
        <label>DTE
          <select value={dte} onChange={(e) => setDte(e.target.value)} disabled={filtersLocked}>
            <option value="15-35">15-35</option>
            <option value="35-55">35-55</option>
            <option value="55-90">55-90</option>
          </select>
        </label>
        <button type="button" className={filter === 'all' ? 'is-active' : ''} onClick={() => setFilter('all')} disabled={filtersLocked}>All</button>
        <button type="button" className={filter === 'signals' ? 'is-active' : ''} onClick={() => setFilter('signals')} disabled={filtersLocked}>Signals {signalCount ? `(${signalCount})` : ''}</button>
      </div>
      <div className="overview-stats">
        <span><b>{filtered.length}</b> underlyings</span>
        <span><b>{signalCount}</b> signals</span>
        <span><b>{fullWheelActions}</b> full wheel</span>
        <span><b>{cspActions}</b> CSP next</span>
        <span><b>{ccActions}</b> CC next</span>
        <span><b>{cspMedian ? `${cspMedian.toFixed(1)}%` : '-'}</b> median CSP APY</span>
        <span><b>{ccMedian ? `${ccMedian.toFixed(1)}%` : '-'}</b> median CC APY</span>
      </div>
      <div className="overview-table-wrap">
        {loading ? <div className="scanner-empty">Loading symbol overview...</div> : (
          <table className="overview-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Sector</th>
                <th className="text-right">CSP APY</th>
                <th className="text-right">CSP Strike</th>
                <th className="text-right">CSP IV</th>
                <th className="text-right">CSP Score</th>
                <th className="text-right">CC APY</th>
                <th className="text-right">CC Strike</th>
                <th className="text-right">CC IV</th>
                <th className="text-right">CC Score</th>
                <th className="text-left">Next step</th>
                <th className="text-center">Signal</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.symbol}>
                  <td className="symbol-cell">
                    <button type="button" onClick={() => openSymbol(row.symbol)}>{row.symbol}</button>
                    <SymbolPriceLine
                      fallbackPrice={row.price ?? null}
                      fallbackChange={row.change}
                      live={quotes[row.symbol]}
                    />
                  </td>
                  <td>{row.sector || 'Other'}</td>
                  <td className="mono text-right up">{percent(row.csp_apy)}</td>
                  <td className="mono text-right">{legMoney(row.csp_apy, row.csp_strike, hideStrikes)}</td>
                  <td className="mono text-right">{integerPct(row.csp_iv)}</td>
                  <ScoreCell rank={row.csp_rank} />
                  <td className="mono text-right up">{percent(row.cc_apy)}</td>
                  <td className="mono text-right">{legMoney(row.cc_apy, row.cc_strike, hideStrikes)}</td>
                  <td className="mono text-right">{integerPct(row.cc_iv)}</td>
                  <ScoreCell rank={row.cc_rank} />
                  <td className="overview-action-cell">
                    <span className={overviewActionClass(row.next_action)}>{row.next_action || 'Review'}</span>
                    {row.next_action_detail && <small>{row.next_action_detail}</small>}
                    {row.data_gaps?.length ? <em>Missing: {row.data_gaps.join(', ')}</em> : null}
                  </td>
                  <td className="text-center">{row.signals?.length ? row.signals.map((signal) => <span className="signal-chip" key={signal}>{signal}</span>) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function SymbolPriceLine({
  fallbackPrice,
  fallbackChange,
  live,
}: {
  fallbackPrice: number | null | undefined
  fallbackChange?: string
  live?: LiveQuote
}) {
  const price = live?.price ?? (fallbackPrice ?? null)
  const flash = useFlash(live?.price)
  let chgText = ''
  let chgNeg = false
  if (live && Number.isFinite(live.changePct as number)) {
    const v = live.changePct as number
    chgNeg = v < 0
    chgText = `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`
  } else if (fallbackChange && fallbackChange !== '-') {
    chgText = fallbackChange
    chgNeg = fallbackChange.trim().startsWith('-')
  }
  if (price == null && !chgText) {
    return <span className="symbol-sub muted">—</span>
  }
  return (
    <span className="symbol-sub mono">
      <span className={`symbol-sub__price ${flash}`}>{price == null ? '—' : money(price)}</span>
      {chgText ? (
        <span className={`symbol-sub__chg ${chgNeg ? 'down' : 'up'}`}>{chgText}</span>
      ) : null}
    </span>
  )
}

function ScoreCell({ rank }: { rank?: number }) {
  const score = Math.max(0, Math.min(100, Math.round(rank || 0)))
  return (
    <td className="score-cell">
      {rank ? (
        <span className={`score-pill ${score >= 70 ? 'score-pill--top' : score >= 50 ? 'score-pill--mid' : ''}`}>
          <b>{score}</b><i><em style={{ width: `${score}%` }} /></i>
        </span>
      ) : '-'}
    </td>
  )
}

function overviewActionClass(action?: string): string {
  const key = (action || '').toLowerCase()
  if (key.includes('full wheel') || key.includes('csp')) return 'overview-action-pill overview-action-pill--green'
  if (key.includes('covered-call')) return 'overview-action-pill overview-action-pill--blue'
  if (key.includes('caution') || key.includes('skip')) return 'overview-action-pill overview-action-pill--amber'
  return 'overview-action-pill'
}

function percent(value?: number) {
  return value ? `${value.toFixed(1)}%` : '-'
}

function integerPct(value?: number) {
  return value ? `${Math.round(value)}%` : '-'
}

function strikeText(value?: number | null, hide = false) {
  if (hide) return 'Locked'
  return money(value ?? null)
}

function legMoney(apy?: number, value?: number, hide = false) {
  if (hide && apy) return 'Locked'
  return apy ? money(value ?? null) : '-'
}

function median(values: number[]) {
  if (!values.length) return 0
  const sorted = [...values].sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)]
}

function ScanPage({ initialSide, signedIn = true }: { initialSide?: 'put' | 'call'; signedIn?: boolean } = {}) {
  return (
    <>
      <MacroStrip />
      <MarketRegimeBar />
      <Scanner initialSide={initialSide} signedIn={signedIn} />
    </>
  )
}

// OptionsPayoffCard — Phase 4.1 minimum viable payoff curve. Renders P/L at
// expiry for a short put (CSP) or short call (CC) using intrinsic value
// only; no live Greeks. Self-contained inline SVG so we don't pull in a
// chart dep.
function OptionsPayoffCard({ side, strike, premium, underlying }: {
  side: 'CSP' | 'CC' | string
  strike: number
  premium: number
  underlying: number
}) {
  if (!strike || !underlying) return null
  const low = underlying * 0.7
  const high = underlying * 1.3
  const steps = 60
  const dx = (high - low) / steps
  const points: { x: number; y: number }[] = []
  for (let i = 0; i <= steps; i++) {
    const p = low + dx * i
    const pnl = side === 'CC' ? premium - Math.max(p - strike, 0) : premium - Math.max(strike - p, 0)
    points.push({ x: p, y: pnl })
  }
  const ys = points.map((pt) => pt.y)
  const yMin = Math.min(...ys, 0)
  const yMax = Math.max(...ys, 0)
  const W = 320, H = 140
  const xScale = (x: number) => ((x - low) / (high - low)) * W
  const yScale = (y: number) => H - ((y - yMin) / (yMax - yMin || 1)) * H
  const path = points.map((pt, i) => (i === 0 ? 'M' : 'L') + xScale(pt.x).toFixed(2) + ',' + yScale(pt.y).toFixed(2)).join(' ')
  const zeroY = yScale(0)
  return (
    <div className="payoff-card">
      <div className="payoff-card__title">Payoff at expiry</div>
      <svg className="payoff-card__svg" viewBox={`0 0 ${W} ${H}`} aria-label="Payoff curve">
        <line x1={0} x2={W} y1={zeroY} y2={zeroY} stroke="rgba(255,255,255,0.15)" strokeDasharray="3,3" />
        <path d={path} fill="none" stroke="rgba(134,239,172,0.9)" strokeWidth={2} />
      </svg>
      <div className="payoff-card__legend">
        <span>Strike ${strike.toFixed(2)}</span>
        <span>Premium ${premium.toFixed(2)}</span>
        <span>{side}</span>
      </div>
    </div>
  )
}

type FundamentalsConcept = {
  concept: string
  value: number
  unit: string
  period_end: string
  fiscal_year?: number
  fiscal_period?: string
}

type FundamentalsSnapshot = {
  symbol: string
  updated: string
  concepts: FundamentalsConcept[]
}

type CotPositioning = {
  symbol: string
  report_date: string
  commercial_long: number
  commercial_short: number
  noncomm_long: number
  noncomm_short: number
  net_noncommercial: number
}

type MarketValuation = {
  observed_at: string
  shiller_pe: number
  buffett_indicator_pct: number
  buffett_label: string
}

type FXRate = {
  base: string
  quote: string
  observed_at: string
  rate: number
}

type MacroIndicator = {
  key: string
  value: number
  prev?: number
  prev_as_of?: string
  change?: number
  change_unit: 'pct' | 'bp' | 'pt'
  as_of: string
  monthly?: boolean
  next_fomc?: string
  rolled?: boolean
}

const MACRO_KEYS = ['wti', 'brent', 'gold', 'us10y', 't10y2y', 'breakeven_10y', 'cpi_yoy', 'core_cpi_yoy', 'fed_funds', 'vix', 'dxy'] as const
type MacroKey = typeof MACRO_KEYS[number]

// MacroStrip is the row of macro readings on top of the signed-in home page and
// the scan pages, from /v1/macro/indicators. Each tile names the date its value
// belongs to: TradingView closes settle the same evening, FRED a day later,
// CPI monthly. Renders nothing until the endpoint answers with something.
function MacroStrip() {
  const { language, t } = useI18n()
  const [items, setItems] = useState<MacroIndicator[]>([])
  const [open, setOpen] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    api<{ items: MacroIndicator[] }>('/v1/macro/indicators')
      .then((d) => { if (alive) setItems((d?.items || []).filter((it) => (MACRO_KEYS as readonly string[]).includes(it.key))) })
      .catch(() => {})
    return () => { alive = false }
  }, [])
  if (!items.length) return null
  const locale = language === 'zh' ? 'zh-CN' : 'en-US'
  const day = (ymd: string, monthly?: boolean) => {
    const d = new Date(`${ymd.slice(0, 10)}T12:00:00Z`)
    if (Number.isNaN(d.getTime())) return ymd
    return d.toLocaleDateString(locale, monthly
      ? { year: 'numeric', month: 'short', timeZone: 'UTC' }
      : { month: 'short', day: 'numeric', timeZone: 'UTC' })
  }
  const bp = t('macro.unit.bp')
  // Chinese sets a unit tight against its number ("2基点"); English spaces it.
  const gap = language === 'zh' ? '' : ' '
  const tvSourced = (k: string) => ['wti', 'brent', 'gold', 'us10y', 't10y2y', 'vix', 'dxy'].includes(k)
  const value = (it: MacroIndicator) => {
    switch (it.key) {
      case 'wti': case 'brent': return '$' + it.value.toFixed(2)
      case 'gold': return '$' + it.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      case 't10y2y': { const x = it.value * 100; return (x < 0 ? '−' : '+') + Math.abs(x).toFixed(0) + gap + bp }
      case 'vix': case 'dxy': return it.value.toFixed(2)
      // CPI is published to one decimal; more would be false precision.
      default: return it.value.toFixed(it.monthly ? 1 : 2) + '%'
    }
  }
  // The move as a neutral arrow and a size — no colour, because up is not
  // good and down is not bad for most of these. CPI shows the prior month's
  // reading; a roll session shows no change, since it spans two contracts.
  const change = (it: MacroIndicator) => {
    if (it.rolled) return t('macro.rollDay')
    if (it.monthly && it.prev != null && it.prev_as_of) {
      const m = new Date(`${it.prev_as_of.slice(0, 10)}T12:00:00Z`).toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' })
      return t('macro.prev').replace('{v}', `${m} ${it.prev.toFixed(1)}%`)
    }
    if (it.change == null) return '—'
    if (it.change === 0) return t('macro.unch')
    const a = Math.abs(it.change)
    // A move too small for the usual precision gets one more place, so it
    // never reads as a placeholder zero.
    const size = it.change_unit === 'pct' ? a.toFixed(a < 0.05 ? 2 : 1) + '%'
      : it.change_unit === 'bp' ? a.toFixed(a < 0.5 ? 1 : 0) + gap + bp
      : a.toFixed(2) + gap + t('macro.unit.pt')
    return (it.change > 0 ? '▲ ' : '▼ ') + size
  }
  const subline = (it: MacroIndicator) =>
    it.key === 'fed_funds' && it.next_fomc ? t('macro.nextFomc').replace('{d}', day(it.next_fomc)) : change(it)
  const changeLine = (it: MacroIndicator) =>
    !it.monthly && !it.rolled && it.change != null && it.prev_as_of
      ? t(tvSourced(it.key) ? 'macro.vsClose' : 'macro.vsReading').replace('{c}', change(it)).replace('{d}', day(it.prev_as_of))
      : change(it)
  const selected = items.find((it) => it.key === open)
  const source = (k: string) => k === 'wti' || k === 'brent' ? t('macro.source.oil')
    : k === 'gold' ? t('macro.source.gold')
    : tvSourced(k) ? t('macro.source.tv') : 'FRED'
  // The session most tiles share is written once in the header; a tile on
  // another date carries its own.
  const counts = new Map<string, number>()
  items.filter((it) => !it.monthly).forEach((it) => counts.set(it.as_of, (counts.get(it.as_of) || 0) + 1))
  const common = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0]
  const ownDate = (it: MacroIndicator) => it.monthly
    ? t('macro.monthData').replace('{d}', day(it.as_of, true))
    : t('macro.asOf').replace('{d}', day(it.as_of))
  return (
    <section className="macro-strip" aria-label={t('macro.title')}>
      <p className="macro-strip__head">
        {t('macro.title')}
        {common && <span className="macro-strip__asof">{t('macro.header').replace('{d}', day(common))}</span>}
      </p>
      <div className="macro-strip__row">
        {items.map((it) => (
          <button
            type="button"
            key={it.key}
            className={'macro-tile' + (it.key === 'fed_funds' ? ' macro-tile--wide' : '') + (open === it.key ? ' is-open' : '')}
            aria-expanded={open === it.key}
            onClick={() => setOpen(open === it.key ? null : it.key)}
          >
            <span className="macro-tile__label">{t(`macro.label.${it.key as MacroKey}`)}</span>
            <span className="macro-tile__value">{value(it)}</span>
            <span className="macro-tile__change">{subline(it)}</span>
            {it.as_of !== common && <span className="macro-tile__date">{ownDate(it)}</span>}
          </button>
        ))}
      </div>
      {selected && (
        <p className="macro-strip__detail">
          <strong>{changeLine(selected)}</strong>{' '}
          {t(`macro.meaning.${selected.key as MacroKey}`)}{' '}
          {selected.change_unit === 'bp' ? t('macro.bpNote') + ' ' : ''}
          <span className="macro-strip__source">
            {t('macro.source')
              .replace('{d}', day(selected.as_of, selected.monthly))
              .replace('{s}', source(selected.key))}
          </span>
        </p>
      )}
    </section>
  )
}

const REGIME_STATES = ['recovery', 'stress_up', 'calm_up', 'bear', 'correction'] as const

type RegimeState = typeof REGIME_STATES[number]

type MarketRegime = {
  as_of: string
  state: RegimeState
  dd_pct: number
  rv21_pct: number
  spy_close: number
  ma200: number
}

// MarketRegimeBar puts today's Wheel SOP market state above the scanner.
// /v1/market/regime answers 503 until daily_closes holds 201 SPY rows, and a
// state bar without a state would be worse than none — so any failure renders
// nothing at all rather than a placeholder.
function MarketRegimeBar() {
  const { language, t } = useI18n()
  const [regime, setRegime] = useState<MarketRegime | null>(null)
  useEffect(() => {
    let alive = true
    api<MarketRegime>('/v1/market/regime')
      .then((r) => { if (alive) setRegime(r) })
      .catch(() => { if (alive) setRegime(null) })
    return () => { alive = false }
  }, [])
  // A state this build has no copy for would render an empty label, so an
  // unknown value is treated like no data at all.
  if (!regime || !REGIME_STATES.includes(regime.state)) return null
  const asOfDay = new Date(`${regime.as_of.slice(0, 10)}T00:00:00`)
  const asOf = Number.isNaN(asOfDay.getTime())
    ? regime.as_of
    : asOfDay.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', { month: 'short', day: 'numeric' })
  const facts = [
    t(regime.spy_close > regime.ma200 ? 'regime.above' : 'regime.below'),
    t('regime.dd').replace('{v}', Math.abs(regime.dd_pct).toFixed(1)),
    t('regime.rv').replace('{v}', regime.rv21_pct.toFixed(0)),
  ].join(' · ')
  return (
    <div className="regime-bar">
      <p className="regime-bar__head">
        <span className={'macro-chip regime-chip regime-chip--' + regime.state}>
          {t('regime.title')} · {t(`regime.state.${regime.state}`)}
        </span>
        <span className="muted">· {t('regime.asOf').replace('{d}', asOf)}</span>
      </p>
      <p className="muted">{facts}</p>
      <p className="regime-bar__sop">{t('regime.sop')} {t(`regime.sop.${regime.state}`)}</p>
      <p className="muted small">{t('regime.sop.source')}</p>
    </div>
  )
}

// ─── Plain-English glossary ─────────────────────────────────────────────
// One-line definitions for every acronym / jargon term that appears in
// the UI. Used by <GlossaryTerm term="apy">APY</GlossaryTerm> to render
// an underlined word with a hover/tap popover.
const GLOSSARY: Record<string, string> = {
  apy: 'Annualized yield — the return you’d earn if you could repeat this trade every year. Most option trades hit max profit in 30–60 days, so APY is a way to compare different durations.',
  delta: 'Roughly the probability the option finishes in-the-money. For a put seller, a delta of −0.20 means ~20% chance of assignment. Lower = safer, lower yield.',
  dte: 'Days to expiration. Shorter DTE = faster decay (good for sellers) but less premium.',
  iv: 'Implied volatility — how much the market expects the stock to move, expressed as an annual %. High IV = expensive options = better income for sellers.',
  oi: 'Open interest — how many of this option contract are currently held. Higher = easier to enter and exit at a fair price.',
  volume: 'Contracts traded today on this option contract. A sudden jump versus normal volume can signal unusual interest.',
  notional: 'Estimated dollar value of the option flow. Larger notional needs context before it becomes a signal.',
  multiple: 'Today’s option volume divided by its recent average. Higher multiple means the contract is trading far above normal activity.',
  'uoa-priority': 'How much attention this UOA row deserves. High = strongest UOA, Medium = worth checking, Watchlist = monitor only.',
  premium: 'The cash the option buyer pays you upfront. Yours to keep no matter what happens.',
  spread: 'The gap between bid and ask. Wider = harder to get a good fill.',
  csp: 'Cash-secured put — you sell a put and set aside cash to buy the stock at the strike if assigned. You get paid for waiting to buy at a discount.',
  cc: 'Covered call — you own 100 shares and sell a call against them. You collect premium; if the stock runs past the strike, your shares get called away.',
  wheel: 'Sell cash-secured puts on a stock you’re happy to own. If you get assigned the shares, switch to selling covered calls. Repeat. That’s the wheel.',
  assignment: 'When the option buyer exercises their right and you have to deliver — buy the shares (put) or sell your shares (call).',
  itm: 'In-the-money — the strike is on the profitable side of the current price for the option holder. Higher assignment risk.',
  otm: 'Out-of-the-money — the strike is on the safer side for the seller. Lower premium, lower assignment risk.',
  atm: 'At-the-money — strike is very close to the current stock price.',
  rv: 'Realized volatility — how much the stock has actually moved historically. If RV is much lower than IV, options are richly priced.',
  contango: 'Long-dated options cost more than short-dated. Means traders see uncertainty later, not now. Normal for most stocks.',
  backwardation: 'Short-dated options cost more than long-dated. Usually signals a known event coming up — earnings, FDA decision, court ruling.',
  tv: 'Tech read — an independent trend/momentum check. Use it as confirmation or conflict against the Quality Score; it does not change the quality grade.',
  'second-opinion': 'A separate cross-check that compares NavOption’s option-income ranking with an independent technical read.',
  '13f': 'A quarterly SEC filing where institutional investors with >$100M list every US stock they own. Lags by 45 days but reveals what big funds hold.',
  uoa: 'Unusual options activity — much higher contract volume than usual on a specific strike. Often interpreted as institutional bets.',
  theta: 'How much an option loses each day from time passing alone. Sellers love theta; buyers fight it.',
  'iv-rank': 'Where current implied volatility sits inside its own recent range. Higher IV rank usually means richer option premium.',
  'atm-iv': 'At-the-money implied volatility — IV measured near the current stock price, often used as the cleanest “current IV” read.',
  'flow-read': 'NavOption’s plain-English read of unusual-options flow. It summarizes what to check next instead of treating every large trade as bullish.',
  'block-trade': 'A large single trade or print. Size matters, but it still needs context: moneyness, expiry, repeat flow, and trend.',
  'follow-rate': 'How often prior alerts on this symbol were followed by favorable movement. It is a historical tendency, not a guarantee.',
  'income-score': 'Quality Score is NavOption’s 0–100 rating for this option setup. S/A = strongest, B = workable, C = lower quality. It blends yield, assignment cushion, liquidity, and IV richness. Tech read is separate and does not change this grade.',
  'implied-move': 'The move option traders are pricing in for the next event (usually earnings), derived from at-the-money call + put prices.',
  'iv-crush': 'IV typically falls hard right after earnings — the “unknown” is gone. Selling options before earnings, then closing after, captures this drop.',
}

function GlossaryTerm({ term, children, focusable = true }: { term: keyof typeof GLOSSARY | string; children: React.ReactNode; focusable?: boolean }) {
  const def = GLOSSARY[term as string]
  if (!def) return <>{children}</>
  return (
    <span className="glossary-term" tabIndex={focusable ? 0 : undefined}>
      {children}
      <span className="glossary-term__pop" role="tooltip">{def}</span>
    </span>
  )
}

function normalizeScan(data: { headers?: string[]; data?: unknown[][] }): ScanRow[] {
  const headers = data.headers || []
  const idx = (name: string) => headers.findIndex((h) => h.toLowerCase() === name.toLowerCase())
  const get = (row: unknown[], name: string, fallback = '') => {
    const i = idx(name)
    return String(i >= 0 ? row[i] ?? fallback : fallback)
  }
  return (data.data || []).map((row, index) => {
    const symbol = String(row[0] || get(row, 'Sym.') || get(row, 'Symbol')).toUpperCase()
    const side = get(row, 'Side', 'Put')
    const strike = toNum(get(row, 'Strike'))
    const expiration = get(row, 'ExpDate')
    return {
      id: [symbol, side, strike ?? '', expiration, index].join(':'),
      symbol,
      side,
      change: get(row, 'Chg%'),
      underlying: toNum(get(row, 'UndPrice')),
      strike,
      premium: toNum(get(row, 'Premium')),
      volume: toNum(get(row, 'Vol.')),
      oi: toNum(get(row, 'OI')),
      expiration,
      dte: toNum(get(row, 'DTE')),
      delta: toNum(get(row, 'Delta')),
      iv: get(row, 'IV'),
      gap: get(row, 'Gap%'),
      apy: get(row, 'APY%'),
      risk: get(row, 'Risk', 'Review').replace(' Risk', ''),
      updated: get(row, 'Updated'),
    }
  })
}

function glossaryTermForColumn(column: SortKey): string | undefined {
  switch (column) {
    case 'premium': return 'premium'
    case 'volume': return 'volume'
    case 'oi': return 'oi'
    case 'delta': return 'delta'
    case 'iv': return 'iv'
    case 'apy': return 'apy'
    default: return undefined
  }
}

function SortableTh({ label, column, sort, onSort, align }: { label: string; column: SortKey; sort: SortState; onSort: (column: SortKey) => void; align?: 'left' }) {
  const active = sort.key === column
  const glossaryTerm = glossaryTermForColumn(column)
  const title = glossaryTerm ? GLOSSARY[glossaryTerm] : undefined
  return (
    <th className={align === 'left' ? 'text-left' : 'text-right'} title={title}>
      <button type="button" onClick={() => onSort(column)} title={title}>
        {glossaryTerm ? <GlossaryTerm term={glossaryTerm} focusable={false}>{label}</GlossaryTerm> : label}
        <i className={`fa-solid ${active && sort.dir === 'asc' ? 'fa-arrow-up-short-wide' : 'fa-arrow-down-wide-short'}`} />
      </button>
    </th>
  )
}

function Liquidity({ value }: { value: number | null }) {
  const level = !value ? 0 : value >= 500 ? 3 : value >= 100 ? 2 : 1
  return (
    <span className={`liquidity liquidity-${level}`} aria-label={`Liquidity level ${level}`}>
      <span /><span /><span />
    </span>
  )
}

function EarningsWeekCard({ data }: { data: EarningsWeekCardData }) {
  const c = data.iv_crush
  const im = data.implied_move
  const rm = data.realized_move
  const p = data.position
  return (
    <section className="earnings-week-card">
      <h3>Earnings is coming · {data.symbol}</h3>
      <div className="kv">
        <span>{new Date(data.next_earnings_at).toLocaleDateString()}</span>
        <span className="value">{data.session === 'BMO' ? 'Before the open' : data.session === 'AMC' ? 'After the close' : data.session}</span>
      </div>

      {c && (
        <div className="section">
          <div className="section-title" title="How much option prices typically drop right after earnings is announced — selling premium can capture this drop.">
            How options usually deflate (last {c.past_quarters_used}Q)
          </div>
          <div className="kv"><span>Pricing before</span><span className="value">{c.pre_avg_pct.toFixed(1)}%</span></div>
          <div className="kv"><span>Pricing after</span><span className="value">{c.post_avg_pct.toFixed(1)}%</span></div>
          <div className="kv"><span>Drop</span><span className="value">{c.compression_pp >= 0 ? '+' : ''}{c.compression_pp.toFixed(1)}pp</span></div>
        </div>
      )}

      {im && (
        <div className="section">
          <div className="section-title" title="The move option traders are pricing in for earnings, based on at-the-money calls + puts.">
            Move the market is expecting
          </div>
          <div className="kv"><span>Expected</span><span className="value">±{im.pct.toFixed(1)}%</span></div>
          <div className="kv"><span>Upside / Downside target</span><span className="value">${im.upper.toFixed(2)} / ${im.lower.toFixed(2)}</span></div>
        </div>
      )}

      {rm && (
        <div className="section">
          <div className="section-title" title="How much the stock actually moved after earnings over the past few quarters.">
            What actually happened (last {rm.past_quarters_used}Q)
          </div>
          <div className="kv"><span>Average move</span><span className="value">±{rm.avg_pct.toFixed(1)}%</span></div>
          <div className="kv"><span>Range</span><span className="value">±{rm.min_pct.toFixed(1)}% — ±{rm.max_pct.toFixed(1)}%</span></div>
          {rm.diff_from_implied_pp !== 0 && (
            <div className="kv"><span>Expected vs actual</span><span className="value">{rm.diff_from_implied_pp >= 0 ? '+' : ''}{rm.diff_from_implied_pp.toFixed(1)}pp</span></div>
          )}
        </div>
      )}

      {p && (
        <div className="section">
          <div className="section-title">Your position here</div>
          {p.shares > 0 && (
            <div className="kv"><span>Shares</span><span className="value">{p.shares} @ ${p.avg_cost.toFixed(2)}</span></div>
          )}
          {p.open_options.map((opt, idx) => (
            <div key={idx} className="kv">
              <span>{opt.side.toUpperCase()} ${opt.strike.toFixed(2)}</span>
              <span className="value">{opt.qty} × {new Date(opt.expiry).toLocaleDateString()}</span>
            </div>
          ))}
          {p.strike_to_implied_upper_diff != null && (
            <div className="kv">
              <span>Your strike vs expected high</span>
              <span className="value">{p.strike_to_implied_upper_diff >= 0 ? '+' : ''}{p.strike_to_implied_upper_diff.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function IVEvaluationCard({ data }: { data: IVEvaluation }) {
  const shapeLabel = (s?: string) => {
    if (s === 'contango') return 'Longer-dated options pricier — calm now, more uncertainty later'
    if (s === 'backwardation') return 'Short-dated options pricier — something big expected soon'
    if (s === 'flat') return 'Roughly even across expirations — no near-term event priced in'
    return ''
  }
  return (
    <section className="iv-eval-card">
      <h3>Option pricing right now</h3>
      <p className="muted" style={{ marginTop: -4, marginBottom: 10 }}>
        How expensive options are today vs. how much the stock has actually moved.
      </p>

      {data.term_structure && data.term_structure.length > 0 && (
        <div>
          <div className="iv-term-row">
            {data.term_structure.map((p) => (
              <div className="iv-term-col" key={p.tenor_days}>
                <div className="tenor">{Math.round(p.tenor_days / 30)}M</div>
                <div className="value">{p.atm_iv_pct.toFixed(1)}%</div>
              </div>
            ))}
          </div>
          {data.term_shape && (
            <div className={`iv-term-shape ${data.term_shape}`} title={shapeLabel(data.term_shape)}>
              {data.term_shape.charAt(0).toUpperCase() + data.term_shape.slice(1)} — {shapeLabel(data.term_shape)}
            </div>
          )}
        </div>
      )}

      {data.iv_vs_rv && data.iv_vs_rv.length > 0 && (
        <table className="iv-rv-table">
          <thead>
            <tr>
              <th align="left" title="How far back we're looking">Last</th>
              <th align="left" title="How much the stock actually moved (realized volatility)">Actual move</th>
              <th align="left" title="How much option traders expect it to move (implied volatility)">Option-priced move</th>
              <th align="right" title="Option-priced minus actual. Positive = options expensive, negative = cheap.">You’re paid</th>
            </tr>
          </thead>
          <tbody>
            {data.iv_vs_rv.map((row) => (
              <tr key={row.window_days}>
                <td>{row.window_days}d</td>
                <td>{row.rv_pct.toFixed(1)}%</td>
                <td>{row.iv_pct.toFixed(1)}%</td>
                <td align="right" className={row.premium_pp > 0 ? 'premium-pos' : 'premium-neg'}>
                  {row.premium_pp >= 0 ? '+' : ''}{row.premium_pp.toFixed(1)}pp
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data.summary && <div className="iv-eval-summary">{data.summary}</div>}
    </section>
  )
}

type EventFilter = 'all' | CalendarEventType

function EventCalendarPanel({ events }: { events: CalendarEvent[] }) {
  const [filter, setFilter] = useState<EventFilter>('all')

  const filtered = useMemo(
    () => filter === 'all' ? events : events.filter((e) => e.type === filter),
    [events, filter]
  )

  const grouped = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {}
    for (const e of filtered) {
      const day = new Date(e.date).toISOString().slice(0, 10)
      ;(map[day] ??= []).push(e)
    }
    return Object.keys(map).sort().map((day) => ({ day, items: map[day] }))
  }, [filtered])

  const filters: { key: EventFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'earnings', label: 'Earnings' },
    { key: 'exdiv', label: 'Ex-Div' },
    { key: 'option_expiry', label: 'Expiries' },
    { key: 'macro', label: 'Macro' },
  ]

  if (events.length === 0) return null

  return (
    <section className="event-calendar-panel">
      <h3>Coming up</h3>
      <div className="event-calendar-filter">
        {filters.map((f) => (
          <button
            key={f.key}
            className={filter === f.key ? 'active' : ''}
            onClick={() => setFilter(f.key)}
            type="button"
          >{f.label}</button>
        ))}
      </div>
      {grouped.map(({ day, items }) => (
        <div key={day} className="event-calendar-day">
          <div className="event-calendar-day-header">
            {new Date(day).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
          {items.map((event, idx) => (
            <div key={`${event.date}-${event.type}-${event.symbol || idx}`} className="event-calendar-row">
              {event.symbol ? <span className="symbol">{event.symbol}</span> : <span className="symbol" aria-hidden="true" />}
              <span>{event.label}</span>
              <EventChip event={event} />
            </div>
          ))}
        </div>
      ))}
    </section>
  )
}

type LoadingStateVariant = 'table' | 'card' | 'list' | 'grid'

function LoadingState({ variant = 'card', rows = 6, label }: { variant?: LoadingStateVariant; rows?: number; label?: string }) {
  return (
    <div className={`loading-state loading-state-${variant}`} role="status" aria-live="polite">
      {label && <div className="loading-state-label">{label}</div>}
      <div className="loading-state-rows">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="loading-state-row" style={{ animationDelay: `${i * 60}ms` }} />
        ))}
      </div>
    </div>
  )
}

function EventChip({ event }: { event: CalendarEvent }) {
  const days = Math.max(0, Math.round((new Date(event.date).getTime() - Date.now()) / (24 * 3600 * 1000)))
  const label = days === 0 ? 'today' : `${days}d`
  const tone =
    event.impact === 'H' ? 'event-chip-h' :
    event.impact === 'M' ? 'event-chip-m' : 'event-chip-l'
  const icon =
    event.type === 'earnings' ? '📊' :
    event.type === 'exdiv' ? '💵' :
    event.type === 'option_expiry' ? '⏱' : '🏛'
  return (
    <span className={`event-chip ${tone}`} title={event.label}>
      {icon} {label}
    </span>
  )
}

function WatchlistFilterBar({ value, onChange, counts }: {
  value: WatchFilter
  onChange: (v: WatchFilter) => void
  counts: Record<WatchFilter, number>
}) {
  const tabs: { key: WatchFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'signals', label: 'Radar signals' },
    { key: 'earnings', label: 'Earnings week' },
    { key: 'high_iv', label: 'Elevated IV' },
    { key: 'has_positions', label: 'With positions' },
  ]
  return (
    <div className="watchlist-filter">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          className={value === t.key ? 'active' : ''}
          onClick={() => onChange(t.key)}
        >
          {t.label} <span className="count">{counts[t.key]}</span>
        </button>
      ))}
    </div>
  )
}

function WatchlistStats({ items }: { items: WatchlistItem[] }) {
  if (items.length === 0) return null
  const ivs = items.map((i) => i.iv_rank).filter((v): v is number => typeof v === 'number')
  const avgIV = ivs.length > 0 ? Math.round(ivs.reduce((a, b) => a + b, 0) / ivs.length) : null
  const atmIvs = items.map((i) => i.atm_iv).filter((v): v is number => typeof v === 'number')
  const avgATMIV = avgIV == null && atmIvs.length > 0 ? Math.round(atmIvs.reduce((a, b) => a + b, 0) / atmIvs.length) : null
  const earningsWeek = items.filter((i) => {
    const e = i.next_event
    if (!e || e.type !== 'earnings') return false
    return (new Date(e.date).getTime() - Date.now()) <= 7 * 24 * 3600 * 1000
  }).length
  const hasPos = items.filter((i) => (i.open_positions_count ?? 0) > 0).length
  const signals = items.filter((i) => (i.signal?.score ?? 0) >= 55).length
  const parts: string[] = [`${items.length} symbols`]
  if (signals > 0) parts.push(`${signals} radar signals`)
  if (avgIV != null) parts.push(`avg IV rank ${avgIV}`)
  if (avgATMIV != null) parts.push(`avg ATM IV ${avgATMIV}%`)
  if (earningsWeek > 0) parts.push(`${earningsWeek} in earnings week`)
  if (hasPos > 0) parts.push(`${hasPos} with open positions`)
  return <div className="watchlist-stats">{parts.join(' · ')}</div>
}

// Helper used by WatchlistPage to compute filter chip counts.
function watchlistFilterCounts(items: WatchlistItem[]): Record<WatchFilter, number> {
  let earnings = 0, highIV = 0, hasPos = 0, signals = 0
  for (const it of items) {
    const e = it.next_event
    if (e && e.type === 'earnings' && (new Date(e.date).getTime() - Date.now()) <= 7 * 24 * 3600 * 1000) earnings++
    if (watchlistIVSignal(it) > 50) highIV++
    if ((it.signal?.score ?? 0) >= 55) signals++
    if ((it.open_positions_count ?? 0) > 0) hasPos++
  }
  return { all: items.length, signals, earnings, high_iv: highIV, has_positions: hasPos }
}

function watchlistIVSignal(it: WatchlistItem): number {
  if (typeof it.iv_rank === 'number') return it.iv_rank
  if (typeof it.atm_iv === 'number') return it.atm_iv
  return -1
}

function loadTradingView() {
  const existing = (window as unknown as { TradingView?: unknown }).TradingView
  if (existing) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    const prior = document.querySelector<HTMLScriptElement>('script[data-tradingview]')
    if (prior) {
      prior.addEventListener('load', () => resolve(), { once: true })
      prior.addEventListener('error', () => reject(new Error('TradingView failed to load')), { once: true })
      return
    }
    const script = document.createElement('script')
    script.src = 'https://s3.tradingview.com/tv.js'
    script.async = true
    script.dataset.tradingview = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('TradingView failed to load'))
    document.head.appendChild(script)
  })
}

function SymbolDrawer({ symbol, onClose, hideStrikes = false }: { symbol: string; onClose: () => void; hideStrikes?: boolean }) {
  const [stock, setStock] = useState<StockSnapshot | null>(null)
  const [contracts, setContracts] = useState<ScanRow[]>([])
  const [loading, setLoading] = useState(true)
  const [chartReady, setChartReady] = useState(false)
  const [chartBlocked, setChartBlocked] = useState(false)
  const chartId = `tv-${symbol.replace(/[^a-zA-Z0-9]/g, '')}`

  // The drawer is web's symbol detail, and it only opens from a Find result.
  useEffect(() => {
    track('symbol_detail_opened', { symbol, entry_point: 'find' })
  }, [symbol])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.allSettled([
      api<StockSnapshot>(`/api/stock/${encodeURIComponent(symbol)}`),
      api<{ headers?: string[]; data?: unknown[][] }>('/filter_options', {
        method: 'POST',
        body: JSON.stringify({
          symbolSearch: symbol,
          minDte: 1,
          maxDte: 90,
          minDelta: -1,
          maxIV: 220,
          include_leverage: 1,
          side: 'all',
          minVolume: 0,
          minOI: 0,
          minAPY: 0,
          sortBy: 'SCORE',
        }),
      }),
    ]).then(([stockResult, optionResult]) => {
      if (cancelled) return
      setStock(stockResult.status === 'fulfilled' ? stockResult.value : null)
      setContracts(optionResult.status === 'fulfilled' ? normalizeScan(optionResult.value) : [])
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [symbol])

  useEffect(() => {
    let cancelled = false
    let observer: MutationObserver | null = null
    let readyTimer: number | null = null
    let ready = false
    setChartReady(false)
    setChartBlocked(false)
    const blockedTimer = window.setTimeout(() => {
      if (!cancelled && !ready) setChartBlocked(true)
    }, 6000)
    loadTradingView().then(() => {
      if (cancelled) return
      const host = document.getElementById(chartId)
      if (!host) return
      host.innerHTML = ''
      const TradingView = (window as unknown as { TradingView?: { widget: new (config: Record<string, unknown>) => unknown } }).TradingView
      if (!TradingView) return
      new TradingView.widget({
        autosize: true,
        symbol,
        interval: 'D',
        timezone: 'Etc/UTC',
        theme: 'dark',
        backgroundColor: '#0b1020',
        toolbar_bg: '#0b1020',
        style: '1',
        locale: 'en',
        hide_top_toolbar: true,
        hide_legend: true,
        allow_symbol_change: false,
        save_image: false,
        studies: [],
        container_id: chartId,
      })

      const markReady = () => {
        if (cancelled) return
        readyTimer = window.setTimeout(() => {
          if (cancelled) return
          ready = true
          setChartReady(true)
        }, 3500)
      }
      const bindFrame = () => {
        const frame = host.querySelector('iframe')
        if (!frame) return false
        frame.addEventListener('load', markReady, { once: true })
        return true
      }
      if (!bindFrame()) {
        observer = new MutationObserver(() => {
          if (bindFrame()) observer?.disconnect()
        })
        observer.observe(host, { childList: true, subtree: true })
      }
    }).catch(() => { if (!cancelled) setChartBlocked(true) })
    return () => {
      cancelled = true
      observer?.disconnect()
      if (readyTimer) window.clearTimeout(readyTimer)
      window.clearTimeout(blockedTimer)
    }
  }, [chartId, symbol])

  const puts = contracts.filter((row) => row.side.toLowerCase().includes('put')).sort((a, b) => pctNum(b.apy) - pctNum(a.apy)).slice(0, 5)
  const calls = contracts.filter((row) => row.side.toLowerCase().includes('call')).sort((a, b) => pctNum(b.apy) - pctNum(a.apy)).slice(0, 5)
  const bestPut = puts[0]
  const bestCall = calls[0]
  const avgIv = contracts.length ? contracts.reduce((sum, row) => sum + pctNum(row.iv), 0) / contracts.length : 0
  const totalOi = contracts.reduce((sum, row) => sum + (row.oi || 0), 0)
  const live = useLiveQuote(symbol)
  const price = live?.price ?? stock?.lastPrice ?? stock?.underlying_price ?? bestPut?.underlying ?? bestCall?.underlying ?? null
  const priceFlash = useFlash(live?.price)
  const updated = stock?.last_collected || bestPut?.updated || bestCall?.updated || ''

  return (
    <div className="drawer-backdrop" role="dialog" aria-modal="true" aria-label={`${symbol} snapshot`} onClick={onClose}>
      <aside className="symbol-drawer" onClick={(event) => event.stopPropagation()}>
        <header className="drawer-header">
          <div>
            <h2><span>{symbol}</span> <span className={priceFlash}>{money(price)}</span> <em>Snapshot</em></h2>
            <p>{stock?.sector || 'Options snapshot'} · Updated {dateTimeLabel(updated)}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
        </header>
        <div className="drawer-chart">
          <div id={chartId} className={`tradingview-host ${chartReady ? 'is-ready' : ''}`} />
          {chartBlocked && !chartReady ? (
            <a className="drawer-chart__fallback" href={`https://www.tradingview.com/chart/?symbol=${encodeURIComponent(symbol)}`} target="_blank" rel="noopener noreferrer">
              Chart blocked — open in TradingView
            </a>
          ) : (!chartReady || loading) && <span>{loading ? 'Loading snapshot...' : 'Loading chart...'}</span>}
        </div>
        <div className="yield-cards">
          <article>
            <span>Best Cash-Secured Put</span>
            <strong>{bestPut?.apy || '-'}</strong>
            <p>Strike {strikeText(bestPut?.strike ?? null, hideStrikes)} · IV {bestPut?.iv || '-'}</p>
          </article>
          <article>
            <span>Best Covered Call</span>
            <strong>{bestCall?.apy || '-'}</strong>
            <p>Strike {strikeText(bestCall?.strike ?? null, hideStrikes)} · IV {bestCall?.iv || '-'}</p>
          </article>
        </div>
        <DrawerSection title="Market Snapshot">
          <DrawerTile label="Price" value={money(price)} />
          <DrawerTile label="Change" value={bestPut?.change && bestPut.change !== '-' ? bestPut.change : '-'} />
          <DrawerTile label="Sector" value={stock?.sector || '-'} />
          <DrawerTile label="Near DTE" value={contracts.length ? `${Math.min(...contracts.map((row) => row.dte || 999))}d` : '-'} />
          <DrawerTile label="Contracts" value={compact(contracts.length)} />
          <DrawerTile label="Updated" value={dateTimeLabel(updated)} />
        </DrawerSection>
        <DrawerSection title="Options Depth">
          <DrawerTile label="CSP APY" value={bestPut?.apy || '-'} />
          <DrawerTile label="CSP Strike" value={strikeText(bestPut?.strike ?? null, hideStrikes)} />
          <DrawerTile label="CC APY" value={bestCall?.apy || '-'} />
          <DrawerTile label="CC Strike" value={strikeText(bestCall?.strike ?? null, hideStrikes)} />
          <DrawerTile label="Avg IV" value={`${avgIv.toFixed(2)}%`} />
          <DrawerTile label="Open Interest" value={compact(totalOi)} />
        </DrawerSection>
        <section className="drawer-contracts">
          <h3>Recommended Contracts</h3>
          <div className="drawer-contract-grid">
            <MiniContracts title="Puts" rows={puts} hideStrikes={hideStrikes} />
            <MiniContracts title="Calls" rows={calls} hideStrikes={hideStrikes} />
          </div>
        </section>
      </aside>
    </div>
  )
}

function DrawerSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="drawer-section"><h3>{title}</h3><div className="drawer-tiles">{children}</div></section>
}

function DrawerTile({ label, value }: { label: string; value: string }) {
  return <article><span>{label}</span><strong>{value}</strong></article>
}

function MiniContracts({ title, rows, hideStrikes = false }: { title: string; rows: ScanRow[]; hideStrikes?: boolean }) {
  return (
    <div className="mini-contracts">
      <h4>{title}</h4>
      <table>
        <thead><tr><th>Strike</th><th><GlossaryTerm term="premium">Premium</GlossaryTerm></th><th><GlossaryTerm term="dte">DTE</GlossaryTerm></th><th><GlossaryTerm term="delta">Delta</GlossaryTerm></th><th><GlossaryTerm term="iv">IV</GlossaryTerm></th><th><GlossaryTerm term="apy">APY</GlossaryTerm></th><th>Risk</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{strikeText(row.strike, hideStrikes)}</td>
              <td className="up">{money(row.premium)}</td>
              <td>{row.dte}d</td>
              <td>{number(row.delta, 2)}</td>
              <td>{row.iv}</td>
              <td className="up">{row.apy}</td>
              <td><span className={riskClass(row.risk)}>{riskLabel(row.risk)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function compareRows(a: ScanRow, b: ScanRow, sort: SortState) {
  const av = comparableValue(a, sort.key)
  const bv = comparableValue(b, sort.key)
  const result = typeof av === 'number' && typeof bv === 'number'
    ? av - bv
    : String(av).localeCompare(String(bv))
  return sort.dir === 'asc' ? result : -result
}

function comparableValue(row: ScanRow, key: SortKey): string | number {
  if (key === 'iv' || key === 'apy') return pctNum(row[key])
  if (key === 'risk') return riskRank(row.risk)
  if (key === 'expiration' || key === 'updated') return new Date(row[key]).getTime() || 0
  const value = row[key]
  return typeof value === 'number' && isFinite(value) ? value : String(value || '')
}

function toNum(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = Number(String(value).replace(/[%,$,]/g, ''))
  return Number.isFinite(n) ? n : null
}

function pctNum(value: string) {
  return toNum(value) || 0
}

function money(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-'
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: value >= 1000 ? 0 : 2, maximumFractionDigits: 2 })}`
}

function number(value: number | null | undefined, digits = 2) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-'
  return value.toFixed(digits)
}

function compact(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-'
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return Math.round(value).toLocaleString()
}

function shortExpiration(value: string) {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value || '-'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function timeLabel(value: string) {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
}

function dateTimeLabel(value: string) {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function riskRank(risk: string) {
  const lower = risk.toLowerCase()
  if (lower.includes('low')) return 1
  if (lower.includes('med')) return 2
  return 3
}

function riskLabel(risk: string) {
  const lower = risk.toLowerCase()
  if (lower.includes('low')) return 'Low'
  if (lower.includes('med')) return 'Med'
  if (lower.includes('high')) return 'High'
  return risk || 'Review'
}

function riskClass(risk: string) {
  const lower = risk.toLowerCase()
  if (lower.includes('low')) return 'pill pill--green'
  if (lower.includes('medium') || lower.includes('med')) return 'pill pill--amber'
  return 'pill pill--rose'
}

type WatchlistNextEvent = {
  date: string
  type: 'earnings' | 'exdiv' | 'option_expiry' | 'macro'
  impact: 'H' | 'M' | 'L'
  label: string
}

type WatchlistItem = {
  symbol: string
  position: number
  iv_rank?: number | null
  atm_iv?: number | null
  csp_apy_30d?: number | null
  open_positions_count?: number
  next_event?: WatchlistNextEvent | null
  signal?: WatchlistSignal | null
}

type WatchlistSignal = {
  score: number
  level: 'hot' | 'watch' | 'steady' | string
  label: string
  reason: string
}

type WatchFilter = 'all' | 'signals' | 'earnings' | 'high_iv' | 'has_positions'

type ScoreExplanationDriver = {
  kind: string
  factor: string
  text: string
}

type ChainScore = {
  score: number
  recommendation_level?: string
  explanation?: { summary?: string; drivers?: ScoreExplanationDriver[] }
}

type ChainRow = {
  option_symbol: string
  underlying: string
  side: string
  strike: number
  underlying_price: number
  underlying_change_pct?: number
  bid?: number
  ask?: number
  premium?: number
  volume?: number
  open_interest?: number
  expiration: string
  dte: number
  delta?: number
  iv?: number
  gap_pct?: number
  apy?: number
  collected_at?: string
  score?: ChainScore
}

type VolatilitySetup = {
  score: number
  level: string
  reason?: string
  average_iv?: number
  near_iv?: number
  back_iv?: number
  term_slope?: number
  liquid_percent?: number
}

// C13: /v1/symbols/{sym}/range. Web reads only the dividend yield from it —
// the rest of that payload is already on the page from /api/stock. The server
// sends null unless dividend_calendar.yield_pct is above zero.
type SymbolRangeResponse = { dividend_yield?: number | null }

type SymbolChainResponse = {
  symbol: string
  rows: ChainRow[]
  truncated?: boolean
  total_matched?: number
  volatility_setup?: VolatilitySetup
}

function SymbolLink({ symbol, children, className }: { symbol: string; children?: React.ReactNode; className?: string }) {
  return (
    <Link to={`/s/${encodeURIComponent(symbol.toUpperCase())}`} className={className}>
      {children ?? <strong>{symbol.toUpperCase()}</strong>}
    </Link>
  )
}

// Inline secondary link from a Brief row straight into fast stock analysis.
// autorun=1 makes AnalyzePage kick off the fast run on arrival (deep never auto-runs).
function CheckupLink({ symbol }: { symbol: string }) {
  const { t } = useI18n()
  return (
    <Link
      to={`/analyze?symbol=${encodeURIComponent(symbol.toUpperCase())}&autorun=1`}
      className="brief-checkup-link"
    >
      {t('brief.checkup')}
    </Link>
  )
}

function symbolFromRoute(route: string): string | null {
  if (!route.startsWith('/s/')) return null
  const raw = route.slice(3).split('/')[0].split('?')[0]
  if (!raw) return null
  return decodeURIComponent(raw).toUpperCase()
}

type InsiderTxn = {
  filing_date: string
  transaction_date: string
  insider_name: string
  insider_role?: string
  transaction_code: string
  shares: number
  price_per_share?: number
  total_value?: number
}

type InsiderSignalSummary = {
  window_days: number
  buy_count: number
  sell_count: number
  net_value_usd: number
  exec_involved: boolean
  signal: 'net_buying' | 'net_selling' | 'mixed' | 'quiet'
}

type InsiderSummary = {
  window_days: number
  buy_shares: number
  sell_shares: number
  buy_value: number
  sell_value: number
  net_shares: number
  net_value: number
  buyer_count: number
}

type SmartMoneyHolding = {
  filer_name: string
  value_usd: number
  shares: number
  shares_change: number
  percent_change?: number
}

type SmartMoneySummary = {
  quarter: string
  new_count: number
  added_count: number
  trimmed_count: number
  exited_count: number
  held_count: number
  total_value_usd: number
}

type ChainStrike = { put?: ChainRow; call?: ChainRow }
type ChainGroup = {
  expiration: string
  dte: number
  byStrike: Map<number, ChainStrike>
  strikes: number[]
}

function groupChainByExpiration(rows: ChainRow[], preserveRedacted = false): ChainGroup[] {
  const map = new Map<string, ChainGroup>()
  rows.forEach((row, index) => {
    let g = map.get(row.expiration)
    if (!g) {
      g = { expiration: row.expiration, dte: row.dte, byStrike: new Map(), strikes: [] }
      map.set(row.expiration, g)
    }
    const strikeKey = preserveRedacted && row.strike === 0 ? -(index + 1) : row.strike
    let pair = g.byStrike.get(strikeKey)
    if (!pair) {
      pair = {}
      g.byStrike.set(strikeKey, pair)
    }
    if (row.side.toLowerCase() === 'call') pair.call = row
    else pair.put = row
  })
  for (const g of map.values()) {
    g.strikes = Array.from(g.byStrike.keys()).sort((a, b) => a - b)
  }
  return Array.from(map.values()).sort((a, b) => a.dte - b.dte)
}

function ChainBoard({ rows, underlying, loading, hideStrikes = false }: { rows: ChainRow[]; underlying: number | null; loading: boolean; hideStrikes?: boolean }) {
  const groups = useMemo(() => groupChainByExpiration(rows, hideStrikes), [rows, hideStrikes])
  const [activeExp, setActiveExp] = useState<string | null>(null)
  const stickyheadRef = useRef<HTMLDivElement>(null)
  const [stickyheadH, setStickyheadH] = useState(80)
  useEffect(() => {
    if (!stickyheadRef.current) return
    const el = stickyheadRef.current
    const ro = new ResizeObserver(() => setStickyheadH(el.offsetHeight))
    ro.observe(el)
    setStickyheadH(el.offsetHeight)
    return () => ro.disconnect()
  }, [])
  // Top offset for sticky thead rows. Page nav is sticky at top:0 with
  // ~56px height; stickyhead pins right below; thead rows pin below
  // stickyhead. Heights are measured dynamically so adding/removing
  // volbar or wrapping tab rows doesn't break the offsets.
  const NAV_HEIGHT = 56
  const bandTop = NAV_HEIGHT + stickyheadH
  // Default the active expiration to the first group whenever rows change.
  useEffect(() => {
    if (groups.length > 0 && (!activeExp || !groups.some((g) => g.expiration === activeExp))) {
      setActiveExp(groups[0].expiration)
    }
  }, [groups, activeExp])

  const activeGroup = useMemo(
    () => groups.find((g) => g.expiration === activeExp) || groups[0] || null,
    [groups, activeExp],
  )
  // ATM = strike closest to spot in the active expiration.
  const atmStrike = useMemo(() => {
    if (hideStrikes || !activeGroup || underlying == null) return null
    let best: number | null = null
    let bestGap = Infinity
    for (const s of activeGroup.strikes) {
      const gap = Math.abs(s - underlying)
      if (gap < bestGap) { bestGap = gap; best = s }
    }
    return best
  }, [activeGroup, underlying, hideStrikes])

  if (groups.length === 0) {
    return <div className="empty-row">{loading ? 'Loading chain...' : 'No chain data yet.'}</div>
  }

  const totalCallVol = activeGroup
    ? activeGroup.strikes.reduce((acc, s) => acc + (activeGroup.byStrike.get(s)?.call?.volume ?? 0), 0)
    : 0
  const totalPutVol = activeGroup
    ? activeGroup.strikes.reduce((acc, s) => acc + (activeGroup.byStrike.get(s)?.put?.volume ?? 0), 0)
    : 0
  const totalVol = totalCallVol + totalPutVol
  const callPct = totalVol > 0 ? (totalCallVol / totalVol) * 100 : 50

  // Strikes are ascending. Find the insertion index of the spot price so we
  // can render a single "spot" divider row at the correct position.
  let spotIndex = -1
  if (activeGroup && underlying != null) {
    for (let i = 0; i < activeGroup.strikes.length; i++) {
      if (activeGroup.strikes[i] >= underlying) { spotIndex = i; break }
    }
  }

  return (
    <div className="chain-board">
      <div className="chain-stickyhead" ref={stickyheadRef}>
        <nav className="chain-tabs" aria-label="Expiration dates">
          {groups.map((g) => (
            <button
              key={g.expiration}
              type="button"
              className={g.expiration === activeGroup?.expiration ? 'chain-tab is-active' : 'chain-tab'}
              onClick={() => setActiveExp(g.expiration)}
            >
              <span>{formatExpiration(g.expiration)}</span>
              <small>{g.dte}d</small>
            </button>
          ))}
        </nav>

        {totalVol > 0 && (
          <div className="chain-volbar" aria-label="Call vs put volume ratio">
            <span className="chain-volbar__label call">Call {formatVolume(totalCallVol)}</span>
            <div className="chain-volbar__track">
              <div className="chain-volbar__fill call" style={{ width: `${callPct}%` }} />
              <div className="chain-volbar__fill put" style={{ width: `${100 - callPct}%` }} />
            </div>
            <span className="chain-volbar__label put">{formatVolume(totalPutVol)} Put</span>
          </div>
        )}
      </div>

      {activeGroup && (
        <div className="scanner-table-wrap chain-table-wrap">
          <table className="chain-table">
            <thead>
              <tr className="chain-table__band">
                <th colSpan={5} className="chain-side chain-side--call" style={{ top: `${bandTop}px` }}>Calls</th>
                <th className="chain-strike-head" style={{ top: `${bandTop}px` }}>Strike</th>
                <th colSpan={5} className="chain-side chain-side--put" style={{ top: `${bandTop}px` }}>Puts</th>
              </tr>
              <tr>
                <th style={{ top: `${bandTop + 22}px` }}>Vol</th>
                <th style={{ top: `${bandTop + 22}px` }}>OI</th>
                <th style={{ top: `${bandTop + 22}px` }}>IV</th>
                <th style={{ top: `${bandTop + 22}px` }}>Δ</th>
                <th style={{ top: `${bandTop + 22}px` }}>Premium</th>
                <th style={{ top: `${bandTop + 22}px` }}></th>
                <th style={{ top: `${bandTop + 22}px` }}>Premium</th>
                <th style={{ top: `${bandTop + 22}px` }}>Δ</th>
                <th style={{ top: `${bandTop + 22}px` }}>IV</th>
                <th style={{ top: `${bandTop + 22}px` }}>OI</th>
                <th style={{ top: `${bandTop + 22}px` }}>Vol</th>
              </tr>
            </thead>
            <tbody>
              {activeGroup.strikes.map((strike, idx) => {
                const pair = activeGroup.byStrike.get(strike)!
                const isAtm = atmStrike === strike
                const callItm = !hideStrikes && underlying != null && underlying > strike
                const putItm = !hideStrikes && underlying != null && underlying < strike
                const callCellClass = callItm ? 'chain-cell--itm' : ''
                const putCellClass = putItm ? 'chain-cell--itm' : ''
                const showSpotRow = !hideStrikes && underlying != null && spotIndex === idx
                return (
                  <Fragment key={strike}>
                    {showSpotRow && (
                      <tr className="chain-spot-row" aria-label="Underlying price">
                        <td colSpan={11}>
                          <span className="chain-spot-line" />
                          <span className="chain-spot-price">${underlying!.toFixed(2)}</span>
                          <span className="chain-spot-line" />
                        </td>
                      </tr>
                    )}
                    <tr className={isAtm ? 'is-atm' : ''}>
                      <td className={callCellClass}>{pair.call ? formatVolume(pair.call.volume ?? 0) : '—'}</td>
                      <td className={callCellClass}>{pair.call ? formatVolume(pair.call.open_interest ?? 0) : '—'}</td>
                      <td className={callCellClass}>{pair.call?.iv != null ? `${pair.call.iv.toFixed(1)}%` : '—'}</td>
                      <td className={callCellClass}>{pair.call?.delta != null ? pair.call.delta.toFixed(2) : '—'}</td>
                      <td className={`chain-premium ${callCellClass}`}>
                        {pair.call ? (
                          <>
                            <strong>${(pair.call.premium ?? 0).toFixed(2)}</strong>
                            <small>
                              {pair.call.apy != null ? `${pair.call.apy.toFixed(1)}% APY` : ''}
                              {pair.call.score?.score != null ? ` · ${pair.call.score.score.toFixed(0)}` : ''}
                            </small>
                          </>
                        ) : '—'}
                      </td>
                      <td className="chain-strike"><strong>{strikeText(strike, hideStrikes)}</strong></td>
                      <td className={`chain-premium ${putCellClass}`}>
                        {pair.put ? (
                          <>
                            <strong>${(pair.put.premium ?? 0).toFixed(2)}</strong>
                            <small>
                              {pair.put.apy != null ? `${pair.put.apy.toFixed(1)}% APY` : ''}
                              {pair.put.score?.score != null ? ` · ${pair.put.score.score.toFixed(0)}` : ''}
                            </small>
                          </>
                        ) : '—'}
                      </td>
                      <td className={putCellClass}>{pair.put?.delta != null ? pair.put.delta.toFixed(2) : '—'}</td>
                      <td className={putCellClass}>{pair.put?.iv != null ? `${pair.put.iv.toFixed(1)}%` : '—'}</td>
                      <td className={putCellClass}>{pair.put ? formatVolume(pair.put.open_interest ?? 0) : '—'}</td>
                      <td className={putCellClass}>{pair.put ? formatVolume(pair.put.volume ?? 0) : '—'}</td>
                    </tr>
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function SymbolStats({ chain }: { chain: SymbolChainResponse | null }) {
  if (!chain || !chain.rows || chain.rows.length === 0) return null
  const setup = chain.volatility_setup
  const first = chain.rows[0]
  const price = first?.underlying_price ?? null
  const changePct = first?.underlying_change_pct ?? null
  const putCount = chain.rows.filter((r) => r.side.toLowerCase() === 'put').length
  const callCount = chain.rows.filter((r) => r.side.toLowerCase() === 'call').length
  return (
    <article className="symbol-stats">
      <header><h3>Stats</h3></header>
      <div className="symbol-stats__grid">
        {price != null && (
          <div className="symbol-stats__cell">
            <span>Last price</span>
            <strong>${price.toFixed(2)}</strong>
            {changePct != null && (
              <small className={changePct >= 0 ? 'up' : 'down'}>
                {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%
              </small>
            )}
          </div>
        )}
        {setup && (
          <>
            <div className="symbol-stats__cell">
              <span>Volatility setup</span>
              <strong>{setup.score}</strong>
              <small className="muted">{setup.level}</small>
            </div>
            {setup.average_iv != null && (
              <div className="symbol-stats__cell">
                <span>Avg IV</span>
                <strong>{setup.average_iv.toFixed(1)}%</strong>
                {setup.near_iv != null && setup.back_iv != null && (
                  <small className="muted">near {setup.near_iv.toFixed(1)}% · back {setup.back_iv.toFixed(1)}%</small>
                )}
              </div>
            )}
            {setup.term_slope != null && (
              <div className="symbol-stats__cell">
                <span>Term slope</span>
                <strong>{setup.term_slope >= 0 ? '+' : ''}{setup.term_slope.toFixed(1)} pts</strong>
                <small className="muted">near vs back</small>
              </div>
            )}
            {setup.liquid_percent != null && (
              <div className="symbol-stats__cell">
                <span>Liquid contracts</span>
                <strong>{setup.liquid_percent}%</strong>
                <small className="muted">of chain</small>
              </div>
            )}
          </>
        )}
        <div className="symbol-stats__cell">
          <span>Chain depth</span>
          <strong>{chain.rows.length}</strong>
          <small className="muted">{putCount} puts · {callCount} calls</small>
        </div>
      </div>
      {setup?.reason && <p className="symbol-stats__reason">{setup.reason}</p>}
    </article>
  )
}

// WheelPanel surfaces the highest-scoring cash-secured put and covered call
// for a single symbol side-by-side. Mirrors the wheelstrategyoptions.com
// "browse by symbol" idea — beginners immediately see both legs of the wheel
// without having to navigate the full scanner.
type WheelLeg = {
  strike: number
  expiration: string
  dte: number
  delta: number
  bid: number
  ask: number
  APY: number
  IV: number
  option_symbol: string
  underlying_price?: number
  tv_recommend_all?: number
  score?: WheelScore | null
}
function WheelPanel({ symbol, underlying, hideStrikes = false }: { symbol: string; underlying: number | null; hideStrikes?: boolean }) {
  const [put, setPut] = useState<WheelLeg | null>(null)
  const [call, setCall] = useState<WheelLeg | null>(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let alive = true
    setLoading(true)
    const body = (side: string) => JSON.stringify({ side, symbols: [symbol] })
    Promise.all([
      apiCached<WheelLeg[]>('/api/wheel/recommendations', { method: 'POST', body: body('put') }).catch(() => []),
      apiCached<WheelLeg[]>('/api/wheel/recommendations', { method: 'POST', body: body('call') }).catch(() => []),
    ]).then(([puts, calls]) => {
      if (!alive) return
      setPut((puts || []).find((p) => p) ?? null)
      setCall((calls || []).find((c) => c) ?? null)
      setLoading(false)
    })
    return () => { alive = false }
  }, [symbol])

  if (loading) return null
  if (!put && !call) return null

  const renderLeg = (kind: 'CSP' | 'CC', leg: WheelLeg | null) => {
    if (!leg) {
      return (
        <div className="wheel-leg wheel-leg--empty">
          <h4>{kind === 'CSP' ? 'Sell a put' : 'Sell a covered call'}</h4>
          <p className="muted">No qualifying contract right now.</p>
        </div>
      )
    }
    const tv = tvVerdict(leg.tv_recommend_all)
    const u = underlying ?? leg.underlying_price ?? 0
    const distancePct = !hideStrikes && u > 0 && leg.strike > 0 ? ((leg.strike - u) / u) * 100 : null
    return (
      <div className="wheel-leg">
        <div className="wheel-leg__head">
          <h4>{kind === 'CSP' ? 'Sell a put' : 'Sell a covered call'}</h4>
          {tv && <span className={tv.cls} title={tv.title}>Tech read: {tv.label}</span>}
        </div>
        <div className="wheel-leg__strike">
          {strikeText(leg.strike, hideStrikes)} {kind === 'CSP' ? 'put' : 'call'}
          {distancePct != null && (
            <small className="muted">
              {' · '}{distancePct >= 0 ? '+' : ''}{distancePct.toFixed(1)}% vs price
            </small>
          )}
        </div>
        <div className="wheel-leg__kv">
          <div><span className="dim"><GlossaryTerm term="apy">APY</GlossaryTerm></span><strong className="apy">{fmtPct(leg.APY ?? 0)}</strong></div>
          <div><span className="dim">Expires</span><strong>{fmtExpShort(leg.expiration)} <em className="dim">{leg.dte}d</em></strong></div>
          <div><span className="dim"><GlossaryTerm term="delta">Δ</GlossaryTerm></span><strong>{leg.delta.toFixed(2)}</strong></div>
          <div><span className="dim"><GlossaryTerm term="iv">IV</GlossaryTerm></span><strong>{fmtPct(leg.IV ?? 0)}</strong></div>
        </div>
        {leg.score && (
          <div className={wheelScoreClass(leg.score)} title={wheelScoreTooltip(leg.score)}>
            Quality Score {wheelScoreGrade(leg.score)} {Math.round(leg.score.score ?? 0)}
          </div>
        )}
      </div>
    )
  }

  return (
    <section className="wheel-panel analyze-card">
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
        <h3 style={{ margin: 0 }}>Run the wheel on {symbol}</h3>
        <small className="muted">Top picks — sell the put, get assigned, then sell the call.</small>
      </header>
      <div className="wheel-panel__grid">
        {renderLeg('CSP', put)}
        {renderLeg('CC', call)}
      </div>
    </section>
  )
}

function SymbolDetailPage({ symbol, signedIn }: { symbol: string; signedIn: boolean }) {
  const { t } = useI18n()
  const hideStrikes = !signedIn
  const [chain, setChain] = useState<SymbolChainResponse | null>(null)
  const [stock, setStock] = useState<StockSnapshot | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [watchlist, setWatchlist] = useState<string[] | null>(null)
  const [watchPending, setWatchPending] = useState(false)
  const [watchMessage, setWatchMessage] = useState('')
  const [news, setNews] = useState<NewsItem[]>([])
  const [insider, setInsider] = useState<{ window: InsiderSummary; transactions: InsiderTxn[]; summary?: InsiderSignalSummary } | null>(null)
  const [smartMoney, setSmartMoney] = useState<{ summary: SmartMoneySummary; holdings: SmartMoneyHolding[] } | null>(null)
  const [fundamentals, setFundamentals] = useState<FundamentalsSnapshot | null>(null)
  const [dividendYield, setDividendYield] = useState<number | null>(null)
  // /v1/iv/evaluation is a public endpoint — let anon visitors see it too.
  const ivEval = useIVEvaluation(symbol, true)
  const earningsCard = useEarningsWeekCard(symbol, signedIn)

  useEffect(() => {
    track('symbol_detail_opened', { symbol, entry_point: symbolEntryPoint() })
  }, [symbol])

  useEffect(() => {
    let alive = true
    setDividendYield(null)
    api<FundamentalsSnapshot>(`/v1/fundamentals/${encodeURIComponent(symbol)}`)
      .then((s) => { if (alive) setFundamentals(s) })
      .catch(() => { /* empty until collector populates */ })
    api<SymbolRangeResponse>(`/v1/symbols/${encodeURIComponent(symbol)}/range`)
      .then((r) => { if (alive) setDividendYield(r.dividend_yield ?? null) })
      .catch(() => { /* no yield to show */ })
    return () => { alive = false }
  }, [symbol])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    setChain(null)
    setStock(null)
    const path = signedIn
      ? `/v1/symbols/authed/${encodeURIComponent(symbol)}`
      : `/v1/symbols/${encodeURIComponent(symbol)}`
    Promise.allSettled([
      api<SymbolChainResponse>(path),
      api<StockSnapshot>(`/api/stock/${encodeURIComponent(symbol)}`),
    ]).then(([chainRes, stockRes]) => {
      if (cancelled) return
      if (chainRes.status === 'fulfilled') setChain(chainRes.value)
      else setError((chainRes.reason as ApiError)?.message || 'Symbol chain unavailable')
      if (stockRes.status === 'fulfilled') setStock(stockRes.value)
    }).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [symbol, signedIn])

  useEffect(() => {
    if (!signedIn) { setWatchlist(null); return }
    let cancelled = false
    api<{ items: WatchlistItem[] }>('/v1/watchlist')
      .then((res) => { if (!cancelled) setWatchlist((res.items || []).map((i) => i.symbol.toUpperCase())) })
      .catch(() => { if (!cancelled) setWatchlist([]) })
    return () => { cancelled = true }
  }, [signedIn, symbol])

  useEffect(() => {
    if (!signedIn) { setNews([]); setInsider(null); setSmartMoney(null); return }
    let cancelled = false
    api<NewsResponse>(`/v1/news/today?symbols=${encodeURIComponent(symbol)}&limit=5`)
      .then((res) => { if (!cancelled) setNews(res.items || []) })
      .catch(() => { if (!cancelled) setNews([]) })
    api<{ window: InsiderSummary; transactions: InsiderTxn[]; summary?: InsiderSignalSummary }>(`/v1/symbols/${encodeURIComponent(symbol)}/insider`)
      .then((res) => { if (!cancelled) setInsider(res) })
      .catch(() => { if (!cancelled) setInsider(null) })
    api<{ summary: SmartMoneySummary; holdings: SmartMoneyHolding[] }>(`/v1/symbols/${encodeURIComponent(symbol)}/smart-money`)
      .then((res) => { if (!cancelled) setSmartMoney(res) })
      .catch(() => { if (!cancelled) setSmartMoney(null) })
    return () => { cancelled = true }
  }, [signedIn, symbol])

  const isWatched = (watchlist || []).includes(symbol)

  const toggleWatch = async () => {
    if (!signedIn) { navigate('/login?next=watchlist'); return }
    setWatchPending(true)
    setWatchMessage('')
    try {
      if (isWatched) {
        await api(`/v1/watchlist/${encodeURIComponent(symbol)}`, { method: 'DELETE' })
        setWatchlist((prev) => (prev || []).filter((s) => s !== symbol))
        setWatchMessage('Removed from watchlist')
      } else {
        await api('/v1/watchlist', { method: 'POST', body: JSON.stringify({ symbol }) })
        setWatchlist((prev) => [...(prev || []), symbol])
        setWatchMessage('Added to watchlist')
      }
    } catch (err) {
      setWatchMessage((err as Error).message)
    } finally {
      setWatchPending(false)
    }
  }

  const underlying = chain?.rows[0]?.underlying_price ?? null
  const displayPrice = stock?.lastPrice ?? stock?.underlying_price ?? underlying
  const displayChange = stock?.netPercentChange ?? chain?.rows[0]?.underlying_change_pct
  const ivs = (chain?.rows || []).map((r) => r.iv).filter((v): v is number => v != null && v > 0)
  const avgIv = ivs.length ? Math.round(ivs.reduce((a, b) => a + b, 0) / ivs.length) : null
  const bestApy = chain?.rows.reduce<ChainRow | null>((best, r) => {
    if (r.apy == null) return best
    if (!best || (best.apy ?? 0) < r.apy) return r
    return best
  }, null)

  return (
    <section className="scanner symbol-workbench">
      <header className="symbol-hero">
        <div className="symbol-hero__main">
          <div>
            <p className="kicker">SYMBOL</p>
            <h1>
              {symbol}
              {stock?.sector && <span className="muted symbol-price"> · {stock.sector}</span>}
            </h1>
          </div>
          <div className="symbol-hero__price">
            {displayPrice != null && <strong>${displayPrice.toFixed(2)}</strong>}
            {displayChange != null && (
              <span className={displayChange >= 0 ? 'up' : 'down'}>
                {displayChange >= 0 ? '+' : ''}{displayChange.toFixed(2)}%
              </span>
            )}
          </div>
        </div>
        <div className="symbol-hero__facts">
          <SymbolFact label={t('symbol.previousClose')} value={stock?.closePrice != null ? `$${stock.closePrice.toFixed(2)}` : '—'} />
          <SymbolFact label={t('symbol.fiveDayMove')} value={stock?.five_day_change_pct != null ? `${stock.five_day_change_pct >= 0 ? '+' : ''}${stock.five_day_change_pct.toFixed(2)}%` : '—'} tone={stock?.five_day_change_pct} />
          <SymbolFact label={t('symbol.volume')} value={stock?.totalVolume != null ? formatVolume(Math.round(stock.totalVolume)) : '—'} />
          <SymbolFact label={t('symbol.range52w')} value={stock?.fifty_two_week_low != null && stock.fifty_two_week_high != null ? `$${stock.fifty_two_week_low.toFixed(2)} - $${stock.fifty_two_week_high.toFixed(2)}` : '—'} />
          <SymbolFact label={t('symbol.avgChainIv')} value={avgIv != null ? `${avgIv}%` : '—'} />
          <SymbolFact label={t('symbol.bestApy')} value={bestApy?.apy != null ? `${bestApy.apy.toFixed(1)}% ${bestApy.side.toUpperCase()}` : '—'} />
          {dividendYield != null && <SymbolFact label={t('symbol.dividendYield')} value={`${dividendYield.toFixed(1)}%`} />}
        </div>
        <div className="symbol-hero__footer">
          <nav className="symbol-tabs" aria-label="Symbol detail sections">
            <a href="#chain">Chain</a>
            <a href="#news">News</a>
            <a href="#earnings">Earnings</a>
            <a href="#insider">Insider</a>
            <a href="#institutional">Institutional</a>
          </nav>
          <div className="scanner__head-actions">
          <Link to={`/analyze?symbol=${encodeURIComponent(symbol)}&autorun=1`} className="button button--primary">Run analysis</Link>
          <button
            type="button"
            className={`button ${isWatched ? 'button--ghost' : 'button--ghost'}`}
            onClick={toggleWatch}
            disabled={watchPending}
          >
            {watchPending ? '...' : isWatched ? '✓ In Watchlist' : '+ Watchlist'}
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              const printWin = window.open('', '_blank')
              if (!printWin) return
              const dateStr = new Date().toLocaleDateString()
              const nextEarnings = earningsCard?.next_earnings_at ? new Date(earningsCard.next_earnings_at).toLocaleDateString() : null
              const atmIv = earningsCard?.implied_move ? `±${earningsCard.implied_move.pct.toFixed(1)}%` : null
              const insiderSignalLine = insider?.summary && insider.summary.signal !== 'quiet'
                ? (insider.summary.signal === 'net_buying' ? 'Insiders net buying' : insider.summary.signal === 'net_selling' ? 'Insiders net selling' : 'Mixed insider activity')
                  + (insider.summary.exec_involved ? ' · incl. CEO/CFO' : '')
                : null
              const fundamentalRows = (fundamentals?.concepts || []).slice(0, 8)
              printWin.document.write(`<!DOCTYPE html><html><head><title>${symbol} tear sheet</title><style>
                body{font-family:sans-serif;font-size:13px;margin:32px;color:#111}
                h1{font-size:20px;margin:0 0 4px}
                .header{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px}
                .muted{color:#666;font-size:11px}
                table{border-collapse:collapse;width:100%;margin-bottom:16px}
                td,th{border:1px solid #ddd;padding:6px 10px;text-align:left;font-size:12px}
                th{background:#f5f5f5}
                .footer{margin-top:24px;font-size:10px;color:#999;border-top:1px solid #eee;padding-top:8px}
                @media print{button{display:none}}
              </style></head><body>
              <div class="header">
                <div>
                  <h1>${symbol}</h1>
                  ${stock?.sector ? `<div class="muted">${stock.sector}</div>` : ''}
                  ${nextEarnings ? `<div class="muted">Next earnings: ${nextEarnings}</div>` : ''}
                </div>
                <div>
                  ${displayPrice != null ? `<div><strong>$${displayPrice.toFixed(2)}</strong></div>` : ''}
                  ${stock?.fifty_two_week_low != null && stock.fifty_two_week_high != null ? `<div class="muted">52W: $${stock.fifty_two_week_low.toFixed(2)} – $${stock.fifty_two_week_high.toFixed(2)}</div>` : ''}
                </div>
              </div>
              ${(avgIv != null || atmIv) ? `<table><thead><tr><th>Metric</th><th>Value</th></tr></thead><tbody>
                ${avgIv != null ? `<tr><td>Avg chain IV</td><td>${avgIv}%</td></tr>` : ''}
                ${atmIv ? `<tr><td>Implied move (next earnings)</td><td>${atmIv}</td></tr>` : ''}
              </tbody></table>` : ''}
              ${fundamentalRows.length > 0 ? `<table><thead><tr><th>Fundamental</th><th>Value</th></tr></thead><tbody>
                ${fundamentalRows.map((c) => `<tr><td>${c.concept}</td><td>${formatFundamental(c.value, c.unit)}</td></tr>`).join('')}
              </tbody></table>` : ''}
              ${insiderSignalLine ? `<p class="muted">Insider signal: ${insiderSignalLine}</p>` : ''}
              <div class="footer">Data: NavOption research · Generated ${dateStr} · For informational purposes only. Not investment advice.</div>
              </body></html>`)
              printWin.document.close()
              printWin.focus()
              printWin.print()
            }}
          >
            Export tear sheet
          </button>
          </div>
        </div>
      </header>

      {watchMessage && <p className="form-message">{watchMessage}</p>}
      {error && <p className="form-message form-message--error">{error}</p>}

      <SymbolStats chain={chain} />

      <WheelPanel symbol={symbol} underlying={underlying} hideStrikes={hideStrikes} />

      <div id="chain">
        <ChainBoard rows={chain?.rows || []} underlying={underlying} loading={loading} hideStrikes={hideStrikes} />
      </div>

      <div className="symbol-two-col">
        <article id="news" className="analyze-card">
          <h3>News summaries</h3>
          {signedIn && news.length > 0 ? (
            <ul className="symbol-news-list">
              {news.map((item) => (
                <li key={item.id}>
                  <a href={item.url} target="_blank" rel="noreferrer">{decodeEntities(item.title)}</a>
                  <small>{item.source} · {formatNewsTime(item.published_at)}</small>
                  {item.summary && <p>{decodeEntities(item.summary)}</p>}
                </li>
              ))}
            </ul>
          ) : signedIn ? (
            <p className="muted">No symbol-specific news in the current NavOption cache.</p>
          ) : (
            <p className="muted">Sign in to see symbol-matched news from the NavOption market cache.</p>
          )}
        </article>
        <div id="earnings" className="symbol-side-stack">
          {earningsCard && <EarningsWeekCard data={earningsCard} />}
          {ivEval && <IVEvaluationCard data={ivEval} />}
          {!earningsCard && !ivEval && (
            <article className="analyze-card">
              <h3>Earnings + IV</h3>
              <p className="muted">
                {signedIn
                  ? <>
                      No earnings within 14 days for {symbol}. IV history is still
                      building — this card fills in automatically.
                    </>
                  : <>
                      IV evaluation builds from NavOption&apos;s 30-minute RTH snapshot
                      history; the earnings-week card is available after you{' '}
                      <Link to="/login" className="link-like">sign in</Link>.
                    </>}
              </p>
            </article>
          )}
        </div>
      </div>

      {fundamentals && fundamentals.concepts && fundamentals.concepts.length > 0 && (
        <article className="analyze-card">
          <h3>Latest financials</h3>
          <p className="muted" style={{ marginBottom: 6 }}>
            From the company’s most recent SEC filing — period ending {fundamentals.updated?.slice(0, 10)}
          </p>
          <ul className="analyze-history" style={{ columnCount: 2, listStyle: 'none', padding: 0 }}>
            {fundamentals.concepts.slice(0, 8).map((c) => (
              <li key={c.concept + c.period_end}>
                <small className="muted">{c.concept}</small>{' '}
                <strong>{formatFundamental(c.value, c.unit)}</strong>
              </li>
            ))}
          </ul>
        </article>
      )}
      {!signedIn && (
        <article id="insider" className="analyze-card">
          <h3>Insider activity</h3>
          <p className="muted">
            <Link to="/login" className="link-like">Sign in</Link> to see SEC Form 4 insider buy / sell flow for {symbol}.
          </p>
        </article>
      )}
      {signedIn && insider && insider.window &&
        (insider.window.buy_shares > 0 ||
         insider.window.sell_shares > 0 ||
         (insider.transactions && insider.transactions.length > 0)) && (
        <article id="insider" className="analyze-card">
          <h3>Insider activity · {insider.window.window_days}d window</h3>
          <p>
            <strong className={insider.window.net_shares >= 0 ? 'up' : 'down'}>
              Net {insider.window.net_shares >= 0 ? '+' : ''}{formatVolume(Math.round(insider.window.net_shares))} sh
            </strong>
            <span className="muted"> · {formatRevenue(insider.window.net_value)}</span>
            <span className="muted"> · {insider.window.buyer_count} buyers</span>
          </p>
          {insider.summary && insider.summary.signal !== 'quiet' && (
            <p className="insider-signal">
              <span className={`pill pill--${insider.summary.signal === 'net_buying' ? 'green' : insider.summary.signal === 'net_selling' ? 'rose' : 'gray'}`}>
                {insider.summary.signal === 'net_buying' ? 'Insiders net buying' : insider.summary.signal === 'net_selling' ? 'Insiders net selling' : 'Mixed insider activity'}
              </span>
              {insider.summary.exec_involved && <span className="muted"> · incl. CEO/CFO</span>}
            </p>
          )}
          {insider.transactions && insider.transactions.length > 0 && (
            <table className="scanner-table">
              <thead>
                <tr>
                  <th className="text-left">Filed</th>
                  <th className="text-left">Insider</th>
                  <th className="text-left">Role</th>
                  <th className="text-left">Code</th>
                  <th>Shares</th>
                  <th>$/sh</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {insider.transactions.slice(0, 12).map((t, i) => (
                  <tr key={`${t.filing_date}-${t.insider_name}-${i}`}>
                    <td className="text-left">{formatExpiration(t.filing_date)}</td>
                    <td className="text-left">{t.insider_name}</td>
                    <td className="text-left muted">{t.insider_role || '—'}</td>
                    <td className="text-left"><span className="badge">{t.transaction_code}</span></td>
                    <td>{formatVolume(Math.round(t.shares))}</td>
                    <td>{t.price_per_share ? `$${t.price_per_share.toFixed(2)}` : '—'}</td>
                    <td>{t.total_value ? formatRevenue(t.total_value) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </article>
      )}

      {signedIn && smartMoney && smartMoney.summary &&
        (smartMoney.summary.held_count > 0 ||
         smartMoney.summary.total_value_usd > 0 ||
         (smartMoney.holdings && smartMoney.holdings.length > 0)) && (
        <article id="institutional" className="analyze-card">
          <h3>What big funds own · {smartMoney.summary.quarter ? formatExpiration(smartMoney.summary.quarter) : '?'} filing</h3>
          <p>
            <span className="muted">
              {(() => {
                const funds = Math.max(smartMoney.summary.held_count, smartMoney.holdings?.length || 0)
                return <>held by {funds} {funds === 1 ? 'fund' : 'funds'} · total <strong>{formatRevenue(smartMoney.summary.total_value_usd)}</strong></>
              })()}
            </span>
            {(() => {
              const parts = [
                smartMoney.summary.new_count > 0 ? `+${smartMoney.summary.new_count} new` : '',
                smartMoney.summary.added_count > 0 ? `+${smartMoney.summary.added_count} added` : '',
                smartMoney.summary.trimmed_count > 0 ? `-${smartMoney.summary.trimmed_count} trimmed` : '',
                smartMoney.summary.exited_count > 0 ? `-${smartMoney.summary.exited_count} exited` : '',
              ].filter(Boolean)
              return parts.length > 0 ? <span className="muted"> · {parts.join(' · ')}</span> : null
            })()}
          </p>
          {smartMoney.holdings && smartMoney.holdings.length > 0 && (
            <table className="scanner-table">
              <thead>
                <tr>
                  <th className="text-left">Filer</th>
                  <th>Value</th>
                  <th>Shares</th>
                  <th>Δ shares</th>
                  <th>Δ %</th>
                </tr>
              </thead>
              <tbody>
                {smartMoney.holdings.slice(0, 12).map((h, i) => (
                  <tr key={`${h.filer_name}-${i}`}>
                    <td className="text-left">{h.filer_name}</td>
                    <td>{formatRevenue(h.value_usd)}</td>
                    <td>{formatVolume(Math.round(h.shares))}</td>
                    <td className={h.shares_change >= 0 ? 'up' : 'down'}>{h.shares_change >= 0 ? '+' : ''}{formatVolume(Math.round(h.shares_change))}</td>
                    <td className={h.percent_change != null && h.percent_change >= 0 ? 'up' : 'down'}>{h.percent_change != null ? `${h.percent_change >= 0 ? '+' : ''}${h.percent_change.toFixed(1)}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </article>
      )}
    </section>
  )
}

function SymbolFact({ label, value, tone }: { label: string; value: string; tone?: number | null }) {
  const toneClass = typeof tone === 'number' ? (tone >= 0 ? 'up' : 'down') : ''
  return (
    <div className="symbol-fact">
      <span>{label}</span>
      <strong className={toneClass}>{value}</strong>
    </div>
  )
}

type WatchSortKey = 'symbol' | 'signal' | 'price' | 'iv_rank' | 'next_event' | 'csp_apy' | 'positions'

function WatchlistPage({ signedIn, isPro, calendarEnabled = false }: {
  signedIn: boolean; isPro: boolean; calendarEnabled?: boolean
}) {
  const [items, setItems] = useState<WatchlistItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<WatchFilter>('all')
  const [sortKey, setSortKey] = useState<WatchSortKey>('signal')
  const [sortDesc, setSortDesc] = useState(true)
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null)
  const liveQuotes = useLiveQuotes()
  const calendarEvents = useCalendarEvents(60, calendarEnabled)

  useEffect(() => {
    if (!openMenuFor) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (!t.closest('.row-menu') && !t.closest('.row-menu-trigger')) {
        setOpenMenuFor(null)
      }
    }
    document.addEventListener('click', onDoc)
    return () => document.removeEventListener('click', onDoc)
  }, [openMenuFor])

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api<{ items: WatchlistItem[] }>('/v1/watchlist')
      setItems(res?.items ?? [])
      setError(null)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { if (signedIn) refresh() }, [signedIn, refresh])

  const remove = async (symbol: string) => {
    try {
      await api(`/v1/watchlist/${encodeURIComponent(symbol)}`, { method: 'DELETE' })
    } catch {
      /* swallow — refresh below will show current state */
    }
    refresh()
  }

  const counts = useMemo(() => watchlistFilterCounts(items), [items])

  const filtered = useMemo(() => {
    const f = items.filter((it) => {
      switch (filter) {
        case 'earnings':
          return it.next_event?.type === 'earnings' &&
            (new Date(it.next_event.date).getTime() - Date.now()) <= 7 * 24 * 3600 * 1000
        case 'high_iv':
          return watchlistIVSignal(it) > 50
        case 'signals':
          return (it.signal?.score ?? 0) >= 55
        case 'has_positions':
          return (it.open_positions_count ?? 0) > 0
        default:
          return true
      }
    })
    return [...f].sort((a, b) => {
      const dir = sortDesc ? -1 : 1
      switch (sortKey) {
        case 'symbol':
          return a.symbol.localeCompare(b.symbol) * dir
        case 'signal':
          return ((a.signal?.score ?? 0) - (b.signal?.score ?? 0)) * dir
        case 'price':
          return ((liveQuotes[a.symbol]?.price ?? 0) - (liveQuotes[b.symbol]?.price ?? 0)) * dir
        case 'iv_rank':
          return (watchlistIVSignal(a) - watchlistIVSignal(b)) * dir
        case 'next_event': {
          const ad = a.next_event ? new Date(a.next_event.date).getTime() : Number.MAX_SAFE_INTEGER
          const bd = b.next_event ? new Date(b.next_event.date).getTime() : Number.MAX_SAFE_INTEGER
          return (ad - bd) * dir
        }
        case 'csp_apy':
          return ((a.csp_apy_30d ?? -1) - (b.csp_apy_30d ?? -1)) * dir
        case 'positions':
          return ((a.open_positions_count ?? 0) - (b.open_positions_count ?? 0)) * dir
        default:
          return 0
      }
    })
  }, [items, filter, sortKey, sortDesc, liveQuotes])

  const setSort = (k: WatchSortKey) => {
    if (sortKey === k) setSortDesc((d) => !d)
    else { setSortKey(k); setSortDesc(true) }
  }
  const sortIndicator = (k: WatchSortKey) => sortKey === k ? (sortDesc ? ' ↓' : ' ↑') : ''

  if (!signedIn) {
    return <div className="page"><h2>Sign in to use the watchlist.</h2></div>
  }

  return (
    <div className="page watchlist-page">
      <div className="watchlist-inner">
        <MacroStrip />
        <header className="page-header">
          <div className="eyebrow">WATCHLIST</div>
          <h2>Your symbols</h2>
          <WatchlistStats items={items} />
        </header>

        {calendarEnabled && <EventCalendarPanel events={calendarEvents} />}

        <WatchlistFilterBar value={filter} onChange={setFilter} counts={counts} />

        {loading && items.length === 0 && (
          <LoadingState variant="list" rows={5} label="Loading watchlist..." />
        )}

        {!loading && items.length === 0 && (
          <div className="scanner-empty" style={{ textAlign: 'center', padding: '32px 16px' }}>
            <p style={{ fontSize: 16, marginBottom: 8 }}>Your watchlist is empty.</p>
            <p className="muted" style={{ marginBottom: 16 }}>
              Try a few popular wheel-friendly stocks to get started:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {['AAPL', 'MSFT', 'NVDA', 'SPY'].map((sym) => (
                <button
                  key={sym}
                  className="button button--ghost"
                  onClick={async () => {
                    try {
                      await api('/v1/watchlist', { method: 'POST', body: JSON.stringify({ symbol: sym }) })
                      refresh()
                    } catch (e) { /* surfaced via list reload */ }
                  }}
                >+ {sym}</button>
              ))}
            </div>
            <p className="muted" style={{ marginTop: 18, fontSize: 12 }}>
              Or browse <Link to="/scan" className="link-like">option ideas</Link> and add from there.
            </p>
          </div>
        )}

        {filtered.length > 0 && (
          <table className="watchlist-table">
            <thead>
              <tr>
                <th onClick={() => setSort('symbol')}>Symbol{sortIndicator('symbol')}</th>
                <th onClick={() => setSort('signal')}>Radar Signal{sortIndicator('signal')}</th>
                <th onClick={() => setSort('price')}>Price · Chg{sortIndicator('price')}</th>
                <th onClick={() => setSort('iv_rank')} className="text-right">IV Rank / ATM{sortIndicator('iv_rank')}</th>
                <th onClick={() => setSort('next_event')}>Next Event{sortIndicator('next_event')}</th>
                <th onClick={() => setSort('csp_apy')} className="text-right">CSP APY 30D{sortIndicator('csp_apy')}</th>
                <th onClick={() => setSort('positions')} className="text-right">Positions{sortIndicator('positions')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const live = liveQuotes[it.symbol]
                const price = live?.price
                const chg = live?.changePct
                return (
                  <tr key={it.symbol}>
                    <td className="symbol-cell"><SymbolLink symbol={it.symbol} /></td>
                    <td><WatchlistSignalBadge signal={it.signal} /></td>
                    <td className="mono price-cell">
                      {typeof price === 'number' ? `$${price.toFixed(2)}` : '—'}
                      {typeof chg === 'number' && (
                        <span className={`chg-inline ${chg < 0 ? 'down' : 'up'}`}>
                          {chg < 0 ? '▼' : '▲'}{Math.abs(chg).toFixed(2)}%
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      <WatchlistIVCell item={it} />
                    </td>
                    <td>
                      {it.next_event ? (
                        <EventChip event={{
                          date: it.next_event.date,
                          type: it.next_event.type as CalendarEventType,
                          impact: it.next_event.impact as CalendarImpact,
                          label: it.next_event.label,
                          symbol: it.symbol,
                        }} />
                      ) : '—'}
                    </td>
                    <td className="text-right">
                      {typeof it.csp_apy_30d === 'number'
                        ? <span className="csp-apy-pill">{it.csp_apy_30d.toFixed(1)}%</span>
                        : '—'}
                    </td>
                    <td className="text-right">
                      {(it.open_positions_count ?? 0) > 0 ? (
                        <a href={`/positions?symbol=${it.symbol}`} className="positions-chip">{it.open_positions_count}</a>
                      ) : '—'}
                    </td>
                    <td className="row-menu-cell">
                      <button
                        type="button"
                        className="row-menu-trigger"
                        aria-label="Row actions"
                        onClick={(e) => { e.stopPropagation(); setOpenMenuFor(openMenuFor === it.symbol ? null : it.symbol) }}
                      >⋯</button>
                      {openMenuFor === it.symbol && (
                        <div className="row-menu" role="menu">
                          <a href={`/analyze?symbol=${it.symbol}`} role="menuitem">Analyze</a>
                          <a href={`/scan?symbol=${it.symbol}`} role="menuitem">Scan</a>
                          <button type="button" className="row-menu-danger" onClick={() => { setOpenMenuFor(null); remove(it.symbol) }} role="menuitem">Remove</button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {error && <div className="scanner-empty" role="alert">{error}</div>}
      </div>
    </div>
  )
}

function WatchlistIVCell({ item }: { item: WatchlistItem }) {
  if (typeof item.iv_rank === 'number') return <IVRankBadge value={item.iv_rank} />
  if (typeof item.atm_iv === 'number') {
    return (
      <span className="atm-iv-pill" title="IV rank needs enough historical range; showing current ATM IV.">
        <span className="atm-iv-label">ATM</span>{item.atm_iv.toFixed(1)}%
      </span>
    )
  }
  return <span className="watchlist-missing" title="No current option-chain IV available.">No IV</span>
}

function WatchlistSignalBadge({ signal }: { signal?: WatchlistSignal | null }) {
  if (!signal) return <span className="watchlist-missing">No signal</span>
  const level = signal.level === 'hot' || signal.level === 'watch' ? signal.level : 'steady'
  return (
    <span className={`watch-signal watch-signal-${level}`} title={signal.reason}>
      <span className="watch-signal-score">{Math.round(signal.score)}</span>
      <span className="watch-signal-copy">
        <strong>{signal.label || 'Steady'}</strong>
        <em>{signal.reason || 'No urgent catalyst'}</em>
      </span>
    </span>
  )
}

function IVRankBadge({ value }: { value: number }) {
  const tone = value >= 70 ? 'high' : value >= 30 ? 'mid' : 'low'
  return (
    <span className={`iv-rank-badge iv-rank-${tone}`}>
      <span className="iv-rank-bar" style={{ width: `${Math.round(value)}%` }} />
      <span className="iv-rank-num">{Math.round(value)}</span>
    </span>
  )
}

type EarningsPastMove = {
  report_date: string
  realized_move_pct: number
  surprise_pct?: number | null
}

type EarningsEntry = {
  symbol: string
  next_earnings_at: string
  est_eps?: number | null
  est_revenue?: number | null
  days_out: number
  setup_score: number
  setup_level: string
  setup_reason: string
  session: string
  implied_move_pct?: number | null
  prior_actual_eps?: number | null
  prior_surprise_pct?: number | null
  yoy_eps_growth_pct?: number | null
  past_moves?: EarningsPastMove[] | null
  avg_abs_realized_move_pct?: number | null
}

type EarningsRecapItem = {
  symbol: string
  report_date: string
  actual_eps?: number | null
  est_eps?: number | null
  surprise_pct?: number | null
  realized_move_pct?: number | null
  iv_before_pct?: number | null
  iv_after_pct?: number | null
  iv_crush_pct?: number | null
  position_note?: string | null
}

type EarningsWeekCardPageData = {
  recaps?: EarningsRecapItem[] | null
}

function formatEarningsDate(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// Backend text fields may carry HTML entities (&apos; &amp; …) or markdown bold; normalize before render.
function decodeEntities(text: string): string {
  if (!text.includes('&')) return text
  const el = document.createElement('textarea')
  el.innerHTML = text
  return el.value
}

function renderInlineBold(text: string): React.ReactNode {
  const parts = text.split(/\*\*([^*]+)\*\*/g)
  if (parts.length === 1) return text
  return parts.map((p, i) => (i % 2 === 1 ? <strong key={i}>{p}</strong> : p))
}

function formatRevenue(value?: number | null): string {
  if (value == null) return '—'
  const abs = Math.abs(value)
  if (abs >= 1e12) return `$${(value / 1e12).toFixed(2)}T`
  if (abs >= 1e9) return `$${(value / 1e9).toFixed(2)}B`
  if (abs >= 1e6) return `$${(value / 1e6).toFixed(1)}M`
  return `$${value.toFixed(0)}`
}

function formatEps(value?: number | null): string {
  if (value == null) return '—'
  const sign = value < 0 ? '-' : ''
  return `${sign}$${Math.abs(value).toFixed(2)}`
}

function formatSignedPct(value?: number | null): string {
  if (value == null) return '—'
  const sign = value >= 0 ? '+' : ''
  return `${sign}${value.toFixed(1)}%`
}

function formatPct(value?: number | null): string {
  if (value == null) return '—'
  return `${value.toFixed(1)}%`
}

function pctClass(value?: number | null): string {
  if (value == null) return ''
  if (value > 0) return 'up'
  if (value < 0) return 'down'
  return ''
}

function setupPillClass(level: string): string {
  const lower = level.toLowerCase()
  if (lower === 'urgent' || lower === 'high' || lower === 'strong') return 'pill pill--rose'
  if (lower === 'medium' || lower === 'med' || lower === 'watch') return 'pill pill--amber'
  if (lower === 'low' || lower === 'calm') return 'pill pill--green'
  return 'pill'
}

// ─── Daily post-close report (/brief) ───────────────────────────────
type BriefSignal = 'green' | 'yellow' | 'red' | 'none'
type BriefVix = { value?: number; change_pct?: number }
// midday_market indices/sectors use `price` (intraday) instead of `close`;
// BriefMoverTable reads price ?? close so both close_recap and midday_market work.
type BriefMover = { symbol: string; name?: string; close?: number; price?: number; change_pct?: number }
// Universe-wide advance/decline tally for the session (every symbol in
// daily_closes, not just the index/sector ETFs). postclose close_recap only.
type BriefBreadth = { advancers?: number; decliners?: number; unchanged?: number; universe?: number }
type BriefCloseRecap = {
  indices?: BriefMover[]
  sectors?: BriefMover[]
  vix?: BriefVix
  yields?: Record<string, number>
  breadth?: BriefBreadth
  // midday `closing_watch` only: what is still ahead in today's session, as
  // rows rather than a sentence. `label` is the calendar's own event name on a
  // macro row; every other word on screen is ours.
  closing_watch?: BriefClosingWatchRow[]
}
// postclose `movers` — the session's largest absolute movers, each optionally
// paired with a same-day headline. news_state is explicit: matched | none |
// unavailable. `none` renders "no notable headline"; `unavailable` renders
// nothing at all (no news source was configured, which is not the same claim).
// `reason` says which of our own datasets also names the symbol today
// (earnings | uoa), or null when neither does. It is a cross-reference, never a
// cause: the move and the reason are two separate facts on one row.
type BriefMoversData = {
  movers?: { symbol: string; close?: number; change_pct?: number; headline?: string; news_state?: string; reason?: string | null }[]
}
// postclose `key_levels` — where option open interest is densest for the next
// session. Gross basis only; never support/resistance (see 0072 migration).
type BriefTopStrike = { strike?: number; side?: string; gross_gex?: number }
type BriefKeyLevels = {
  trade_date?: string
  levels?: {
    symbol: string
    snapshot_ts?: string
    spot?: number
    put_wall?: number
    call_wall?: number
    gross_gex_total?: number
    top_strikes?: BriefTopStrike[]
  }[]
}
// One of a symbol's ten largest prints by premium. `side` is the contract's
// call/put, never a buy/sell inference — the tick source carries no direction
// and no open interest, so nothing here says who traded or which way.
type BriefTopPrint = {
  condition?: number
  condition_name?: string
  expiration?: string
  premium_usd?: number
  side?: string
  size?: number
  strike?: number
}
// postclose `option_flow` — two blocks with deliberately different vintages.
// verified_flow is the PREVIOUS settled session's tick-level structure (the
// source is T+1); unusual_activity is today's volume outliers. The two must
// never be rendered as one same-day picture — hence the separate as-of labels.
type BriefOptionFlow = {
  verified_flow?: {
    symbol: string
    blocks?: number
    sweeps?: number
    block_premium_usd?: number
    sweep_premium_usd?: number
    clean_volume?: number
    // 0.0-1.0 fraction, not a percentage — rendered ×100.
    multi_leg_pct?: number
    // Ten largest prints by premium, descending. Rendered collapsed — the
    // per-symbol row must stay one row.
    top_prints?: BriefTopPrint[]
  }[]
  verified_as_of?: string
  unusual_activity?: BriefUoaItem[]
  unusual_as_of?: string
}
type BriefUoaItem = { symbol: string; side?: string; strike?: number; dte?: number; multiple?: number; notional?: number; tier?: string }
type BriefUoaSummary = {
  counts?: Record<string, number>
  tiers?: Record<string, BriefUoaItem[]>
}
// actual_eps / surprise_pct only exist on a postclose `earnings_recap` row and
// only after the number is filed — both stay optional and print "—" when absent.
type BriefEarningsRow = {
  symbol: string
  actual_eps?: number | null
  est_eps?: number | null
  surprise_pct?: number | null
  est_revenue?: number
  atm_iv?: number
  iv_change?: number
  session?: string
}
type BriefEarningsRecap = {
  reported?: BriefEarningsRow[]
  // premarket `today_earnings` reuses this shape but keys the list `earnings`.
  earnings?: BriefEarningsRow[]
  // `today_earnings` only: the report date this roster was diffed against.
  // Present means every row below is a name yesterday's list did not carry;
  // absent means there was no prior report and the roster is the full one.
  diff_from?: string
}
// premarket `earnings_iv_watch` — symbols with upcoming earnings whose latest
// ATM-IV sits near its own trailing-{window_days}d high (descriptive percentile
// ranking of historical snapshots, never a prediction / hit-rate claim).
// percentile is a 0..1 fraction — render as ×100.
type BriefEarningsIvWatch = {
  watch?: { symbol: string; session?: string; earnings_at?: string; atm_iv?: number; percentile?: number; sample_size?: number }[]
  window_days?: number
}
// report.carryoverRow — `close`/`change_pct` are absent for a symbol-less item
// and for a symbol with no close today, so both stay optional and are only
// printed when sent.
type BriefCarryoverRow = {
  kind: 'earnings' | 'ex_div' | 'macro' | string
  symbol?: string
  session?: string
  label: string
  close?: number | null
  change_pct?: number | null
}
// report.closingWatchRow — kind is the row TYPE (macro | earnings), not
// macro_events.kind.
type BriefClosingWatchRow = {
  kind: 'macro' | 'earnings' | string
  label?: string
  occurs_at?: string
  symbol?: string
  session?: string
}

type BriefTomorrowWatch = {
  // `carryover` replaced the continuity card: the previous session's watch list
  // restated as rows, each named symbol carrying today's settled print. The
  // server's `label` is Chinese and deliberately unused — the kind and session
  // words come from this client's own interface language.
  carryover?: BriefCarryoverRow[]
  earnings?: { symbol: string; session?: string }[]
  events?: { label: string; kind?: string; impact?: string; occurs_at?: string }[]
  // postclose v2 additions — corporate actions that change an option position's
  // mechanics, plus a short excerpt of today's wheel ranking. Both optional.
  dividends?: { symbol: string; ex_div_date?: string; amount?: number; yield_pct?: number }[]
  wheel_picks?: BriefWheelRow[]
  wheel_as_of?: string
}
type BriefWheelRow = {
  symbol: string
  side?: string
  strike?: number
  dte?: number
  apy?: number
  iv?: number
  premium?: number
  // Tri-state: true = earnings falls inside the DTE window, false = checked
  // and clear, null/undefined = the check was not performed successfully.
  // null must NOT be rendered as "no earnings" — see briefSectionBody.
  earnings_in_dte?: boolean | null
}
// A carryover row in the reader's own language: the server sends the facts and
// a Chinese `label`, and only the macro row's calendar event name is taken from
// it — that name has no translation of ours to use instead.
function briefCarryoverText(t: ReturnType<typeof useI18n>['t'], r: BriefCarryoverRow): string {
  let head: string
  if (r.kind === 'macro') {
    head = r.label
  } else {
    const kindWord = r.kind === 'ex_div' ? t('brief.kind.exDiv') : t('brief.kind.earnings')
    const session = briefSessionLabel(t, r.session)
    head = `${r.symbol || ''} ${kindWord}`.trim() + (session ? ` (${session})` : '')
  }
  const print: string[] = []
  if (r.close != null) print.push(`$${briefNum(r.close)}`)
  if (r.change_pct != null) print.push(briefPct(r.change_pct))
  return print.length > 0 ? `${head} ${print.join(' ')}` : head
}

// Times are ET: these are market events, and the session they sit in is the
// whole point of the line.
function briefClosingWatchText(t: ReturnType<typeof useI18n>['t'], r: BriefClosingWatchRow): string {
  if (r.kind === 'macro') {
    const when = r.occurs_at
      ? new Date(r.occurs_at).toLocaleTimeString('en-US', {
          timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
        })
      : ''
    return [r.label || '', when].filter(Boolean).join(' ')
  }
  const session = briefSessionLabel(t, r.session)
  return `${r.symbol || ''}${session ? ` (${session})` : ''}`.trim()
}

function briefTierLabel(t: ReturnType<typeof useI18n>['t'], key: string): string {
  switch (key) {
    case 'smart_flow': return t('brief.tier.smart_flow')
    case 'contrarian': return t('brief.tier.contrarian')
    case 'lottery': return t('brief.tier.lottery')
    case 'bearish_hedge': return t('brief.tier.bearish_hedge')
    default: return key
  }
}
function briefSessionLabel(t: ReturnType<typeof useI18n>['t'], session?: string | null): string {
  if (!session) return ''
  switch (session.toUpperCase()) {
    case 'BMO': return t('brief.session.bmo')
    case 'AMC': return t('brief.session.amc')
    case 'MID': return t('brief.session.mid')
    default: return ''
  }
}
// The closed vocabulary is earnings | uoa; anything else prints as sent rather
// than being dropped.
function briefMoverReasonLabel(t: ReturnType<typeof useI18n>['t'], reason: string): string {
  switch (reason) {
    case 'earnings': return t('brief.moverReason.earnings')
    case 'uoa': return t('brief.moverReason.uoa')
    default: return reason
  }
}
function briefMoney(v: number | undefined | null): string {
  if (v == null || Number.isNaN(v)) return '—'
  return `$${v.toFixed(2)}`
}
type BriefSection = {
  key: string
  title: string
  status: 'ok' | 'missing'
  signal: BriefSignal
  data: unknown
  narrative?: string
}
type BriefReport = {
  trade_date: string
  slot: string
  status: 'ok' | 'degraded'
  data_cutoff: string
  headline: string
  sections: BriefSection[]
  briefing?: BriefBriefing
  coverage?: BriefCoverage
}

function briefPct(v: number | undefined | null): string {
  if (v == null || Number.isNaN(v)) return '—'
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`
}
function briefNum(v: number | undefined | null, digits = 2): string {
  if (v == null || Number.isNaN(v)) return '—'
  return v.toFixed(digits)
}
function briefNotional(v: number | undefined | null): string {
  if (v == null || Number.isNaN(v)) return '—'
  const abs = Math.abs(v)
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v.toFixed(0)}`
}
// Compact one-line rendering of key_levels' top_strikes bucket list:
// "620P · 630C · 615P", densest first, capped at three so the row stays a row.
function briefTopStrikes(rows: BriefTopStrike[] | undefined): string {
  if (!Array.isArray(rows) || !rows.length) return '—'
  const parts = rows.slice(0, 3).map((r) => {
    if (r.strike == null) return ''
    const side = r.side === 'put' ? 'P' : r.side === 'call' ? 'C' : ''
    return `${briefNum(r.strike, 0)}${side}`
  }).filter(Boolean)
  return parts.length ? parts.join(' · ') : '—'
}
// OPRA condition names, as the tick source spells them, mapped to a plain
// wording for HOW the print executed. Execution venue/mechanism only — these
// labels never say who traded or which way. Any code this client does not
// know renders the source's own name unchanged rather than being dropped.
function briefConditionLabel(t: ReturnType<typeof useI18n>['t'], name?: string): string {
  switch (name) {
    case 'Single Leg Cross Non ISO': return t('brief.cond.crossNonIso')
    case 'Single Leg Auction Non ISO': return t('brief.cond.auctionNonIso')
    case 'Single Leg Floor Trade': return t('brief.cond.floorTrade')
    case 'Automatic Execution': return t('brief.cond.autoExecution')
    case 'Intermarket Sweep Order': return t('brief.cond.intermarketSweep')
    default: return name || '—'
  }
}
// One print as a single dense line: contract, expiry, size, premium, and how
// it executed. Descriptive only — the tick source has no direction and no open
// interest, so no "bought"/"sold"/"opening" reading may be attached to it.
function briefPrintLine(t: ReturnType<typeof useI18n>['t'], p: BriefTopPrint): string {
  const contract = [(p.side || '').toUpperCase(), p.strike != null ? briefNum(p.strike, 0) : '']
    .filter(Boolean).join(' ')
  const parts = [contract || '—']
  if (p.expiration) parts.push(p.expiration)
  if (p.size != null) parts.push(`${p.size.toLocaleString()} ${t('brief.unit.contracts')}`)
  parts.push(briefNotional(p.premium_usd))
  parts.push(briefConditionLabel(t, p.condition_name))
  return parts.join(' · ')
}
// movers' news_state is a closed vocabulary the backend may extend. Each
// state is a DIFFERENT claim and must never collapse into one message:
// `none` = the lookup ran and matched nothing published today, `unavailable`
// = no news source is configured, `error` (and any value this client does not
// know yet) = the lookup did not complete. An absent state renders nothing —
// old reports predate the field and must not sprout a new caption.
function briefNewsStateNote(t: ReturnType<typeof useI18n>['t'], state?: string): string {
  if (!state) return ''
  switch (state) {
    case 'matched': return ''
    case 'none': return t('brief.news.none')
    case 'unavailable': return t('brief.news.unavailable')
    default: return t('brief.news.error')
  }
}
// key_levels' snapshot_ts rendered in ET — the exchange session the chain
// snapshot belongs to, not the reader's local zone.
function briefEtTimestamp(iso: string | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  })
}
function briefMoveClass(v: number | undefined | null): string {
  if (v == null || Number.isNaN(v)) return ''
  return v >= 0 ? 'up' : 'down'
}
// priceLabel/priceColumn let midday_market show "Price" (intraday) over the
// same table close_recap uses for "Close" (close-to-close); the value itself
// reads price ?? close so both payload shapes render correctly.
function BriefMoverTable({ label, rows, priceLabel }: { label: string; rows: BriefMover[]; priceLabel?: string }) {
  const { t } = useI18n()
  if (!rows.length) return null
  return (
    <div className="scanner-table-wrap">
      <table className="scanner-table">
        <thead><tr><th className="text-left">{label}</th><th>{priceLabel || t('brief.col.close')}</th><th>{t('brief.col.change')}</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${label}-${r.symbol}-${i}`}>
              <td className="text-left"><strong>{r.name || r.symbol}</strong></td>
              <td>{briefNum(r.price ?? r.close)}</td>
              <td className={briefMoveClass(r.change_pct)}>{briefPct(r.change_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function BriefSectionBody({ section }: { section: BriefSection }) {
  const { t } = useI18n()
  if (section.data == null) return null
  switch (section.key) {
    case 'close_recap':
    // midday `midday_market` reuses the index/sector mover layout (intraday
    // change instead of close-to-close).
    case 'midday_market': {
      const d = section.data as BriefCloseRecap
      const yieldKeys = d.yields ? Object.keys(d.yields) : []
      const priceLabel = section.key === 'midday_market' ? t('brief.col.price') : undefined
      return (
        <>
          <BriefMoverTable label={t('brief.col.index')} rows={d.indices || []} priceLabel={priceLabel} />
          <BriefMoverTable label={t('brief.col.sector')} rows={d.sectors || []} priceLabel={priceLabel} />
          {/* Universe breadth: the index prints say what the cap-weighted
              average did, the advance/decline tally says how many names
              actually participated. Omitted entirely when the tally failed. */}
          {/* The universe is this platform's option-liquid coverage, not the
              whole market — saying so is part of the number. */}
          {d.breadth && (d.breadth.universe ?? 0) > 0 && (
            <p className="brief-breadth">
              <span className="muted">{t('brief.breadth.universePrefix')}{d.breadth.universe}{t('brief.breadth.universeSuffix')}</span>
              <span className="up">{t('brief.breadth.advancers')} {d.breadth.advancers ?? 0}</span>
              {' · '}
              <span className="down">{t('brief.breadth.decliners')} {d.breadth.decliners ?? 0}</span>
              {' · '}
              <span>{t('brief.breadth.unchanged')} {d.breadth.unchanged ?? 0}</span>
              <span className="muted">{t('brief.breadth.notMarketWide')}</span>
            </p>
          )}
          {(d.vix?.value != null || yieldKeys.length > 0) && (
            <div className="brief-stat-row">
              {d.vix?.value != null && (
                <span className="brief-stat">VIX <strong>{briefNum(d.vix.value)}</strong>
                  {d.vix.change_pct != null && <em className={briefMoveClass(d.vix.change_pct)}>{briefPct(d.vix.change_pct)}</em>}
                </span>
              )}
              {yieldKeys.map((k) => (
                <span className="brief-stat" key={k}>{k} <strong>{briefNum(d.yields![k])}</strong></span>
              ))}
            </div>
          )}
          {(d.closing_watch || []).length > 0 && (
            <p className="brief-micro-label">
              {t('brief.closingWatch')}
              {d.closing_watch!.map((r) => briefClosingWatchText(t, r)).filter(Boolean).join(' · ')}
            </p>
          )}
        </>
      )
    }
    // postclose `movers` — biggest absolute movers with an optional same-day
    // headline. The headline is quoted as-is and never asserted as the cause.
    case 'movers': {
      const d = section.data as BriefMoversData
      const movers = d.movers || []
      if (!movers.length) return null
      return (
        <>
        <div className="scanner-table-wrap">
          <table className="scanner-table">
            <thead><tr><th className="text-left">{t('brief.col.symbol')}</th><th>{t('brief.col.close')}</th><th>{t('brief.col.change')}</th></tr></thead>
            <tbody>
              {movers.map((m, i) => {
                // `none` (the lookup ran and matched nothing dated today) and
                // `unavailable` (no news source configured) are different
                // claims and must never collapse into one message.
                const note = m.headline || briefNewsStateNote(t, m.news_state)
                return (
                  <Fragment key={`${m.symbol}-${i}`}>
                    <tr>
                      <td className="text-left">
                        <SymbolLink symbol={m.symbol} /> <CheckupLink symbol={m.symbol} />
                        {m.reason && <span className="brief-chip brief-chip--reason">{briefMoverReasonLabel(t, m.reason)}</span>}
                      </td>
                      <td>{m.close != null ? briefNum(m.close) : '—'}</td>
                      <td className={briefMoveClass(m.change_pct)}>{briefPct(m.change_pct)}</td>
                    </tr>
                    {note && (
                      <tr className="brief-news-row">
                        <td className="text-left" colSpan={3}>{note}</td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
        {/* Anti-causal note: a same-day headline sitting beside a -9% print
            reads as a cause even when the text never says so. */}
        <p className="muted brief-ivwatch-disclosure">{t('brief.movers.help')}</p>
        </>
      )
    }
    // postclose `key_levels` — gross-basis option-interest concentration. The
    // walls say where positioning sits, never where price will turn: the
    // "支撑/阻力" framing is forbidden here (see migration 0072).
    case 'key_levels': {
      const d = section.data as BriefKeyLevels
      const levels = d.levels || []
      if (!levels.length) return null
      return (
        <>
          <div className="scanner-table-wrap">
            <table className="scanner-table">
              <thead>
                <tr>
                  <th className="text-left">{t('brief.col.symbol')}</th>
                  <th>{t('brief.col.spot')}</th>
                  <th>{t('brief.col.putWall')}</th>
                  <th>{t('brief.col.callWall')}</th>
                  <th className="text-left">{t('brief.col.topStrikes')}</th>
                  <th className="text-left">{t('brief.col.snapshotTs')}</th>
                </tr>
              </thead>
              <tbody>
                {levels.map((lv, i) => (
                  <tr key={`${lv.symbol}-${i}`}>
                    <td className="text-left"><SymbolLink symbol={lv.symbol} /></td>
                    <td>{lv.spot != null ? briefNum(lv.spot) : '—'}</td>
                    <td>{lv.put_wall != null ? briefNum(lv.put_wall) : '—'}</td>
                    <td>{lv.call_wall != null ? briefNum(lv.call_wall) : '—'}</td>
                    <td className="text-left">{briefTopStrikes(lv.top_strikes)}</td>
                    {/* The chain snapshot these numbers were taken from — a
                        gross-GEX figure is only meaningful next to its as-of. */}
                    <td className="text-left">{briefEtTimestamp(lv.snapshot_ts)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Fixed, LLM-independent disclosure. Always renders: a bare "$620P"
              beside a symbol reads as a price target unless it is said outright
              that it is not one. The snapshot time is part of the sentence. */}
          <p className="muted brief-ivwatch-disclosure">
            {t('brief.keyLevels.disclosurePrefix')}
            {briefEtTimestamp(levels[0].snapshot_ts)}
            {t('brief.keyLevels.disclosureSuffix')}
          </p>
        </>
      )
    }
    // postclose `option_flow` — two blocks, two vintages. verified_flow is the
    // previous settled session (source is T+1) and MUST be labelled as such;
    // unusual_activity is today's volume outliers and is worded as
    // "成交集中", never as verified structure.
    case 'option_flow': {
      const d = section.data as BriefOptionFlow
      const verified = d.verified_flow || []
      const unusual = d.unusual_activity || []
      if (!verified.length && !unusual.length) return null
      return (
        <>
          {verified.length > 0 && (
            <>
              {/* Fixed label, not prose: the vintage is structural data from
                  the payload, so it can never drift with the narrative. */}
              <p className="brief-group-head">
                <span className="brief-vintage-badge">{t('brief.flow.verifiedBadge')}</span>
                {/* The date comes from the payload, never the phrase "previous
                    trading day" — a pipeline gap would turn that into a lie. */}
                <span className="brief-group-head__note">
                  {t('brief.flow.verifiedLabelPrefix')}{d.verified_as_of || '—'}{t('brief.flow.verifiedLabelSuffix')}
                </span>
              </p>
              <div className="scanner-table-wrap">
                <table className="scanner-table">
                  <thead>
                    <tr>
                      <th className="text-left">{t('brief.col.symbol')}</th>
                      <th>{t('brief.col.blocks')}</th>
                      <th>{t('brief.col.sweeps')}</th>
                      <th>{t('brief.col.blockPremium')}</th>
                      <th>{t('brief.col.sweepPremium')}</th>
                      <th>{t('brief.col.cleanVolume')}</th>
                      <th>{t('brief.col.multiLeg')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {verified.map((f, i) => {
                      const prints = f.top_prints || []
                      return (
                      <Fragment key={`${f.symbol}-${i}`}>
                      <tr>
                        <td className="text-left"><SymbolLink symbol={f.symbol} /></td>
                        <td>{f.blocks ?? '—'}</td>
                        <td>{f.sweeps ?? '—'}</td>
                        <td>{briefNotional(f.block_premium_usd)}</td>
                        <td>{briefNotional(f.sweep_premium_usd)}</td>
                        <td>{f.clean_volume != null ? f.clean_volume.toLocaleString() : '—'}</td>
                        {/* multi_leg_pct is a 0..1 fraction from the backend. */}
                        <td>{f.multi_leg_pct != null ? `${briefNum(f.multi_leg_pct * 100, 0)}%` : '—'}</td>
                      </tr>
                      {/* The per-print breakdown stays collapsed: ten lines per
                          symbol would bury the table it belongs to. Collapsed it
                          costs exactly one line, carrying the largest print's
                          premium so the row is worth opening (or not). The list
                          arrives sorted by premium descending. */}
                      {prints.length > 0 && (
                        <tr className="brief-prints-row">
                          <td colSpan={7} className="text-left">
                            <details className="brief-prints">
                              <summary>
                                {t('brief.flow.topPrintPrefix')}{briefNotional(prints[0].premium_usd)}{t('brief.flow.topPrintSuffix')}
                              </summary>
                              <ul className="brief-prints__list">
                                {prints.map((p, j) => (
                                  <li key={j}>{briefPrintLine(t, p)}</li>
                                ))}
                              </ul>
                            </details>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <p className="muted brief-ivwatch-disclosure">{t('brief.flow.help')}</p>
            </>
          )}
          {unusual.length > 0 && (
            <>
              <p className="brief-group-head">
                <span className="brief-group-head__note">
                  {t('brief.flow.unusualLabelPrefix')}{d.unusual_as_of || '—'}
                </span>
              </p>
              <div className="scanner-table-wrap">
                <table className="scanner-table">
                  <thead><tr><th className="text-left">{t('brief.col.symbol')}</th><th className="text-left">{t('brief.col.side')}</th><th>{t('brief.col.strike')}</th><th>{t('brief.col.dte')}</th><th>{t('brief.col.multiple')}</th><th>{t('brief.col.notional')}</th></tr></thead>
                  <tbody>
                    {unusual.map((it, i) => (
                      <tr key={`${it.symbol}-${i}`}>
                        <td className="text-left"><SymbolLink symbol={it.symbol} /> <CheckupLink symbol={it.symbol} /></td>
                        <td className="text-left">{it.side || '—'}</td>
                        <td>{it.strike != null ? briefNum(it.strike) : '—'}</td>
                        <td>{it.dte ?? '—'}</td>
                        <td>{it.multiple != null ? `${it.multiple.toFixed(1)}x` : '—'}</td>
                        <td>{briefNotional(it.notional)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )
    }
    case 'uoa_summary':
    // midday `uoa_morning` (open-to-now) reuses the tiered-summary layout unchanged.
    // premarket's `uoa_tail` node was dropped by the backend — no case needed.
    case 'uoa_morning': {
      const d = section.data as BriefUoaSummary
      const counts = d.counts || {}
      const countKeys = Object.keys(counts)
      const tiers = d.tiers || {}
      const tierKeys = Object.keys(tiers)
      return (
        <>
          {countKeys.length > 0 && (
            <div className="brief-stat-row">
              {countKeys.map((k) => (
                <span className="brief-stat" key={k}>{briefTierLabel(t, k)} <strong>{counts[k]}</strong></span>
              ))}
            </div>
          )}
          {tierKeys.map((tier) => {
            const items = tiers[tier] || []
            if (!items.length) return null
            return (
              <div className="scanner-table-wrap" key={tier}>
                <table className="scanner-table">
                  <thead><tr><th className="text-left" colSpan={6}>{briefTierLabel(t, tier)}</th></tr>
                    <tr><th className="text-left">{t('brief.col.symbol')}</th><th className="text-left">{t('brief.col.side')}</th><th>{t('brief.col.strike')}</th><th>{t('brief.col.dte')}</th><th>{t('brief.col.multiple')}</th><th>{t('brief.col.notional')}</th></tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={`${tier}-${it.symbol}-${i}`}>
                        <td className="text-left"><SymbolLink symbol={it.symbol} /> <CheckupLink symbol={it.symbol} /></td>
                        <td className="text-left">{it.side || '—'}</td>
                        <td>{it.strike != null ? briefNum(it.strike) : '—'}</td>
                        <td>{it.dte ?? '—'}</td>
                        <td>{it.multiple != null ? `${it.multiple.toFixed(1)}x` : '—'}</td>
                        <td>{briefNotional(it.notional)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          })}
        </>
      )
    }
    case 'earnings_recap':
    // premarket `today_earnings` reuses the earnings-row layout (session badge +
    // est EPS/revenue); backend keys the list `earnings` instead of `reported`.
    case 'today_earnings': {
      const d = section.data as BriefEarningsRecap
      const reported = d.reported || d.earnings || []
      if (!reported.length) return null
      // Only show the actual/surprise columns when at least one row was filed —
      // a column of dashes claims the data exists and is empty.
      const hasActual = reported.some((r) => r.actual_eps != null || r.surprise_pct != null)
      return (
        <div className="scanner-table-wrap">
          <table className="scanner-table">
            <thead><tr><th className="text-left">{t('brief.col.symbol')}</th>{hasActual && <th>{t('brief.col.actualEps')}</th>}<th>{t('brief.col.estEps')}</th>{hasActual && <th>{t('brief.col.surprise')}</th>}<th>{t('brief.col.estRev')}</th><th>{t('brief.col.atmIv')}</th><th>{t('brief.col.ivChange')}</th></tr></thead>
            <tbody>
              {reported.map((r, i) => {
                const sessionLabel = briefSessionLabel(t, r.session)
                return (
                <tr key={`${r.symbol}-${i}`}>
                  <td className="text-left">
                    <SymbolLink symbol={r.symbol} />
                    {sessionLabel && <span className="brief-chip brief-chip--session">{sessionLabel}</span>}
                    {d.diff_from && (
                      <span className="brief-chip brief-chip--reason" title={t('brief.diffFrom').replace('{d}', d.diff_from)}>
                        {t('brief.newSinceYesterday')}
                      </span>
                    )}
                  </td>
                  {hasActual && <td>{r.actual_eps != null ? briefNum(r.actual_eps) : '—'}</td>}
                  <td>{r.est_eps != null ? briefNum(r.est_eps) : '—'}</td>
                  {hasActual && <td className={briefMoveClass(r.surprise_pct)}>{r.surprise_pct != null ? briefPct(r.surprise_pct) : '—'}</td>}
                  <td>{r.est_revenue != null ? briefNotional(r.est_revenue) : '—'}</td>
                  <td>{r.atm_iv != null ? `${briefNum(r.atm_iv, 0)}` : '—'}</td>
                  <td className={briefMoveClass(r.iv_change)}>{r.iv_change != null ? briefPct(r.iv_change) : '—'}</td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    }
    case 'tomorrow_watch':
    // premarket `macro_watch` (today/next-2d events, no earnings) reuses the
    // same chips + events-table layout; empty sub-lists are skipped.
    case 'macro_watch': {
      const d = section.data as BriefTomorrowWatch
      const earnings = d.earnings || []
      const events = d.events || []
      const dividends = d.dividends || []
      const wheelPicks = d.wheel_picks || []
      // The earnings chips only need a group label once another group sits
      // below them — macro_watch never carries one, so its layout is unchanged.
      const grouped = dividends.length > 0 || wheelPicks.length > 0
      return (
        <>
          {(d.carryover || []).length > 0 && (
            <p className="brief-micro-label">
              {t('brief.carryover')}
              {d.carryover!.map((r) => briefCarryoverText(t, r)).filter(Boolean).join(' · ')}
            </p>
          )}
          {grouped && earnings.length > 0 && (
            <p className="brief-group-head"><span className="brief-group-head__label">{t('brief.group.earnings')}</span></p>
          )}
          {earnings.length > 0 && (
            <div className="brief-chip-row">
              {earnings.map((e, i) => {
                const sessionLabel = briefSessionLabel(t, e.session)
                return (
                  <span key={`${e.symbol}-${i}`} className="brief-chip-group">
                    <SymbolLink symbol={e.symbol} className="brief-chip" />
                    {sessionLabel && <span className="brief-chip brief-chip--session">{sessionLabel}</span>}
                  </span>
                )
              })}
            </div>
          )}
          {events.length > 0 && (
            <div className="scanner-table-wrap">
              <table className="scanner-table">
                <thead><tr><th className="text-left">{t('brief.col.event')}</th><th className="text-left">{t('brief.col.kind')}</th><th className="text-left">{t('brief.col.impact')}</th><th className="text-left">{t('brief.col.when')}</th></tr></thead>
                <tbody>
                  {events.map((ev, i) => (
                    <tr key={`${ev.label}-${i}`}>
                      <td className="text-left">{ev.label}</td>
                      <td className="text-left">{ev.kind || '—'}</td>
                      <td className="text-left">{ev.impact || '—'}</td>
                      <td className="text-left">{ev.occurs_at ? formatHistoryDate(ev.occurs_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Ex-dividend dates matter to covered-call writers: an in-the-money
              short call can be assigned early the day before one. */}
          {dividends.length > 0 && (
            <>
              <p className="brief-group-head"><span className="brief-group-head__label">{t('brief.group.dividends')}</span></p>
              <div className="scanner-table-wrap">
                <table className="scanner-table">
                  <thead><tr><th className="text-left">{t('brief.col.symbol')}</th><th className="text-left">{t('brief.col.exDivDate')}</th><th>{t('brief.col.divAmount')}</th><th>{t('brief.col.divYield')}</th></tr></thead>
                  <tbody>
                    {dividends.map((dv, i) => (
                      <tr key={`${dv.symbol}-${i}`}>
                        <td className="text-left"><SymbolLink symbol={dv.symbol} /></td>
                        <td className="text-left">{dv.ex_div_date || '—'}</td>
                        <td>{briefMoney(dv.amount)}</td>
                        <td>{dv.yield_pct != null ? `${briefNum(dv.yield_pct)}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {/* Wheel excerpt: today's ranked snapshot, top few only. APY is
              annualized off the snapshot premium — never recomputed live. */}
          {wheelPicks.length > 0 && (
            <>
              <p className="brief-group-head">
                <span className="brief-group-head__label">{t('brief.group.wheelPicks')}</span>
                {d.wheel_as_of ? <span className="brief-group-head__date">{t('brief.snapshotAsOf')} · {d.wheel_as_of}</span> : null}
              </p>
              <div className="scanner-table-wrap">
                <table className="scanner-table">
                  <thead>
                    <tr>
                      <th className="text-left">{t('brief.col.symbol')}</th>
                      <th className="text-left">{t('brief.col.side')}</th>
                      <th>{t('brief.col.strike')}</th>
                      <th>{t('brief.col.dte')}</th>
                      <th>{t('brief.col.premium')}</th>
                      <th>{t('brief.col.apy')}</th>
                      <th>{t('brief.col.iv')}</th>
                      <th>{t('brief.col.earningsFlag')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wheelPicks.map((r, i) => (
                      <tr key={`${r.symbol}-${i}`}>
                        <td className="text-left"><SymbolLink symbol={r.symbol} /> <CheckupLink symbol={r.symbol} /></td>
                        <td className="text-left">{r.side || '—'}</td>
                        <td>{r.strike != null ? briefNum(r.strike) : '—'}</td>
                        <td>{r.dte ?? '—'}</td>
                        <td>{briefMoney(r.premium)}</td>
                        <td>{r.apy != null ? briefPct(r.apy) : '—'}</td>
                        <td>{r.iv != null ? briefNum(r.iv, 0) : '—'}</td>
                        {/* null = the check never ran; only true/false may render. */}
                        <td>{r.earnings_in_dte === true ? '⚠️' : r.earnings_in_dte === false ? '—' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )
    }
    case 'earnings_iv_watch': {
      const d = section.data as BriefEarningsIvWatch
      const rows = d.watch || []
      // Empty watch[] renders nothing here — the section's fixed backend
      // narrative (shown by BriefSectionCard above this body) covers it, so we
      // never show a table shell with no rows.
      if (!rows.length) return null
      return (
        <>
          <div className="scanner-table-wrap">
            <table className="scanner-table">
              <thead><tr><th className="text-left">{t('brief.col.symbol')}</th><th>{t('brief.col.atmIv')}</th><th>{t('brief.col.ivPercentile')}</th><th className="text-left">{t('brief.col.earningsDate')}</th></tr></thead>
              <tbody>
                {rows.map((r, i) => {
                  const sessionLabel = briefSessionLabel(t, r.session)
                  return (
                    <tr key={`${r.symbol}-${i}`}>
                      <td className="text-left">
                        <SymbolLink symbol={r.symbol} />
                        {sessionLabel && <span className="brief-chip brief-chip--session">{sessionLabel}</span>}
                      </td>
                      <td>{r.atm_iv != null ? briefNum(r.atm_iv, 0) : '—'}</td>
                      <td>{r.percentile != null ? `${briefNum(r.percentile * 100, 0)}%` : '—'}</td>
                      <td className="text-left">{r.earnings_at ? formatHistoryDate(r.earnings_at) : '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="muted brief-ivwatch-disclosure">{t('brief.ivPercentileDisclosure')}</p>
        </>
      )
    }
    default:
      return null
  }
}

function BriefSectionCard({ section, degraded }: { section: BriefSection; degraded: boolean }) {
  const { t } = useI18n()
  const narrative = degraded ? '' : (section.narrative || '')
  return (
    <article className="analyze-card brief-section" id={briefSectionAnchor(section.key)} tabIndex={-1}>
      <div className="brief-section__head">
        <span className={`brief-dot brief-dot--${section.signal}`} aria-hidden="true" />
        <h3>{section.title}</h3>
        {section.key === 'close_recap' && <span className="brief-micro-label">{t('brief.marketDirection')}</span>}
      </div>
      {narrative && <p className="brief-narrative">{renderInlineBold(narrative)}</p>}
      <BriefSectionBody section={section} />
    </article>
  )
}

type BriefSlot = 'premarket' | 'midday' | 'postclose'
const BRIEF_SLOTS: BriefSlot[] = ['premarket', 'midday', 'postclose']

// Default slot follows ET wall-clock so a visitor lands on the freshest report
// the schedule has already produced: pre-market until 12:30, midday until 17:15,
// post-close after. Weekends have no premarket/midday runs, so they always
// default straight to postclose (the last trading day's recap). Matches iOS
// BriefView default logic.
function defaultBriefSlot(): BriefSlot {
  const { mins, weekday } = nowInET()
  if (weekday === 0 || weekday === 6) return 'postclose'
  if (mins < 12 * 60 + 30) return 'premarket'
  if (mins < 17 * 60 + 15) return 'midday'
  return 'postclose'
}

function BriefPage() {
  const { t } = useI18n()
  const [slot, setSlot] = useState<BriefSlot>(() => defaultBriefSlot())
  const [report, setReport] = useState<BriefReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [state, setState] = useState<'ok' | 'empty' | 'error'>('ok')
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setState('ok')
    setError('')
    setReport(null)
    api<BriefReport>(`/v1/reports/latest?slot=${slot}&lang=${getLanguage()}`)
      .then((res) => {
        if (cancelled) return
        setReport(res)
      })
      .catch((err: ApiError) => {
        if (cancelled) return
        if (err.status === 404) setState('empty')
        else { setState('error'); setError(err.message) }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [slot])

  const degraded = report?.status === 'degraded'
  const sections = (report?.sections || []).filter((s) => s.status !== 'missing')

  return (
    <section className="scanner">
      <header className="scanner__head">
        <div>
          <p className="kicker">{t(`brief.title.${slot}`)}</p>
          <h1>{report?.headline && !degraded ? report.headline : t(`brief.title.${slot}`)}</h1>
          <p className="muted brief-positioning">{t(`brief.positioning.${slot}`)}</p>
          {slot === 'midday' && nowInET().mins >= 16 * 60 && (
            <p className="muted small">{t('brief.middayAfterCloseHint')}</p>
          )}
          {report && (
            <p className="muted">
              {report.trade_date} · {t('brief.dataCutoff')} {formatHistoryDate(report.data_cutoff)}
              {slot === 'postclose' && report.trade_date && report.trade_date.slice(0, 10) !== nowInET().ymd && (
                <span className="brief-micro-label"> {t('brief.priorTradingDay')}</span>
              )}
            </p>
          )}
        </div>
      </header>

      <div className="tab-row" role="tablist" aria-label={t('brief.slotAria')}>
        {BRIEF_SLOTS.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={slot === s}
            className={slot === s ? 'tab is-active' : 'tab'}
            onClick={() => setSlot(s)}
          >
            {t(`brief.slot.${s}`)}
          </button>
        ))}
      </div>

      {loading && !report ? (
        <LoadingState variant="card" rows={5} label={t('brief.loading')} />
      ) : state === 'empty' ? (
        <div className="scanner-empty">
          <p>{t(`brief.empty.${slot}`)}</p>
          <div className="brief-empty-goto">
            <span className="muted small">{t('brief.emptyGoto')}</span>
            {BRIEF_SLOTS.filter((s) => s !== slot).map((s) => (
              <button key={s} type="button" className="button button--ghost button--small" onClick={() => setSlot(s)}>
                {t(`brief.slot.${s}`)}
              </button>
            ))}
          </div>
        </div>
      ) : state === 'error' ? (
        <p className="form-message form-message--error">{error}</p>
      ) : report ? (
        <>
          {degraded && (
            <div className="brief-degraded-bar">{t('brief.degraded')}</div>
          )}
          <BriefExecutiveLayer briefing={report.briefing} coverage={report.coverage} />
          <div className="brief-sections">
            {sections.map((s) => (
              <BriefSectionCard key={s.key} section={s} degraded={degraded} />
            ))}
          </div>
        </>
      ) : null}
    </section>
  )
}

function EarningsPage() {
  const [days, setDays] = useState(7)
  const [items, setItems] = useState<EarningsEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [expandedSymbols, setExpandedSymbols] = useState<Set<string>>(new Set())
  const [recaps, setRecaps] = useState<EarningsRecapItem[]>([])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    api<{ items: EarningsEntry[] }>(`/v1/earnings/upcoming?days=${days}&limit=200`)
      .then((res) => {
        if (cancelled) return
        setItems(res.items || [])
      })
      .catch((err: ApiError) => {
        if (cancelled) return
        setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [days])

  useEffect(() => {
    let cancelled = false
    api<EarningsWeekCardPageData>('/v1/earnings/week-card')
      .then((res) => { if (!cancelled) setRecaps(res?.recaps || []) })
      .catch(() => { if (!cancelled) setRecaps([]) })
    return () => { cancelled = true }
  }, [])

  const toggleExpand = (key: string) => {
    setExpandedSymbols((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <section className="scanner" data-i18n-skip="true">
      <header className="scanner__head">
        <div>
          <p className="kicker">EARNINGS CALENDAR</p>
          <h1>Earnings this week</h1>
          <p className="muted">
            Reports inside the next {days} days, sorted by date.
          </p>
        </div>
        <div className="scanner__head-actions">
          <label className="inline-select">
            Window
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </label>
        </div>
      </header>

      {error && <p className="form-message form-message--error">{error}</p>}

      {recaps.length > 0 && (
        <article className="analyze-card" style={{ marginBottom: 16 }}>
          <h3>Recent reports — your symbols (7d)</h3>
          <table className="scanner-table">
            <thead>
              <tr>
                <th className="text-left">Symbol</th>
                <th className="text-left">Date</th>
                <th>Actual EPS</th>
                <th>Est EPS</th>
                <th>Surprise</th>
                <th>Move</th>
                <th className="text-left" title="Implied volatility usually drops sharply right after earnings — this 'IV crush' is why options can lose value even when the direction was right.">IV crush (before → after)</th>
              </tr>
            </thead>
            <tbody>
              {recaps.map((r, i) => (
                <tr key={`${r.symbol}-${r.report_date}-${i}`}>
                  <td className="text-left"><SymbolLink symbol={r.symbol} /></td>
                  <td className="text-left">{formatEarningsDate(r.report_date)}</td>
                  <td>{formatEps(r.actual_eps)}</td>
                  <td>{formatEps(r.est_eps)}</td>
                  <td className={pctClass(r.surprise_pct)}>{r.surprise_pct != null ? formatSignedPct(r.surprise_pct) : '—'}</td>
                  <td className={r.realized_move_pct != null ? (r.realized_move_pct >= 0 ? 'up' : 'down') : ''}>
                    {r.realized_move_pct != null ? `${r.realized_move_pct >= 0 ? '+' : ''}${r.realized_move_pct.toFixed(1)}%` : '—'}
                  </td>
                  <td className="text-left">
                    {r.iv_before_pct != null && r.iv_after_pct != null
                      ? <>
                          <span>IV {r.iv_before_pct.toFixed(0)} → {r.iv_after_pct.toFixed(0)}</span>
                          {r.iv_crush_pct != null && <span className="muted"> ({r.iv_crush_pct >= 0 ? '+' : ''}{r.iv_crush_pct.toFixed(0)}%)</span>}
                        </>
                      : '—'}
                    {r.position_note && <div className="muted" style={{ fontSize: '0.8em' }}>{r.position_note}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      )}

      <div className="scanner-table-wrap">
        <table className="scanner-table">
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-left">Session</th>
              <th className="text-left">Symbol</th>
              <th>Est EPS</th>
              <th>Est Rev</th>
              <th>YoY EPS</th>
              <th>Implied Move</th>
              <th className="text-left">Setup</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={8} className="empty"><LoadingState variant="card" rows={4} label="Loading earnings..." /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="empty">No earnings reports in the next {days} days.</td></tr>
            ) : items.map((row) => {
              const rowKey = `${row.symbol}-${row.next_earnings_at}`
              const expanded = expandedSymbols.has(rowKey)
              const hasPastMoves = row.past_moves && row.past_moves.length > 0
              return (
                <React.Fragment key={rowKey}>
                  <tr>
                    <td className="text-left">{formatEarningsDate(row.next_earnings_at)}</td>
                    <td className="text-left"><span className="badge badge--muted">{row.session || '—'}</span></td>
                    <td className="text-left"><SymbolLink symbol={row.symbol} /></td>
                    <td>{formatEps(row.est_eps)}</td>
                    <td>{formatRevenue(row.est_revenue)}</td>
                    <td className={pctClass(row.yoy_eps_growth_pct)}>{formatSignedPct(row.yoy_eps_growth_pct)}</td>
                    <td>
                      {row.implied_move_pct != null
                        ? <>
                            <span>Implied ±{row.implied_move_pct.toFixed(1)}%</span>
                            {/* "typical" needs a real sample — suppress below 3 past reports */}
                            {row.avg_abs_realized_move_pct != null && (row.past_moves?.length ?? 0) >= 3 && (
                              <span className="muted" style={{ display: 'block', fontSize: '0.8em' }}>
                                typical move ±{row.avg_abs_realized_move_pct.toFixed(1)}%
                                {row.past_moves && row.past_moves.length > 0 && ` (avg of last ${row.past_moves.length} reports)`}
                                {hasPastMoves && (
                                  <button
                                    type="button"
                                    className="link-like"
                                    style={{ marginLeft: 4, fontSize: 'inherit' }}
                                    onClick={() => toggleExpand(rowKey)}
                                    title="Past quarterly moves — historical data only, not a prediction"
                                  >
                                    {expanded ? '▲' : '▼'}
                                  </button>
                                )}
                              </span>
                            )}
                          </>
                        : '—'}
                    </td>
                    <td className="text-left">
                      <span className={setupPillClass(row.setup_level)}>{row.setup_level || '—'}</span>
                      {(() => {
                        // Date + Session columns already say when the report lands; keep only the differentiating part.
                        const reason = (row.setup_reason || '').replace(/^earnings\s.*?(?:;|\.(?=\s|$))\s*/i, '')
                        return reason ? <span className="muted setup-reason"> · {reason}</span> : null
                      })()}
                    </td>
                  </tr>
                  {expanded && hasPastMoves && (
                    <tr>
                      <td colSpan={8} style={{ padding: '4px 12px 8px' }}>
                        <table className="scanner-table" style={{ fontSize: '0.85em' }}>
                          <thead>
                            <tr>
                              <th className="text-left">Report date</th>
                              <th>Realized move</th>
                              <th>EPS surprise</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(row.past_moves || []).map((m, mi) => (
                              <tr key={`${m.report_date}-${mi}`}>
                                <td className="text-left muted">{formatEarningsDate(m.report_date)}</td>
                                <td className={m.realized_move_pct >= 0 ? 'up' : 'down'}>
                                  {m.realized_move_pct >= 0 ? '+' : ''}{m.realized_move_pct.toFixed(1)}%
                                </td>
                                <td>{m.surprise_pct != null ? formatSignedPct(m.surprise_pct) : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <p className="muted" style={{ fontSize: '0.78em', marginTop: 4 }}>Past data only — not a prediction of future moves.</p>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

type UOAAlert = {
  trade_date: string
  detected_at: string
  symbol: string
  strike: number
  expiration: string
  dte?: number
  side: string
  today_volume: number
  baseline_avg_30d: number
  baseline_avg_7d: number
  multiple: number
  description: string
  tv_recommend_all?: number
  notional: number
  spot?: number
  gap_pct?: number | null
  moneyness?: string
  watchlisted?: boolean
  has_position?: boolean
  oi_confirmed?: boolean | null
}

type UOAResponse = {
  alerts: UOAAlert[]
  tier_flags?: {
    smart_flow: boolean
    contrarian: boolean
    lottery: boolean
    bearish_hedge?: boolean
  }
  as_of?: string | null
  stale?: boolean
}

type UOADailyPoint = {
  trade_date: string
  volume: number
  open_interest: number
  avg_premium: number
  notional: number
}

type UOAContractSnapshot = {
  collected_at: string
  option_symbol: string
  underlying_price: number
  bid: number
  ask: number
  premium: number
  volume: number
  open_interest: number
  dte: number
  delta: number
  iv: number
  gap_pct: number
  apy: number
}

type UOADetailResponse = {
  alert: UOAAlert
  daily_history: UOADailyPoint[]
  snapshots: UOAContractSnapshot[]
  related: UOAAlert[]
}

// Compact USD notional: $42.5M / $3.2M / $850K / $42K. Matches the
// formatting the iOS push body uses so on-screen numbers line up with
// the notification copy.
function formatNotional(usd: number): string {
  if (usd >= 1_000_000) return `$${(usd / 1_000_000).toFixed(1)}M`
  if (usd >= 10_000) return `$${Math.round(usd / 1_000)}K`
  return `$${Math.round(usd).toLocaleString()}`
}

function formatVolume(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`
  return n.toLocaleString()
}

function formatExpiration(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function computeDTE(expiration: string): number | null {
  const d = new Date(expiration)
  if (Number.isNaN(d.getTime())) return null
  const diff = d.getTime() - Date.now()
  return Math.max(0, Math.round(diff / 86400000))
}

function formatUOADTE(row: UOAAlert): string {
  if (typeof row.dte === 'number') {
    return row.dte < 0 ? 'EXP' : `${row.dte}d`
  }
  const d = computeDTE(row.expiration)
  return d == null ? '-' : `${d}d`
}

function formatGapPct(row: UOAAlert): string {
  if (row.gap_pct == null) return ''
  const pct = row.gap_pct * 100
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`
}

function formatUOASpot(row: UOAAlert): string {
  if (row.strike === 0 || row.spot == null || row.spot <= 0) return '—'
  return money(row.spot)
}

function uoaDateParam(value: string): string {
  const d = new Date(value)
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
  return value.slice(0, 10)
}

function uoaDetailPath(row: UOAAlert): string {
  const params = new URLSearchParams({
    symbol: row.symbol,
    strike: String(row.strike),
    expiration: uoaDateParam(row.expiration),
    side: row.side.toLowerCase(),
    trade_date: uoaDateParam(row.trade_date),
  })
  return `/v1/uoa/detail?${params.toString()}`
}

function formatUOADate(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatUOATime(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

type UOASignalTag = { label: string; title: string }

function uoaSignalTags(row: UOAAlert): UOASignalTag[] {
  const parts: UOASignalTag[] = []
  const moneyness = [row.moneyness, formatGapPct(row)].filter(Boolean).join(' ')
  if (moneyness) {
    parts.push({
      label: `Strike: ${moneyness}`,
      title: 'Strike location versus the current underlying price. OTM is out-of-the-money; ITM is in-the-money.',
    })
  }
  if (row.notional > 0) {
    parts.push({
      label: `Notional: ${formatNotional(row.notional)}`,
      title: "The session's total dollar volume on this contract — the whole day added up, not one large print. The feed cannot tell one large trade from many small ones.",
    })
  }
  return parts.slice(0, 3)
}

// This feed describes what traded; it does not rate it. Eleven pre-registered
// tests found no forward directional edge in unusual options volume, so there
// is deliberately no strength badge, quality tier, or historical hit rate here.
function UOASignalCell({ row }: { row: UOAAlert }) {
  const tags = uoaSignalTags(row)
  return (
    <span className="uoa-signal uoa-signal--muted">
      <small className="uoa-signal__reason">{row.description}</small>
      {tags.length > 0 && (
        <small className="uoa-signal__meta">
          {tags.map((part) => <span key={part.label} title={part.title}>{part.label}</span>)}
        </small>
      )}
    </span>
  )
}

// A factual descriptor of the contract, not a read on intent. The tier-based
// variants this replaced ("Cleaner follow-through", "Chasing risk") asserted
// forward behaviour that eleven pre-registered tests did not find.
function uoaFlowRead(row: UOAAlert): { label: string; detail: string; cls: string } {
  const dte = typeof row.dte === 'number' ? row.dte : computeDTE(row.expiration)
  const gap = row.gap_pct == null ? null : Math.abs(row.gap_pct)

  if (dte != null && dte <= 1) {
    return { label: 'Expires within 1d', detail: 'The contract expires today or tomorrow.', cls: 'uoa-read' }
  }
  if (dte != null && dte >= 180) {
    return { label: 'Long-dated', detail: 'LEAPS-style tenor: 180+ days to expiry.', cls: 'uoa-read' }
  }
  if (gap != null && gap >= 0.2) {
    return { label: 'Far from spot', detail: 'The strike sits 20%+ away from the underlying price.', cls: 'uoa-read' }
  }
  if (row.side.toLowerCase() === 'put') {
    return { label: 'Put side', detail: 'Put volume. The feed cannot tell a hedge from a directional position.', cls: 'uoa-read' }
  }
  return { label: 'Call side', detail: 'Call volume. The feed cannot tell an overwrite from a directional position.', cls: 'uoa-read' }
}

function UOADetailPanel({
  row,
  detail,
  loading,
  error,
  onClose,
}: {
  row: UOAAlert | null
  detail: UOADetailResponse | null
  loading: boolean
  error: React.ReactNode
  onClose: () => void
}) {
  if (!row) return null
  const active = detail?.alert || row
  const snapshots = detail?.snapshots || []
  const history = detail?.daily_history || []
  const related = detail?.related || []
  return (
    <section className="uoa-detail-panel" aria-label="UOA flow detail">
      <header className="uoa-detail-panel__head">
        <div>
          <p className="kicker">FLOW DETAIL</p>
          <h2>{active.symbol} {active.side.toUpperCase()} ${active.strike.toFixed(2)} · {formatExpiration(active.expiration)}</h2>
        </div>
        <button type="button" onClick={onClose}>Close</button>
      </header>
      {loading ? (
        <LoadingState variant="card" rows={4} label="Loading flow detail..." />
      ) : error ? (
        <p className="form-message form-message--error">{error}</p>
      ) : (
        <>
          <div className="uoa-detail-stats">
            <span><b>{active.multiple.toFixed(1)}x</b><small>vs baseline</small></span>
            <span><b>{formatVolume(active.today_volume)}</b><small>contracts</small></span>
            <span><b>{active.notional > 0 ? formatNotional(active.notional) : '—'}</b><small>notional</small></span>
            <span><b>{active.moneyness || '—'}</b><small>{formatGapPct(active) || 'moneyness'}</small></span>
          </div>
          <div className="uoa-detail-grid">
            <section>
              <h3>Same-contract history</h3>
              <table>
                <thead><tr><th>Date</th><th>Vol</th><th>OI</th><th>Premium</th><th>Notional</th></tr></thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr><td colSpan={5}>No daily history for this exact contract.</td></tr>
                  ) : history.map((p) => (
                    <tr key={p.trade_date}>
                      <td>{formatUOADate(p.trade_date)}</td>
                      <td>{formatVolume(p.volume)}</td>
                      <td>{formatVolume(p.open_interest)}</td>
                      <td>{p.avg_premium > 0 ? money(p.avg_premium) : '—'}</td>
                      <td>{p.notional > 0 ? formatNotional(p.notional) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <section>
              <h3>Intraday snapshots</h3>
              <table>
                <thead><tr><th>Time</th><th>Underlying</th><th>Bid/Ask</th><th>Mid</th><th>Vol/OI</th><th>Delta</th><th>IV</th></tr></thead>
                <tbody>
                  {snapshots.length === 0 ? (
                    <tr><td colSpan={7}>No archived intraday snapshots for this alert date.</td></tr>
                  ) : snapshots.slice(-8).map((p) => (
                    <tr key={`${p.collected_at}-${p.volume}-${p.premium}`}>
                      <td>{formatUOATime(p.collected_at)}</td>
                      <td>{money(p.underlying_price)}</td>
                      <td>{money(p.bid)} / {money(p.ask)}</td>
                      <td>{money(p.premium)}</td>
                      <td>{formatVolume(p.volume)} / {formatVolume(p.open_interest)}</td>
                      <td>{p.delta.toFixed(2)}</td>
                      <td>{p.iv.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </div>
          {related.length > 0 && (
            <section className="uoa-related">
              <h3>Related flow on {active.symbol}</h3>
              <div>
                {related.slice(0, 8).map((r) => (
                  <span key={`${r.trade_date}-${r.side}-${r.strike}-${r.expiration}`}>
                    {formatUOADate(r.trade_date)} · {r.side.toUpperCase()} ${r.strike.toFixed(0)} · {r.multiple.toFixed(1)}x
                  </span>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </section>
  )
}

function UOAPage({ proModeEnabled, signedIn = true }: { proModeEnabled: boolean; signedIn?: boolean }) {
  const anonymous = !signedIn
  const [windowDays, setWindowDays] = useState(1)
  const [mineOnly, setMineOnly] = useState(false)
  const [alerts, setAlerts] = useState<UOAAlert[]>([])
  const [asOf, setAsOf] = useState<string | null>(null)
  const [stale, setStale] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedDetailRow, setSelectedDetailRow] = useState<UOAAlert | null>(null)
  const [detail, setDetail] = useState<UOADetailResponse | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<React.ReactNode>('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    api<UOAResponse>(`/v1/uoa/today?window_days=${windowDays}&limit=100`)
      .then((res) => {
        if (cancelled) return
        setAlerts(res.alerts || [])
        setAsOf(res.as_of || null)
        setStale(Boolean(res.stale))
      })
      .catch((err: ApiError) => {
        if (cancelled) return
        if (err.status === 401) {
          setError('AUTH_REQUIRED')
        } else {
          setError(err.message)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [windowDays])

  const openDetail = useCallback(async (row: UOAAlert) => {
    setSelectedDetailRow(row)
    setDetail(null)
    if (row.strike === 0) {
      // Item 2: anonymous → sign-in prompt; signed-in Free → Pro upgrade copy
      if (anonymous) {
        setDetailError(<>Sign in to see this contract. <Link to="/login?next=uoa">Sign in</Link></>)
      } else {
        setDetailError(<>Pro unlocks strike prices. <Link to="/pricing">Upgrade to Pro</Link></>)
      }
      return
    }
    setDetailLoading(true)
    setDetailError('')
    try {
      const res = await api<UOADetailResponse>(uoaDetailPath(row))
      setDetail(res)
    } catch (err) {
      const apiErr = err as ApiError
      if (apiErr.status === 402) {
        setDetailError('Flow detail requires Pro access.')
      } else if (apiErr.status === 404) {
        setDetailError('This alert is no longer available in the detail archive.')
      } else {
        setDetailError(apiErr.message)
      }
    } finally {
      setDetailLoading(false)
    }
  }, [])

  const closeDetail = useCallback(() => {
    setSelectedDetailRow(null)
    setDetail(null)
    setDetailError('')
    setDetailLoading(false)
  }, [])

  // The "mine" chip narrows to watchlist/position symbols without ever
  // changing the market-magnitude sort. Nothing else is filtered: the feed
  // shows what traded, and does not rank rows by an implied conviction.
  const visibleAlerts = useMemo(() => {
    if (anonymous || !mineOnly) return alerts
    return alerts.filter((a) => a.watchlisted || a.has_position)
  }, [alerts, anonymous, mineOnly])
  const mineCount = alerts.filter((a) => a.watchlisted || a.has_position).length
  const anyRedacted = visibleAlerts.some((a) => a.strike === 0)

  return (
    <section className="scanner">
      <header className="scanner__head">
        <div>
          <p className="kicker">UNUSUAL OPTIONS ACTIVITY</p>
          <h1>Today's UOA</h1>
          <p className="muted">
            Today's contracts traded vs each strike's typical 7-day baseline. Bigger multiple = more unusual flow.
          </p>
        </div>
        <div className="scanner__head-actions">
          <label className="inline-select">
            Window
            <select value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))} disabled={anonymous}>
              <option value={1}>Today only</option>
              <option value={3}>Last 3 days</option>
              <option value={7}>Last 7 days</option>
            </select>
          </label>
        </div>
      </header>

      {/* No tier chips: the tiers they filtered were derived from a backtest
          whose signal eleven pre-registered tests then rejected. Only the
          personal-symbol narrowing remains, and it never changes the sort. */}
      {!anonymous && mineCount > 0 && (
        <div className="uoa-quality-chips">
          <button
            className={`pill ${mineOnly ? 'pill--blue' : ''}`}
            onClick={() => setMineOnly((v) => !v)}
            aria-pressed={mineOnly}
            title="Only flow on symbols from your watchlist or open positions"
          >
            ★ My symbols<em> · {mineCount}</em>
          </button>
        </div>
      )}
      {stale && asOf && (
        <p className="form-message form-message--warn">
          Latest detection: {formatExpiration(asOf)} · updates after each market close.
        </p>
      )}
      {anyRedacted && proModeEnabled && (
        <p className="form-message">
          Strikes are hidden until you sign in. <Link to="/login?next=uoa">Sign in</Link> to see the full contract.
        </p>
      )}
      {error === 'AUTH_REQUIRED' ? (
        <div className="form-message form-message--warn">
          Sign in to see today's unusual options activity. <Link to="/login?next=uoa">Sign in</Link>
        </div>
      ) : error ? (
        <p className="form-message form-message--error">{error}</p>
      ) : null}

      <div className="scanner-table-wrap">
        <table className="scanner-table scanner-table--uoa">
          <thead>
            <tr>
              <th className="text-left">Symbol</th>
              <th className="text-left">Side</th>
              <th>Current</th>
              <th>Strike</th>
              <th className="text-left">Expiration</th>
              <th><GlossaryTerm term="dte">DTE</GlossaryTerm></th>
              <th><GlossaryTerm term="volume">Today Vol</GlossaryTerm></th>
              <th><GlossaryTerm term="notional">Notional</GlossaryTerm></th>
              <th title="Average daily option volume over the last 7 days.">7d Avg</th>
              <th><GlossaryTerm term="multiple">Multiple</GlossaryTerm></th>
              <th className="text-left"><GlossaryTerm term="uoa-priority">Priority</GlossaryTerm></th>
              <th className="text-left"><GlossaryTerm term="flow-read">Flow read</GlossaryTerm></th>
              <th className="text-left"><GlossaryTerm term="tv">Tech</GlossaryTerm></th>
              <th className="text-left">Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading && alerts.length === 0 ? (
              <tr><td colSpan={14} className="empty"><LoadingState variant="table" rows={6} label="Loading unusual options..." /></td></tr>
            ) : error === 'AUTH_REQUIRED' ? (
              <tr><td colSpan={14} className="empty">Sign in to load alerts.</td></tr>
            ) : visibleAlerts.length === 0 ? (
              <tr><td colSpan={14} className="empty">
                {alerts.length > 0
                  ? 'No alerts on your watchlist or open positions in this window.'
                  : 'No UOA alerts in the selected window. The detector compares per-strike volume against a 7-day baseline; results appear after each RTH close.'}
              </td></tr>
            ) : visibleAlerts.map((row, idx) => (
              <tr key={`${row.symbol}-${row.expiration}-${row.strike}-${row.side}-${row.detected_at}-${row.today_volume}-${idx}`}
                  className={[
                    (row.watchlisted || row.has_position) ? 'uoa-row-mine' : '',
                  ].filter(Boolean).join(' ') || undefined}>
                <td className="text-left">
                  {row.has_position &&
                    <span title="You have an open position on this symbol" style={{ marginRight: 4 }}>📌</span>}
                  {row.watchlisted && !row.has_position &&
                    <span title="On your watchlist" style={{ marginRight: 4 }}>★</span>}
	                  <SymbolLink symbol={row.symbol} />
	                  {row.oi_confirmed === true && (
	                    <span className="oi-confirmed-badge" title="Open interest rose next session — consistent with opening flow, not closing" style={{ marginLeft: 4 }}>OI↑</span>
	                  )}
	                </td>
	                <td className="text-left">
	                  <span className={`pill pill--${row.side.toLowerCase() === 'call' ? 'green' : 'rose'}`}>
	                    {row.side.toUpperCase()}
	                  </span>
	                </td>
	                <td className="mono">{formatUOASpot(row)}</td>
	                <td>{row.strike === 0 ? <span className="locked">Locked</span> : `$${row.strike.toFixed(2)}`}</td>
	                <td className="text-left">{formatExpiration(row.expiration)}</td>
                <td>{formatUOADTE(row)}</td>
                <td>{formatVolume(row.today_volume)}</td>
                <td>{row.notional > 0 ? formatNotional(row.notional) : '—'}</td>
                <td>{formatVolume(Math.round(row.baseline_avg_7d))}</td>
                <td><strong>{row.multiple.toFixed(1)}×</strong></td>
                <td className="text-left">
                  <UOASignalCell row={row} />
                </td>
                <td className="text-left">
                  {(() => {
                    const read = uoaFlowRead(row)
                    return (
                      <span className={read.cls} title={read.detail}>
                        <strong>{read.label}</strong>
                        <small>{read.detail}</small>
                      </span>
                    )
                  })()}
                </td>
                <td className="text-left">
                  {(() => {
                    const v = tvVerdict(row.tv_recommend_all)
                    return v ? <span className={v.cls} title={v.title}>{v.label}</span> : <span className="muted">—</span>
                  })()}
                </td>
                <td className="text-left">
                  <button
                    type="button"
                    className="uoa-detail-button"
                    disabled={row.strike === 0}
                    onClick={() => void openDetail(row)}
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UOADetailPanel
        row={selectedDetailRow}
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onClose={closeDetail}
      />
    </section>
  )
}

type NewsItem = {
  id: string
  title: string
  summary?: string
  url?: string
  source: string
  source_key?: string
  category?: string
  relevance?: string
  why?: string
  published_at?: string
  symbols?: string[]
  priority: number
}

type NewsResponse = {
  items: NewsItem[]
  as_of?: string
  stale?: boolean
  context_symbols?: string[]
  empty_reason?: string
}

function relevancePillClass(rel?: string): string {
  if (!rel) return 'pill'
  const r = rel.toLowerCase()
  if (r === 'high' || r === 'critical') return 'pill pill--rose'
  if (r === 'medium') return 'pill pill--amber'
  return 'pill pill--green'
}

function formatNewsTime(value?: string): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const now = Date.now()
  const diff = (now - d.getTime()) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function NewsPage({ signedIn }: { signedIn: boolean }) {
  const [items, setItems] = useState<NewsItem[]>([])
  const [stale, setStale] = useState(false)
  const [asOf, setAsOf] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!signedIn) return
    let cancelled = false
    setLoading(true)
    setError('')
    api<NewsResponse>('/v1/news/today')
      .then((res) => {
        if (cancelled) return
        setItems((res.items || []).slice().sort((a, b) => b.priority - a.priority))
        setStale(Boolean(res.stale))
        setAsOf(res.as_of || null)
      })
      .catch((err: ApiError) => { if (!cancelled) setError(err.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [signedIn])

  if (!signedIn) {
    return (
      <section className="scanner">
        <header className="scanner__head">
          <div>
            <p className="kicker">NEWS</p>
            <h1>Sign in for today's market news</h1>
            <p className="muted">Curated catalysts and headlines from the same feed iOS uses.</p>
          </div>
          <div className="scanner__head-actions">
            <Link className="button button--primary" to="/login?next=news">Sign in</Link>
          </div>
        </header>
      </section>
    )
  }

  return (
    <section className="scanner">
      <header className="scanner__head">
        <div>
          <p className="kicker">NEWS · CATALYSTS</p>
          <h1>Today's market news</h1>
          <p className="muted">
            Sorted by relevance. {asOf && <>Last refresh: {formatNewsTime(asOf)}.</>}
            {stale && <span className="muted"> (cache stale — origin may be lagging)</span>}
          </p>
        </div>
      </header>

      {error && <p className="form-message form-message--error">{error}</p>}

      {loading && items.length === 0 ? (
        <LoadingState variant="list" rows={5} label="Loading news..." />
      ) : items.length === 0 ? (
        <p className="muted">No news items right now.</p>
      ) : (
        <ul className="news-list">
          {items.map((item) => (
            <li key={item.id} className="news-card">
              <header>
                <span className={relevancePillClass(item.relevance)}>{(item.relevance || 'NORMAL').toUpperCase()}</span>
                <span className="muted news-source">{item.source}</span>
                {item.category && <span className="badge badge--muted">{item.category}</span>}
                <span className="muted news-time">{formatNewsTime(item.published_at)}</span>
              </header>
              <h3>
                {item.url ? (
                  <a href={item.url} target="_blank" rel="noreferrer">{decodeEntities(item.title)}</a>
                ) : decodeEntities(item.title)}
              </h3>
              {item.summary && <p className="news-summary">{decodeEntities(item.summary)}</p>}
              {item.why && <p className="news-why"><strong>Why it matters:</strong> {decodeEntities(item.why)}</p>}
              {item.symbols && item.symbols.length > 0 && (
                <p className="news-symbols">
                  {item.symbols.map((sym) => (
                    <SymbolLink key={sym} symbol={sym} className="badge">{sym}</SymbolLink>
                  ))}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

type Position = {
  id: string
  symbol: string
  side: string
  strike: number
  expiry: string
  premium: number
  qty: number
  opened_at: string
  status: string
  notes?: string
  thesis?: string | null
  dte: number
  underlying_price: number
  is_stale: boolean
  gap_pct: number
  rolling_pnl_estimate: number
  assignment_risk: string
}

// GET /v1/holdings/summary — Portfolio's money bar. Every figure the server
// cannot measure arrives null and is simply not drawn: a null is never a zero
// here (handlers/holdings_summary.go says so on the wire).
type HoldingsSummaryStocks = {
  value: number | null
  unrealized: number | null
  unrealized_n: number
  total_n: number
  last_session_pct: number | null
  last_session_usd: number | null
  valued_n: number
}
type HoldingsSummaryOptions = {
  idle_cash: number | null
  held_for_puts: number
  pending_assignment: number
  capital_at_risk: number | null
}
type HoldingsSummaryAccount = {
  net: number | null
  margin: number
  maint_req: number | null
  excess_liquidity: number | null
}
type HoldingsSummary = {
  stocks: HoldingsSummaryStocks
  // Null when the account holds no option position, and null until an account
  // snapshot is saved — each absent segment is simply not rendered.
  options?: HoldingsSummaryOptions | null
  account?: HoldingsSummaryAccount | null
}

// One horizontal row of three segments, matching iOS PortfolioMoneyBar:
// stocks / option cash / account snapshot.
function MoneyBar({ summary }: { summary: HoldingsSummary }) {
  const { t } = useI18n()
  const s = summary.stocks
  const usd = (v: number) => (v < 0 ? '-' : '') + formatNotional(Math.abs(v))
  const signed = (v: number) => (v < 0 ? '-' : '+') + formatNotional(Math.abs(v))
  const cell = (label: string, value: string, sub?: string, tone?: string) => (
    <span key={label} className={`money-bar__cell ${tone || ''}`}>
      <small>{label}</small>
      <strong>{value}</strong>
      {sub && <em>{sub}</em>}
    </span>
  )
  return (
    <div className="money-bar">
      {s.value != null && cell(t('money.stocks'), usd(s.value),
        s.valued_n < s.total_n
          ? t('money.withQuote').replace('{n}', String(s.valued_n)).replace('{total}', String(s.total_n))
          : t('money.positions').replace('{n}', String(s.total_n)))}
      {s.unrealized != null && cell(t('money.unrealized'), signed(s.unrealized),
        t('money.withCost').replace('{n}', String(s.unrealized_n)).replace('{total}', String(s.total_n)),
        s.unrealized >= 0 ? 'is-up' : 'is-down')}
      {s.last_session_pct != null && cell(t('money.lastSession'), `${s.last_session_pct.toFixed(1)}%`,
        s.last_session_usd != null ? signed(s.last_session_usd) : undefined,
        s.last_session_pct >= 0 ? 'is-up' : 'is-down')}
      {summary.options?.idle_cash != null && cell(t('money.idleCash'), usd(summary.options.idle_cash))}
      {summary.options && cell(t('money.heldForPuts'), usd(summary.options.held_for_puts))}
      {summary.options && cell(t('money.pendingAssignment'), usd(summary.options.pending_assignment), t('money.notInAnyTotal'))}
      {summary.options?.capital_at_risk != null && cell(t('money.capitalAtRisk'), usd(summary.options.capital_at_risk))}
      {summary.account?.net != null && cell(t('money.netAccount'), usd(summary.account.net))}
      {summary.account && cell(t('money.margin'), usd(summary.account.margin))}
      {summary.account?.maint_req != null && cell(t('money.maintReq'), usd(summary.account.maint_req))}
      {summary.account?.excess_liquidity != null && cell(t('money.excessLiquidity'), usd(summary.account.excess_liquidity))}
    </div>
  )
}

// Item 8: portfolio summary card type
type PositionsSummary = {
  as_of?: string
  open_count?: number
  total_collateral_usd?: number
  total_open_premium_usd?: number
  net_delta_shares?: number
  itm_count?: number
  roll_window_count?: number
  sectors?: { sector: string; collateral_usd: number; pct: number }[]
  warnings?: { kind: string; sector: string; pct: number }[]
}

// Backend enum: CSP = cash-secured put, CC = covered call, LC = long call.
// LC is the only long leg, so it gets its own colour rather than borrowing the
// short-call green.
function positionSidePill(side: string): string {
  if (side === 'CSP') return 'rose'
  if (side === 'CC') return 'green'
  if (side === 'LC') return 'blue'
  return 'mute'
}

function todayPlusDays(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

type RollCandidate = {
  strike: number
  expiry: string
  premium: number
  dte: number
  delta: number
  apy: number
  score?: number
  grade?: string
  roll_direction: string
  reason: string
  tradeoffs?: string[]
}

type RollCandidatesResp = {
  candidates: RollCandidate[]
  empty_reason?: string
  empty_message?: string
}

function PositionsPage({ signedIn, isPro, proModeEnabled = true, calendarEnabled = false }: {
  signedIn: boolean; isPro: boolean; proModeEnabled?: boolean; calendarEnabled?: boolean
}) {
  const [items, setItems] = useState<Position[]>([])
  const [freeCap, setFreeCap] = useState<number>(-1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [rollOpen, setRollOpen] = useState<Position | null>(null)
  const [rollData, setRollData] = useState<RollCandidatesResp | null>(null)
  const [rollLoading, setRollLoading] = useState(false)
  const [rollError, setRollError] = useState('')
  const [summary, setSummary] = useState<PositionsSummary | null>(null)
  const [moneyBar, setMoneyBar] = useState<HoldingsSummary | null>(null)
  const [assignPositionId, setAssignPositionId] = useState<string | null>(null)
  const liveQuotes = useLiveQuotes()
  const { t } = useI18n()
  const [form, setForm] = useState({
    symbol: '',
    // Backend enum values: CSP = Cash-Secured Put, CC = Covered Call, LC = Long Call.
    side: 'CSP',
    strike: '',
    expiry: todayPlusDays(30),
    premium: '',
    qty: '1',
    notes: '',
    thesis: '',
  })
  const [adding, setAdding] = useState(false)

  const buildThesisTemplate = (f: typeof form): string => {
    const sym = f.symbol.toUpperCase()
    const side = f.side === 'CSP' ? 'put' : 'call'
    const strike = f.strike ? `$${f.strike}` : ''
    const premium = f.premium ? `$${f.premium}` : ''
    if (!sym || !strike || !premium) return ''
    if (f.side === 'CSP') return `Sold ${strike} ${side} on ${sym} for ${premium} — comfortable owning at this basis`
    if (f.side === 'LC') return `Bought ${strike} ${side} on ${sym} for ${premium} — thesis is ${sym} above this level before expiry`
    return `Sold ${strike} covered ${side} on ${sym} for ${premium} — willing to sell shares at this level`
  }

  const calendarEvents = useCalendarEvents(30, calendarEnabled)
  const eventsBySymbol = useMemo(() => {
    const m: Record<string, CalendarEvent> = {}
    for (const e of calendarEvents) {
      if (!e.symbol) continue
      if (!m[e.symbol]) m[e.symbol] = e
    }
    return m
  }, [calendarEvents])

  const load = () => {
    if (!signedIn) return
    setLoading(true)
    setError('')
    api<{ positions: Position[]; count: number; free_cap: number }>('/v1/positions')
      .then((res) => { setItems(res.positions || []); setFreeCap(res.free_cap ?? -1) })
      .catch((err: ApiError) => setError(err.message))
      .finally(() => setLoading(false))
    // Item 8: portfolio summary card — hide on any error (backend may not be deployed)
    api<HoldingsSummary>('/v1/holdings/summary')
      .then((res) => setMoneyBar(res))
      .catch(() => setMoneyBar(null))
    api<PositionsSummary>('/v1/positions/summary')
      .then((res) => setSummary(res))
      .catch(() => setSummary(null))
  }

  useEffect(() => { load() }, [signedIn])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setAdding(true)
    setError('')
    try {
      await api('/v1/positions', {
        method: 'POST',
        body: JSON.stringify({
          symbol: form.symbol.toUpperCase(),
          side: form.side,
          strike: Number(form.strike),
          expiry: form.expiry,
          premium: Number(form.premium),
          qty: Number(form.qty),
          notes: form.notes,
          ...(form.thesis.trim() ? { thesis: form.thesis.trim() } : {}),
        }),
      })
      setShowAdd(false)
      setForm({ symbol: '', side: 'CSP', strike: '', expiry: todayPlusDays(30), premium: '', qty: '1', notes: '', thesis: '' })
      load()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setAdding(false)
    }
  }

  const remove = async (id: string) => {
    if (!confirm('Remove this position?')) return
    try {
      await api(`/v1/positions/${id}`, { method: 'DELETE' })
      setItems((prev) => prev.filter((p) => p.id !== id))
    } catch (err) {
      setError((err as Error).message)
    }
  }

  // Marking a leg assigned is the moment the user stops holding an option and
  // starts holding shares — offer to record the shares instead of inventing them.
  const markAssigned = async (p: Position) => {
    // status=assigned on a long call means the user exercised it, so the copy
    // changes even though the stored status does not.
    const ask = p.side === 'LC'
      ? t('positions.confirmExercised').replace('{sym}', p.symbol)
      : t('positions.confirmAssigned').replace('{sym}', p.symbol).replace('{side}', p.side)
    if (!confirm(ask)) return
    try {
      await api(`/v1/positions/${p.id}`, { method: 'PATCH', body: JSON.stringify({ status: 'assigned' }) })
      setAssignPositionId(p.id)
      load()
    } catch (err) {
      setError((err as Error).message)
    }
  }

  const openRoll = async (p: Position) => {
    setRollOpen(p)
    setRollData(null)
    setRollError('')
    setRollLoading(true)
    try {
      const data = await api<RollCandidatesResp>(`/v1/positions/${p.id}/roll_candidates`)
      setRollData(data)
    } catch (err) {
      setRollError((err as Error).message)
    } finally {
      setRollLoading(false)
    }
  }

  if (!signedIn) {
    return (
      <section className="scanner">
        <header className="scanner__head">
          <div>
            <p className="kicker">POSITIONS</p>
            <h1>Sign in to track positions</h1>
            <p className="muted">Open positions sync across web and iOS. PnL estimates update from live quotes.</p>
          </div>
          <div className="scanner__head-actions">
            <Link className="button button--primary" to="/login?next=positions">Sign in</Link>
          </div>
        </header>
      </section>
    )
  }

  return (
    <section className="scanner">
      <header className="scanner__head">
        <div>
          <p className="kicker">POSITIONS</p>
          <h1>Open positions</h1>
          <p className="muted">
            DTE and gap % update from live quotes. Synced with iOS.{' '}
            {!proModeEnabled
              ? `${items.length} ${items.length === 1 ? 'position' : 'positions'} open.`
              : isPro || freeCap < 0
                ? `${items.length} positions · Pro tier (no cap).`
              : `${items.length} / ${freeCap} free-tier slots used. ${items.length >= freeCap ? 'At cap — close one or upgrade to Pro to add more.' : ''}`}
          </p>
        </div>
        <div className="scanner__head-actions">
          {proModeEnabled && !isPro && freeCap >= 0 && items.length >= freeCap && (
            <Link className="button button--primary" to="/pricing">Upgrade to Pro</Link>
          )}
          <button type="button" className="button button--primary" onClick={() => setShowAdd((v) => !v)} disabled={proModeEnabled && !isPro && freeCap >= 0 && items.length >= freeCap}>
            {showAdd ? 'Cancel' : '+ Add position'}
          </button>
        </div>
      </header>

      {moneyBar && <MoneyBar summary={moneyBar} />}

      {showAdd && (
        <form className="position-form" onSubmit={submit}>
          <label>Symbol<input required value={form.symbol} onChange={(e) => { const next = { ...form, symbol: e.target.value }; next.thesis = next.thesis || buildThesisTemplate(next); setForm(next) }} placeholder="AAPL" /></label>
          <label>Side<select value={form.side} onChange={(e) => { const next = { ...form, side: e.target.value }; next.thesis = next.thesis || buildThesisTemplate(next); setForm(next) }}><option value="CSP">{t('positions.side.csp')}</option><option value="CC">{t('positions.side.cc')}</option><option value="LC">{t('positions.side.lc')}</option></select>{form.side === 'LC' && <span className="muted" style={{ fontSize: '0.8em' }}>{t('positions.side.lcNote')}</span>}</label>
          <label>Strike<input required type="number" step="0.5" value={form.strike} onChange={(e) => { const next = { ...form, strike: e.target.value }; next.thesis = next.thesis || buildThesisTemplate(next); setForm(next) }} placeholder="200" /></label>
          <label>Expiry<input required type="date" value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} /></label>
          <label>Premium<input required type="number" step="0.01" value={form.premium} onChange={(e) => { const next = { ...form, premium: e.target.value }; next.thesis = next.thesis || buildThesisTemplate(next); setForm(next) }} placeholder="2.50" /></label>
          <label>Qty<input required type="number" min="1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} /></label>
          <label className="full-row">Notes<input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" /></label>
          <label className="full-row">
            Thesis (why this trade)
            <input
              value={form.thesis}
              onChange={(e) => setForm({ ...form, thesis: e.target.value.slice(0, 280) })}
              placeholder="Optional — e.g. comfortable owning at this price"
              maxLength={280}
            />
            <span className="muted" style={{ fontSize: '0.8em' }}>{form.thesis.length}/280</span>
          </label>
          <button type="submit" className="button button--primary" disabled={adding}>{adding ? 'Saving...' : 'Save'}</button>
        </form>
      )}

      {error && <p className="form-message form-message--error">{error}</p>}

      {/* Item 8: portfolio summary card */}
      {summary && (
        <div className="positions-summary-card">
          <div className="positions-summary-card__stats">
            {summary.open_count != null && <span><strong>{summary.open_count}</strong><small>open</small></span>}
            {summary.total_collateral_usd != null && <span><strong>${(summary.total_collateral_usd / 1000).toFixed(1)}K</strong><small>capital at risk</small></span>}
            {summary.total_open_premium_usd != null && <span><strong>${summary.total_open_premium_usd.toFixed(0)}</strong><small>premium collected</small></span>}
            {summary.net_delta_shares != null && (
              <span title="Share-equivalent directional exposure of your short options. +420 means roughly the same direction risk as holding 420 shares.">
                <strong>{summary.net_delta_shares.toFixed(1)}</strong><small>net delta (share-equiv)</small>
              </span>
            )}
          </div>
          {(summary.warnings || []).filter((w) => w.kind === 'sector_concentration').map((w) => (
            <p key={w.sector} className="form-message form-message--warn positions-summary-warn">
              {Math.round(w.pct * 100)}% of capital at risk concentrated in {w.sector}
            </p>
          ))}
        </div>
      )}

      {/* Item 8: roll-window group (DTE ≤ 21) rendered above the rest */}
      {(() => {
        // Triage order: soonest expiry first inside the roll window.
        const rollWindow = items.filter((p) => p.dte <= 21).sort((a, b) => a.dte - b.dte)
        const rest = items.filter((p) => p.dte > 21)
        const renderRow = (p: Position) => (
          <tr key={p.id}>
            <td className="text-left">
              <SymbolLink symbol={p.symbol} />
              {p.thesis && <div className="muted" style={{ fontSize: '0.8em', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.thesis}>{p.thesis}</div>}
            </td>
            <td className="text-left">
              <span className={`pill pill--${positionSidePill(p.side)}`}>{p.side}</span>
              {/* A long call's capital at risk is the premium already paid. */}
              {p.side === 'LC' && (
                <div className="muted" style={{ fontSize: '0.8em' }}>
                  {t('positions.atRisk').replace('{v}', money(p.premium * 100 * p.qty))}
                </div>
              )}
            </td>
            <td>${p.strike.toFixed(2)}</td>
            <td className="text-left">{formatExpiration(p.expiry)}</td>
            <td>{p.dte}d</td>
            <td>{p.underlying_price > 0 ? `$${p.underlying_price.toFixed(2)}${p.is_stale ? '*' : ''}` : '—'}</td>
            <td className={p.gap_pct >= 0 ? 'up' : 'down'}>{p.gap_pct.toFixed(1)}%</td>
            <td>${p.premium.toFixed(2)}</td>
            <td>{p.qty}</td>
            <td>${p.rolling_pnl_estimate.toFixed(0)}</td>
            <td className="text-left"><span className={setupPillClass(p.assignment_risk)}>{p.assignment_risk || '—'}</span></td>
            <td className="text-right">
              {calendarEnabled && eventsBySymbol[p.symbol] && (
                <EventChip event={eventsBySymbol[p.symbol]} />
              )}
              {/* Roll candidates apply to CSP and CC only. */}
              {p.side !== 'LC' && (
                <button type="button" className="button button--ghost button--small" onClick={() => openRoll(p)}>Roll</button>
              )}
              <button type="button" className="button button--ghost button--small" onClick={() => markAssigned(p)}>
                {p.side === 'LC' ? t('positions.markExercised') : t('positions.markAssigned')}
              </button>
              <button type="button" className="button button--ghost button--small" onClick={() => navigate(`/journal?symbol=${encodeURIComponent(p.symbol)}&position_id=${encodeURIComponent(p.id)}`)}>{t('journal.logDecision')}</button>
              <button type="button" className="button button--ghost button--small" onClick={() => remove(p.id)}>Remove</button>
            </td>
          </tr>
        )
        const thead = (
          <thead>
            <tr>
              <th className="text-left">Symbol</th>
              <th className="text-left">Side</th>
              <th>Strike</th>
              <th className="text-left">Expiry</th>
              <th>DTE</th>
              <th>Underlying</th>
              <th>Gap %</th>
              <th>Premium</th>
              <th>Qty</th>
              <th>PnL est</th>
              <th className="text-left">Risk</th>
              <th></th>
            </tr>
          </thead>
        )
        return (
          <>
            {rollWindow.length > 0 && (
              <div className="positions-group">
                <p className="positions-group__label">Roll window (≤21 DTE)</p>
                <div className="scanner-table-wrap">
                  <table className="scanner-table">
                    {thead}
                    <tbody>{rollWindow.map(renderRow)}</tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="scanner-table-wrap">
              <table className="scanner-table">
                {thead}
                <tbody>
                  {loading && items.length === 0 ? (
                    <tr><td colSpan={12} className="empty"><LoadingState variant="list" rows={4} label="Loading positions..." /></td></tr>
                  ) : items.length === 0 ? (
                    <tr><td colSpan={12} className="empty">No open positions. Click "+ Add position" to log a trade.</td></tr>
                  ) : rest.length === 0 && rollWindow.length > 0 ? (
                    <tr><td colSpan={12} className="empty muted">All open positions are in the roll window above.</td></tr>
                  ) : rest.map(renderRow)}
                </tbody>
              </table>
            </div>
          </>
        )
      })()}

      <HoldingsPanel
        quotes={liveQuotes}
        assignPositionId={assignPositionId}
        onAssignClose={() => setAssignPositionId(null)}
      />

      {rollOpen && (
        <div className="drawer-backdrop" onClick={() => setRollOpen(null)}>
          <aside className="drawer drawer--wide" onClick={(e) => e.stopPropagation()}>
            <header className="drawer__head">
              <div>
                <p className="kicker">ROLL CANDIDATES</p>
                <h2>{rollOpen.symbol} {rollOpen.side.toUpperCase()} ${rollOpen.strike.toFixed(2)}</h2>
                <p className="muted">Currently {rollOpen.dte}d to expiry. Candidates ranked by score.</p>
              </div>
              <button type="button" className="drawer__close" onClick={() => setRollOpen(null)}>×</button>
            </header>
            {rollLoading && <p className="muted">Loading candidates...</p>}
            {rollError && <p className="form-message form-message--error">{rollError}</p>}
            {rollData?.empty_message && (
              <p className="form-message form-message--warn">{rollData.empty_message}</p>
            )}
            {rollData && rollData.candidates && rollData.candidates.length > 0 && (
              <table className="scanner-table">
                <thead>
                  <tr>
                    <th>Strike</th>
                    <th className="text-left">Expiry</th>
                    <th>DTE</th>
                    <th>Premium</th>
                    <th>Delta</th>
                    <th>APY</th>
                    <th>Score</th>
                    <th className="text-left">Direction</th>
                    <th className="text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rollData.candidates.map((c, i) => (
                    <tr key={`${c.strike}-${c.expiry}-${i}`}>
                      <td><strong>${c.strike.toFixed(2)}</strong></td>
                      <td className="text-left">{formatExpiration(c.expiry)}</td>
                      <td>{c.dte}d</td>
                      <td>${c.premium.toFixed(2)}</td>
                      <td>{c.delta.toFixed(2)}</td>
                      <td>{c.apy.toFixed(1)}%</td>
                      <td>{c.score != null ? c.score.toFixed(0) : '—'}{c.grade ? ` (${c.grade})` : ''}</td>
                      <td className="text-left"><span className="badge">{c.roll_direction || '—'}</span></td>
                      <td className="text-left rationale">{c.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </aside>
        </div>
      )}
    </section>
  )
}

type AnalyzeEventName =
  | 'mode' | 'stock' | 'fundamentals' | 'news' | 'event_pricing'
  | 'gates' | 'insider' | 'peer_compare' | 'data_unavailable' | 'done' | 'error'

// One stream event, kept as {name, summary} so the log can render both a
// human-readable stage (from name) and the raw event line (name + summary).
type AnalyzeLogEntry = { name: string; summary?: string }

// Maps a raw stream event name to a plain-language stage label. Covers every
// event the backend can emit; anything unmapped falls back to "Working...".
function analyzeStageLabel(t: (key: MessageKey) => string, name: string): string {
  const key = `analyze.stage.${name}` as MessageKey
  const label = analyzeStageKeys.has(name) ? t(key) : t('analyze.stage.unknown')
  return label
}
const analyzeStageKeys = new Set([
  'mode', 'stock', 'fundamentals', 'news', 'peer_compare', 'insider',
  'event_pricing', 'gates', 'data_unavailable', 'done', 'error', 'stopped',
])

// gates.Result — this struct has json tags, so keys are snake_case.
type GateCheckPayload = {
  key: string
  label: string
  value?: number
  known: boolean
  passed: boolean
  vetoed: boolean
  credit: number
  why?: string
  comment?: string
}
type GatePayload = {
  id: string
  title: string
  question: string
  outcome: 'pass' | 'marginal' | 'vetoed' | 'unknown' | string
  checks: GateCheckPayload[]
}
type GatesPayload = {
  gates: GatePayload[]
  stopped_at?: string
  evidence_grade: string
  metrics_known: number
  metrics_total: number
  verdict: string
  score: number
  summary: string
}

// orchestrator.SignalLine / FundamentalsPayload / PeerComparePayload — the
// panels the backend has always computed and the client never drew.
type AnalyzeSignalLine = {
  label: string
  value: number
  display: string
  signal: string
  explanation?: string
}
type AnalyzeSignalScore = { signal: string; confidence: number; pct: number }
type FundamentalsPayload = {
  ticker: string
  score: AnalyzeSignalScore
  lines: AnalyzeSignalLine[]
}
type PeerRankPayload = {
  label: string
  value: number
  display: string
  rank: number
  total: number
  // 0..100 already, from rankMetric.
  percentile: number
  better: string
  signal: string
}
type PeerComparePayload = {
  ticker: string
  ranks: Record<string, PeerRankPayload>
  verdict: string
}
// C17 `event_pricing` — every reading is nullable and a null prints "not
// calculated". Nothing here is zero-filled: a 0% implied move is a real
// reading and must stay distinguishable from an absent one.
type EventPricingPayload = {
  symbol: string
  report_at?: string | null
  session?: string | null
  days_until_trading?: number | null
  implied_move_pct?: number | null
  atm_iv_pct?: number | null
  iv_rank_pct?: number | null
  hist_median_move_pct?: number | null
  hist_samples?: number | null
  as_of?: string | null
}
// orchestrator.DataUnavailablePayload — why a deep-mode panel is missing.
type AnalyzeDataUnavailable = { component: string; ticker?: string; reason: string }

function analysisOutcomeLabel(t: (key: MessageKey) => string, verdict?: string): string {
  switch (verdict) {
    case 'vetoed': return t('analyze.outcome.vetoed')
    case 'marginal': return t('analyze.outcome.marginal')
    case 'pass': return t('analyze.outcome.pass')
    case 'unknown': return t('analyze.outcome.unknown')
    default: return t('analyze.outcome.unavailable')
  }
}

// dataprov.StockData uses snake_case + `ticker` (not `symbol`).
type AnalyzeStockSnapshot = {
  ticker?: string
  name?: string
  sector?: string
  price?: number
  market_cap?: number
  pe_ratio?: number
}

type AnalyzeHistoryRun = {
  id: number
  session_id: string
  symbol: string
  mode?: string
  status?: string
  final_action?: string | null
  weighted_score?: number | null
  started_at: string
  finished_at?: string | null
}

type AnalyzeQuotaSlot = { used: number; quota: number; reset_at: string }
type AnalyzeQuotaResp = {
  tier: string
  fast: AnalyzeQuotaSlot
  deep: AnalyzeQuotaSlot
  in_progress?: { session_id: string; symbol: string; mode: string; started_at: string } | null
}

function AnalyzePage({ signedIn, isPro, proModeEnabled = true, ivEvalEnabled = false, earningsCardEnabled = false }: {
  signedIn: boolean; isPro: boolean; proModeEnabled?: boolean; ivEvalEnabled?: boolean; earningsCardEnabled?: boolean
}) {
  const { t } = useI18n()
  // Pre-fill symbol from ?symbol=AAPL so SymbolDetail's analysis CTA lands here ready.
  // ?autorun=1 auto-starts a fast run on arrival.
  const initialParams = (() => {
    if (typeof window === 'undefined') return { symbol: '', autorun: false }
    const q = new URLSearchParams(window.location.search)
    return {
      symbol: (q.get('symbol') || '').toUpperCase(),
      autorun: q.get('autorun') === '1',
    }
  })()
  const initialSymbol = initialParams.symbol
  const [symbol, setSymbol] = useState(initialSymbol)
  const [mode, setMode] = useState<'fast' | 'deep'>('fast')
  const [quota, setQuota] = useState<AnalyzeQuotaResp | null>(null)
  // Tracks whether the quota fetch has resolved (either way) so B2's autorun
  // guard doesn't wait forever if the fetch silently fails — quota is only a
  // pre-check nicety, not a hard requirement to run.
  const [quotaSettled, setQuotaSettled] = useState(false)
  const [running, setRunning] = useState(false)
  const [logs, setLogs] = useState<AnalyzeLogEntry[]>([])
  const [stock, setStock] = useState<AnalyzeStockSnapshot | null>(null)
  const [gates, setGates] = useState<GatesPayload | null>(null)
  const [fundamentals, setFundamentals] = useState<FundamentalsPayload | null>(null)
  const [peerCompare, setPeerCompare] = useState<PeerComparePayload | null>(null)
  const [eventPricing, setEventPricing] = useState<EventPricingPayload | null>(null)
  const [unavailable, setUnavailable] = useState<AnalyzeDataUnavailable[]>([])
  const [quotaBannerDismissed, setQuotaBannerDismissed] = useState(false)
  const [error, setError] = useState('')
  const eventSourceRef = useRef<EventSource | null>(null)
  // Tracks whether the stream emitted `done` cleanly. EventSource fires
  // onerror after a normal server close (browser tries to reconnect), so we
  // can't blindly treat onerror as a connection failure.
  const doneRef = useRef(false)
  // Guard the URL-driven actions so they fire at most once per mount.
  const didAutorun = useRef(false)
  const ivEval = useIVEvaluation(symbol || undefined, ivEvalEnabled)
  const ewc = useEarningsWeekCard(symbol || undefined, earningsCardEnabled)

  useEffect(() => {
    if (!signedIn) return
    api<AnalyzeQuotaResp>('/v1/analyze/quota')
      .then((res) => setQuota(res))
      .catch(() => {})
      .finally(() => setQuotaSettled(true))
  }, [signedIn, running])

  const reset = () => {
    setLogs([])
    setStock(null)
    setGates(null)
    setFundamentals(null)
    setPeerCompare(null)
    setEventPricing(null)
    setUnavailable([])
    setError('')
  }

  const applyEvent = (name: AnalyzeEventName, data: any) => {
    switch (name) {
      case 'stock': setStock(data); break
      case 'gates': setGates(data); break
      case 'fundamentals': setFundamentals(data); break
      case 'peer_compare': setPeerCompare(data); break
      case 'event_pricing': setEventPricing(data); break
      case 'data_unavailable': setUnavailable((prev) => [...prev, data]); break
    }
  }

  const run = async (sym?: string) => {
    const target = (sym || symbol).trim().toUpperCase()
    if (!target) { setError('Enter a symbol.'); return }
    if (!signedIn) { navigate('/login?next=analyze'); return }
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    reset()
    setRunning(true)
    doneRef.current = false
    // EventSource can't auto-refresh on 401 like the fetch wrapper does, so
    // probe identity first and trigger silent refresh if the access cookie
    // expired. Cheaper than waiting for the SSE to fail and reporting a
    // misleading "connect" error.
    try {
      await api('/v1/me')
    } catch {
      setRunning(false)
      setError('Session expired. Please sign in again.')
      navigate('/login?next=analyze')
      return
    }
    const requestMode = mode
    // Pre-check quota to avoid a confusing EventSource onerror — backend
    // returns 402 with a structured body, but EventSource can't read it.
    if (quota) {
      const slot = requestMode === 'deep' ? quota.deep : quota.fast
      if (slot.quota !== -1 && slot.used >= slot.quota) {
        setRunning(false)
        setError(`Out of ${requestMode} analyses this month (${slot.used}/${slot.quota}). Resets ${formatHistoryDate(slot.reset_at)}.`)
        return
      }
    }
    track('analyze_started', { symbol: target, requested_mode: requestMode, entry_point: analyzeEntryPoint() })
    const url = `${API_BASE}/v1/analyze?symbol=${encodeURIComponent(target)}&mode=${requestMode}&lang=${getLanguage()}`
    const es = new EventSource(url, { withCredentials: true })
    eventSourceRef.current = es

    const addLog = (name: string, summary?: string) => {
      setLogs((prev) => [...prev, { name, summary }])
    }
    const handlers: Partial<Record<AnalyzeEventName, (data: any) => void>> = {
      mode: (d) => addLog('mode', d?.mode || ''),
      stock: (d) => { applyEvent('stock', d); addLog('stock', `${d?.ticker || ''} $${d?.price ?? '?'}`) },
      fundamentals: (d: FundamentalsPayload) => { applyEvent('fundamentals', d); addLog('fundamentals', `${d?.lines?.length || 0} ${t('analyze.linesRead')}`) },
      news: (d) => addLog('news', Array.isArray(d?.items) ? `${d.items.length} headlines` : ''),
      insider: () => addLog('insider'),
      peer_compare: (d: PeerComparePayload) => { applyEvent('peer_compare', d); addLog('peer_compare', d?.verdict || '') },
      event_pricing: (d: EventPricingPayload) => { applyEvent('event_pricing', d); addLog('event_pricing') },
      data_unavailable: (d: AnalyzeDataUnavailable) => { applyEvent('data_unavailable', d); addLog('data_unavailable', d?.component || '') },
      gates: (d: GatesPayload) => {
        applyEvent('gates', d)
        addLog('gates', `${analysisOutcomeLabel(t, d?.verdict)} · ${d?.metrics_known || 0}/${d?.metrics_total || 0} ${t('analyze.checksAvailable')}`)
      },
      done: () => { addLog('done'); doneRef.current = true; setRunning(false); es.close() },
      error: (d) => { setError(d?.message || 'stream error'); setRunning(false); es.close() },
    }

    const wire = (name: AnalyzeEventName) => {
      es.addEventListener(name, (evt: MessageEvent) => {
        try {
          const parsed = evt.data ? JSON.parse(evt.data) : {}
          handlers[name]?.(parsed)
        } catch (e) {
          handlers[name]?.({})
        }
      })
    }
    // Deliberately not cast — an `as AnalyzeEventName[]` here previously hid the
    // fact that this list still named the deleted persona events and never
    // registered `gates` or `macro`, so those two payloads arrived and were
    // dropped. Untyped drift must fail the build, not the product.
    const streamEvents: AnalyzeEventName[] = ['mode', 'stock', 'fundamentals', 'news',
      'event_pricing', 'gates', 'insider', 'peer_compare', 'data_unavailable', 'done', 'error']
    streamEvents.forEach(wire)

    es.onerror = () => {
      // EventSource fires onerror on EVERY server close (it tries to
      // auto-reconnect by default). Only treat as user-visible if we never
      // received `done` AND no other event arrived.
      if (!doneRef.current && es.readyState !== EventSource.OPEN) {
        // readyState=CONNECTING (0) on initial fail, CLOSED (2) after close.
        // We've never received `done`, so this is a real failure.
        if (es.readyState === EventSource.CLOSED) {
          setError('Could not connect to /v1/analyze. Make sure you are signed in.')
        }
      }
      setRunning(false)
      es.close()
    }
  }

  useEffect(() => () => { eventSourceRef.current?.close() }, [])

  // B6 Stop: close the stream, reset running state, unlock inputs, and note it
  // in the log so the user sees a clean stop before re-running.
  const stop = () => {
    if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null }
    doneRef.current = true // suppress the onerror "could not connect" path
    setRunning(false)
    setLogs((prev) => [...prev, { name: 'stopped' }])
  }

  // Drop the autorun query param after we've acted on it so a refresh
  // doesn't re-trigger the same restore/auto-run. Symbol is kept for context.
  const stripAnalyzeParams = () => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (!url.searchParams.has('run') && !url.searchParams.has('autorun')) return
    url.searchParams.delete('run')
    url.searchParams.delete('autorun')
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
  }


  // B2: ?autorun=1 auto-starts a fast run once, after the quota fetch has
  // settled (success or failure) so the pre-check still applies when quota
  // loads — but a failed fetch doesn't block autorun forever. Never auto-runs
  // deep.
  useEffect(() => {
    if (didAutorun.current) return
    if (!initialParams.autorun) return
    if (mode !== 'fast' || !initialSymbol) return
    if (!quotaSettled) return
    didAutorun.current = true
    stripAnalyzeParams()
    run(initialSymbol)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotaSettled])

  // Maps both Signal (bullish/bearish/neutral) and verdict strings (BUY/SELL/HOLD)
  // to a pill colour. Backend emits "bullish/bearish/neutral" for persona +
  // consensus; ActionPlan emits "BUY/SELL/HOLD".
  const voteClass = (vote?: string) => {
    if (!vote) return 'pill'
    const v = vote.toLowerCase()
    if (v.includes('bull') || v.includes('buy')) return 'pill pill--green'
    if (v.includes('bear') || v.includes('sell')) return 'pill pill--rose'
    return 'pill pill--amber'
  }

  return (
    <section className="scanner">
      <header className="scanner__head">
        <div>
          <p className="kicker">{t('analyze.kicker')}</p>
          <h1>{t('analyze.title')}</h1>
          <p className="muted">
            {proModeEnabled
              ? t('analyze.intro.pro')
              : t('analyze.intro.free')}
          </p>
          <p className="muted small">{t('analyze.sourceNote')}</p>
        </div>
      </header>

      <form className="analyze-form" onSubmit={(e) => { e.preventDefault(); run() }}>
        <input
          placeholder="Symbol (e.g. AAPL)"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          disabled={running}
        />
        <div className="tab-row" role="tablist" aria-label="Analysis mode">
          <button type="button" className={mode === 'fast' ? 'tab is-active' : 'tab'} onClick={() => setMode('fast')} disabled={running}>Fast</button>
          <button
            type="button"
            className={mode === 'deep' ? 'tab is-active' : 'tab'}
            onClick={() => setMode('deep')}
            disabled={running}
            title={t('analyze.deepTitle')}
          >
            Deep
            {(() => {
              const dl = quota?.deep.quota ?? (isPro ? 20 : 3)
              if (dl === -1) return null
              return <span className="tab-quota-badge">{dl}{t('analyze.perMonth')}</span>
            })()}
          </button>
        </div>
        <button type="submit" className="button button--primary" disabled={running}>
          {running ? t('analyze.running') : t('analyze.run')}
        </button>
        {running && (
          <button type="button" className="button button--ghost" onClick={stop}>{t('analyze.stop')}</button>
        )}
      </form>

      {quota && (
        <p className="muted analyze-quota">
          {(() => {
            const slot = mode === 'deep' ? quota.deep : quota.fast
            if (slot.quota === -1) return `${mode} analyses: unlimited this month.`
            const remaining = Math.max(0, slot.quota - slot.used)
            return `${mode} analyses: ${slot.used} / ${slot.quota} used this month · ${remaining} left · resets ${formatHistoryDate(slot.reset_at)}.`
          })()}
        </p>
      )}
      {/* Item 6: quota pre-warning banner — show when exactly 1 run left */}
      {quota && !quotaBannerDismissed && (() => {
        const slot = mode === 'deep' ? quota.deep : quota.fast
        if (slot.quota === -1) return null
        const remaining = Math.max(0, slot.quota - slot.used)
        if (remaining !== 1) return null
        return (
          <div className="form-message form-message--warn analyze-quota-warn" role="alert">
            <span>
              1 {mode} run left this month · resets {formatHistoryDate(slot.reset_at)}
              {proModeEnabled && !isPro && <>{' '}<Link to="/pricing">Upgrade to Pro for more</Link></>}
            </span>
            <button type="button" className="button button--ghost button--small" onClick={() => setQuotaBannerDismissed(true)} aria-label="Dismiss">✕</button>
          </div>
        )
      })()}

      {error && <p className="form-message form-message--error">{error}</p>}

      <div className="analyze-grid">
        <div className="analyze-main">
          {running && !stock && <LoadingState variant="card" rows={3} label="Loading analysis..." />}
          {stock && (
            <article className="analyze-card">
              <h3>{stock.ticker || symbol.toUpperCase()}{stock.name && stock.name !== stock.ticker ? ` — ${stock.name}` : ''}</h3>
              <p>
                {stock.price != null && <strong>${stock.price.toFixed(2)}</strong>}
                {stock.sector && <span className="muted"> · {stock.sector}</span>}
                {stock.market_cap != null && stock.market_cap > 0 && <span className="muted"> · mcap {formatRevenue(stock.market_cap)}</span>}
                {stock.pe_ratio != null && stock.pe_ratio > 0 && <span className="muted"> · P/E {stock.pe_ratio.toFixed(1)}</span>}
              </p>
            </article>
          )}

          {gates && <GatesReport report={gates} eventPricing={eventPricing} />}

          {fundamentals && <FundamentalsPanel data={fundamentals} />}

          {peerCompare && <PeerComparePanel data={peerCompare} />}

          {earningsCardEnabled && ewc && <EarningsWeekCard data={ewc} />}
          {ivEvalEnabled && ivEval && <IVEvaluationCard data={ivEval} />}

          {unavailable.length > 0 && (
            <article className="analyze-card analyze-unavailable">
              <h3>{t('analyze.unavailable.title')}</h3>
              <ul>
                {unavailable.map((u, i) => (
                  <li key={`${u.component}-${i}`}>
                    {/* An unmapped component name prints as sent: the generic
                        "Working..." fallback would be a lie in this list. */}
                    <strong>{analyzeStageKeys.has(u.component) ? analyzeStageLabel(t, u.component) : u.component}</strong> — {u.reason}
                  </li>
                ))}
              </ul>
            </article>
          )}

          {running && !gates && <LoadingState variant="card" rows={4} label={t('analyze.loading')} />}
        </div>

        <aside className="analyze-side">
          <article className="analyze-card">
            <h3>Stream</h3>
            <ol className="analyze-log">
              {logs.map((entry, i) => (
                <li key={i}>
                  <span className="analyze-log__stage">{analyzeStageLabel(t, entry.name)}</span>
                  {entry.summary && <span className="muted"> — {entry.summary}</span>}
                </li>
              ))}
            </ol>
          </article>
        </aside>
      </div>
    </section>
  )
}

// A signal line's colour says which side of its own threshold the reading sits
// on. It is a description of the number, not a call on the stock.
function analyzeSignalClass(signal?: string): string {
  if (signal === 'bullish') return 'is-pass'
  if (signal === 'bearish') return 'is-vetoed'
  return 'is-marginal'
}

// C17 event & option pricing. Every field is optional on the wire and a null
// prints "not calculated" — never a zero, which would read as a measurement.
function EventPricingPanel({ data }: { data: EventPricingPayload }) {
  const { t } = useI18n()
  const none = t('analyze.notCalculated')
  const pct = (v?: number | null, digits = 1) => (v == null ? none : `${v.toFixed(digits)}%`)
  const session = briefSessionLabel(t, data.session)
  return (
    <article className="analyze-card event-pricing">
      <h3>{t('analyze.eventPricing.title')}</h3>
      <p className="event-pricing__date">
        <span className="muted">{t('analyze.eventPricing.reportDate')}</span>{' '}
        {data.report_at ? formatHistoryDate(data.report_at) : none}
        {session && <span className="brief-chip brief-chip--session">{session}</span>}
        {data.days_until_trading != null && (
          <span className="muted"> · {t('analyze.eventPricing.countdown').replace('{n}', String(data.days_until_trading))}</span>
        )}
      </p>
      <ul className="event-pricing__grid">
        <li><span>{t('analyze.eventPricing.impliedMove')}</span><strong>{pct(data.implied_move_pct)}</strong></li>
        <li><span>{t('analyze.eventPricing.atmIv')}</span><strong>{pct(data.atm_iv_pct)}</strong></li>
        <li><span>{t('analyze.eventPricing.ivRank')}</span><strong>{data.iv_rank_pct == null ? none : String(Math.round(data.iv_rank_pct))}</strong></li>
        <li>
          <span>{t('analyze.eventPricing.histMedianMove')}</span>
          <strong>{pct(data.hist_median_move_pct)}</strong>
          {data.hist_samples != null && data.hist_samples > 0 && (
            <em className="muted">{t('analyze.eventPricing.samples').replace('{n}', String(data.hist_samples))}</em>
          )}
        </li>
      </ul>
      {data.as_of && <p className="muted small">{t('events.asOf').replace('{d}', data.as_of.slice(0, 10))}</p>}
    </article>
  )
}

// Every line the backend sent, in the order it sent them. Trimming the list is
// what made the result read as "not enough data".
function FundamentalsPanel({ data }: { data: FundamentalsPayload }) {
  const { t } = useI18n()
  return (
    <article className="analyze-card">
      <h3>{t('analyze.fundamentals.title')}</h3>
      <ul className="gate__checks">
        {data.lines.map((l, i) => (
          <li key={`${l.label}-${i}`} className={analyzeSignalClass(l.signal)}>
            <span className="gate__label">{l.label}</span>
            <span className="gate__value">{l.display}</span>
            {l.explanation && <span className="gate__note">{l.explanation}</span>}
          </li>
        ))}
      </ul>
      <p className="muted small">{t('analyze.fundamentals.note')}</p>
    </article>
  )
}

// Rank and percentile inside the peer set the provider returned — a position in
// a group, not a verdict on the company.
function PeerComparePanel({ data }: { data: PeerComparePayload }) {
  const { t } = useI18n()
  const ranks = Object.entries(data.ranks || {})
  if (ranks.length === 0) return null
  return (
    <article className="analyze-card">
      <h3>{t('analyze.peers.title')}</h3>
      <ul className="gate__checks">
        {ranks.map(([key, r]) => (
          <li key={key} className={analyzeSignalClass(r.signal)}>
            <span className="gate__label">{r.label}</span>
            <span className="gate__value">{r.display}</span>
            <span className="gate__note">
              {t('analyze.peers.rank').replace('{r}', String(r.rank)).replace('{n}', String(r.total))}
              {' · '}
              {t('analyze.peers.percentile').replace('{p}', String(Math.round(r.percentile)))}
            </span>
          </li>
        ))}
      </ul>
      <p className="muted small">{t('analyze.peers.note')}</p>
    </article>
  )
}

// Renders the rule-derived stock-analysis result. Internal gate and veto fields
// remain part of the API contract; the UI describes their user-facing meaning.
function GatesReport({ report, eventPricing }: { report: GatesPayload; eventPricing?: EventPricingPayload | null }) {
  const { t } = useI18n()
  const outcomeLabel = (outcome: string) => {
    switch (outcome) {
      case 'vetoed': return t('analyze.check.vetoed')
      case 'marginal': return t('analyze.check.marginal')
      case 'pass': return t('analyze.check.pass')
      case 'unknown': return t('analyze.check.unknown')
      default: return t('analyze.outcome.unavailable')
    }
  }
  const fmt = (c: GateCheckPayload) => {
    if (!c.known || c.value == null) return t('analyze.notReported')
    return ['debt_to_equity', 'current_ratio', 'volatility'].includes(c.key)
      ? c.value.toFixed(2)
      : `${(c.value * 100).toFixed(1)}%`
  }
  const mark = (c: GateCheckPayload) => (!c.known ? '—' : c.vetoed ? '✕' : c.passed ? '✓' : '!')
  const checkClass = (c: GateCheckPayload) =>
    !c.known ? 'is-unknown' : c.vetoed ? 'is-vetoed' : c.passed ? 'is-pass' : 'is-marginal'

  return (
    <article className={`gates gates--${report.verdict}`}>
      <header className="gates__head">
        {/* Score and data-coverage grade always render together: unknown checks
            leave the denominator, so a high score off two checks would
            otherwise be indistinguishable from one off six. */}
        <div className="gates__score">
          {/* The backend withholds the score on an unknown verdict rather than
              publishing a confident number next to "not checked". A literal 0
              would read as the worst result instead of an absent one. */}
          <strong>{report.verdict === 'unknown' ? '—' : report.score}</strong>
          <span>{report.verdict === 'unknown' ? t('analyze.noScore') : '/100'}</span>
        </div>
        <h3>{analysisOutcomeLabel(t, report.verdict)}</h3>
        <div className="gates__evidence">
          {/* Evidence grade sits beside the score, not below the gates: it is
              the qualifier on the number, not a footnote to it. */}
          {report.evidence_grade && (
            <span className={`evidence-grade evidence-grade--${report.evidence_grade.toLowerCase()}`}>
              {t('analyze.evidenceGrade').replace('{g}', report.evidence_grade)}
            </span>
          )}
          <span>{t('analyze.coverage')}</span>
          <span className="muted">{report.metrics_known}/{report.metrics_total} {t('analyze.checksAvailable')}</span>
        </div>
      </header>
      <p className="gates__summary">{report.summary}</p>

      {/* Between the score and the gates: what the market is charging for the
          next event is context on the verdict, not one of its checks. */}
      {eventPricing && <EventPricingPanel data={eventPricing} />}

      {report.gates.map((g) => (
        <section key={g.id} className={`gate gate--${g.outcome}`}>
          <div className="gate__head">
            <h4>{g.title}</h4>
            <span className={`gate__badge gate__badge--${g.outcome}`}>{outcomeLabel(g.outcome)}</span>
          </div>
          <p className="gate__question">{g.question}</p>
          <ul className="gate__checks">
            {g.checks.map((c) => (
              <li key={c.key} className={checkClass(c)}>
                <span className="gate__mark">{mark(c)}</span>
                <span className="gate__label">{c.label}</span>
                <span className="gate__value">{fmt(c)}</span>
                {c.comment
                  ? <span className="gate__note">{c.comment}</span>
                  : (c.why && (c.vetoed || !c.passed)) ? <span className="gate__note">{c.why}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ))}

      {report.stopped_at && (
        <p className="gates__note">
          {t('analyze.laterChecks')}
        </p>
      )}
      <p className="gates__note gates__note--scope">
        {t('analyze.scopeNote')}
      </p>
    </article>
  )
}

function AuthPage({ mode, onAuthChange }: { mode: 'login' | 'register'; onAuthChange: (value: boolean) => void }) {
  const { t } = useI18n()
  const [message, setMessage] = useState('')
  // OAuth code-flow client lives in a ref so the click handler can call
  // requestCode() without forcing a re-init on every render.
  const googleCodeClientRef = useRef<{ requestCode: () => void } | null>(null)
  const title = mode === 'login' ? t('auth.title.login') : t('auth.title.register')

  const finishAuth = () => {
    onAuthChange(true)
    const next = new URLSearchParams(window.location.search).get('next')
    const target = authDestination(next)
    // Hard-redirect after sign-in so the App re-mounts and re-reads the
    // signed-in flag from localStorage; SPA navigate() does not always
    // propagate state changes triggered from a GSI popup callback.
    window.location.assign(target)
  }

  useEffect(() => {
    let cancelled = false
    const tryInit = () => {
      if (cancelled) return
      const oauth2 = window.google?.accounts?.oauth2
      if (!oauth2) {
        window.setTimeout(tryInit, 120)
        return
      }
      // Code-flow + popup → we get an authorization code in the callback,
      // exchange it for an id_token on the backend. This lets us style the
      // button to match Apple's (renderButton would force the personalized
      // "Continue as <name>" card variant for signed-in Google users).
      googleCodeClientRef.current = oauth2.initCodeClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        ux_mode: 'popup',
        callback: async (response) => {
          if (response.error) {
            const e = response.error.toLowerCase()
            if (e.includes('cancel') || e.includes('popup_closed')) {
              setMessage('')
              return
            }
            setMessage(response.error_description || response.error)
            return
          }
          if (!response.code) {
            setMessage(t('auth.message.googleCodeMissing'))
            return
          }
          setMessage(t('auth.message.signingInGoogle'))
          try {
            await api('/v1/auth/google', {
              method: 'POST',
              body: JSON.stringify({ code: response.code, redirect_uri: 'postmessage' }),
            })
            finishAuth()
          } catch (err) {
            setMessage((err as Error).message)
          }
        },
      })
    }
    tryInit()
    return () => {
      cancelled = true
    }
  }, [])

  const signInWithGoogle = () => {
    if (!googleCodeClientRef.current) {
      setMessage(t('auth.loadingGoogle'))
      return
    }
    googleCodeClientRef.current.requestCode()
  }

  useEffect(() => {
    let cancelled = false
    const tryInit = () => {
      if (cancelled) return
      const a = window.AppleID?.auth
      if (!a) {
        window.setTimeout(tryInit, 120)
        return
      }
      a.init({
        clientId: APPLE_SERVICE_ID,
        scope: 'name email',
        redirectURI: APPLE_REDIRECT_URI,
        state: 'navoption-' + Math.random().toString(36).slice(2),
        usePopup: true,
      })
    }
    tryInit()
    return () => {
      cancelled = true
    }
  }, [])

  const signInWithApple = async () => {
    const a = window.AppleID?.auth
    if (!a) {
      setMessage(t('auth.loadingApple'))
      return
    }
    setMessage(t('auth.message.signingInApple'))
    try {
      const result = await a.signIn()
      const idToken = result?.authorization?.id_token
      if (!idToken) throw new Error(t('auth.message.appleTokenMissing'))
      await api('/v1/auth/apple', {
        method: 'POST',
        body: JSON.stringify({ identity_token: idToken }),
      })
      finishAuth()
    } catch (err) {
      const msg = (err as Error).message || ''
      if (msg.includes('popup_closed') || msg.toLowerCase().includes('cancel')) {
        setMessage('')
        return
      }
      setMessage(msg || t('auth.message.appleFailed'))
    }
  }

  return (
    <section className="auth">
      <div className="auth-card">
        <h1>{title}</h1>
        <p className="form-hint">{mode === 'login' ? t('auth.body.login') : t('auth.body.register')}</p>
        <div className="auth-social">
          <button type="button" className="social-button apple-button" onClick={signInWithApple}>
            <svg className="social-button__logo" viewBox="0 0 384 512" aria-hidden focusable={false}>
              <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
            </svg>
            <span>{mode === 'login' ? t('auth.apple.login') : t('auth.apple.register')}</span>
          </button>
          <button type="button" className="social-button google-button" onClick={signInWithGoogle}>
            <svg className="social-button__logo" viewBox="0 0 48 48" aria-hidden focusable={false}>
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            <span>{mode === 'login' ? t('auth.google.login') : t('auth.google.register')}</span>
          </button>
        </div>
        <p className="form-message">{message}</p>
      </div>
    </section>
  )
}

type SubscriptionEntry = {
  product_id: string
  status: string
  source: string
  environment: string
  original_txn_id?: string
  expires_at: string
  last_verified_at: string
  cancel_at_period_end?: boolean
}

type AccountPlanInfo = {
  id: string
  name: string
  price_label?: string
  price_subtext?: string
  trial_days?: number
  stripe_price_id?: string
}

// Map a persisted subscription's product_id (Stripe price_... or App Store
// reverse-DNS id) back to a friendly plan name + price label using the
// /v1/billing/plans response. Falls back to a humanised App-Store id when
// nothing matches (e.g. legacy / one-off products).
function planLookup(plans: AccountPlanInfo[], proModeEnabled = true): (productId: string) => { name: string; subtext: string } {
  const byPriceId = new Map<string, AccountPlanInfo>()
  for (const p of plans) {
    if (p.stripe_price_id) byPriceId.set(p.stripe_price_id, p)
  }
  return (productId: string) => {
    if (!proModeEnabled) {
      if (productId.startsWith('com.navoption.')) return { name: 'App Store record', subtext: 'Paused billing' }
      if (productId.startsWith('price_')) return { name: 'Stripe record', subtext: 'Paused billing' }
      if (productId.startsWith('promo.')) return { name: 'Access record', subtext: 'Promo' }
      if (productId === 'admin.pro') return { name: 'Access record', subtext: 'Admin grant' }
      return { name: 'Access record', subtext: '' }
    }
    const fromStripe = byPriceId.get(productId)
    if (fromStripe) {
      const subtext = [fromStripe.price_label, fromStripe.price_subtext].filter(Boolean).join(' ')
      return { name: fromStripe.name, subtext }
    }
    // App Store: com.navoption.app.pro.month → "Pro Month · App Store"
    if (productId.startsWith('com.navoption.')) {
      const tail = productId.split('.').slice(-2).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
      return { name: tail, subtext: 'App Store' }
    }
    // Unmapped Stripe price (rare — plan removed from billing.plans but the
    // subscription still exists). Show a friendly label rather than the raw
    // price_id which is meaningless to users.
    if (productId.startsWith('price_')) {
      return { name: 'Pro plan', subtext: 'Stripe subscription' }
    }
    if (productId.startsWith('promo.code')) {
      return { name: 'Pro access', subtext: 'Promo code' }
    }
    if (productId.startsWith('promo.signup_trial') || productId === 'promo.signup_trial') {
      return { name: 'Pro access', subtext: 'Signup trial' }
    }
    if (productId === 'admin.pro') {
      return { name: 'Pro access', subtext: 'Admin grant' }
    }
    return { name: productId, subtext: '' }
  }
}

type AuthEventEntry = {
  provider: string
  event_type: string
  ip?: string
  user_agent?: string
  ts: string
}

function AccountPage({ onAuthChange, proModeEnabled }: { onAuthChange: (value: boolean) => void; proModeEnabled: boolean }) {
  const { t } = useI18n()
  const [me, setMe] = useState<Me | null>(null)
  const [counts, setCounts] = useState<WorkspaceCounts>({ watchlist: 0, positions: 0, alerts: 0, presets: 0 })
  const [subscriptions, setSubscriptions] = useState<SubscriptionEntry[]>([])
  const [authEvents, setAuthEvents] = useState<AuthEventEntry[]>([])
  const [plans, setPlans] = useState<AccountPlanInfo[]>([])
  const [message, setMessage] = useState(t('account.loading'))
  const [promoCode, setPromoCode] = useState('')
  const [promoMsg, setPromoMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [promoPending, setPromoPending] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const profile = await api<Me>('/v1/me')
        if (cancelled) return
        setMe(profile)
        onAuthChange(true)
        const [watchlist, positions, alerts, presets, subs, events, plansRes] = await Promise.allSettled([
          api<{ items?: unknown[] }>('/v1/watchlist'),
          api<{ positions?: { status?: string }[] }>('/v1/positions'),
          api<{ items?: { status?: string }[] }>('/v1/alerts'),
          api<{ items?: unknown[] }>('/v1/presets'),
          api<{ subscriptions?: SubscriptionEntry[] }>('/v1/subscriptions/me'),
          api<{ events?: AuthEventEntry[] }>('/v1/auth/events'),
          api<{ plans?: AccountPlanInfo[] }>('/v1/billing/plans'),
        ])
        if (cancelled) return
        setCounts({
          watchlist: watchlist.status === 'fulfilled' ? watchlist.value.items?.length || 0 : 0,
          positions: positions.status === 'fulfilled' ? positions.value.positions?.filter((p) => (p.status || 'open') === 'open').length || 0 : 0,
          alerts: alerts.status === 'fulfilled' ? alerts.value.items?.filter((a) => a.status === 'pending').length || 0 : 0,
          presets: presets.status === 'fulfilled' ? presets.value.items?.length || 0 : 0,
        })
        setSubscriptions(subs.status === 'fulfilled' ? subs.value.subscriptions || [] : [])
        setAuthEvents(events.status === 'fulfilled' ? events.value.events || [] : [])
        setPlans(plansRes.status === 'fulfilled' ? plansRes.value.plans || [] : [])
        setMessage('')
      } catch (err) {
        if ((err as ApiError).status === 401) {
          onAuthChange(false)
          navigate('/login?next=account')
          return
        }
        setMessage((err as Error).message)
      }
    }
    load()
    return () => { cancelled = true }
  }, [onAuthChange])

  // When the homepage promo bar sends the user here, pre-fill the redeem
  // code from either ?code=… (signed-in click) or sessionStorage (set
  // before the login round-trip), then scroll + focus the input. The
  // user still has to click Apply — we never auto-redeem.
  useEffect(() => {
    if (!me) return
    const urlCode = new URLSearchParams(window.location.search).get('code')
    let stashed: string | null = null
    try { stashed = sessionStorage.getItem('pendingPromoCode') } catch {}
    const pending = (urlCode || stashed || '').trim().toUpperCase()
    if (pending) {
      setPromoCode(pending)
      try { sessionStorage.removeItem('pendingPromoCode') } catch {}
    }
    if (!pending && window.location.hash !== '#promo-code-input') return
    const el = document.getElementById('promo-code-input') as HTMLInputElement | null
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.focus({ preventScroll: true })
  }, [me])

  // Cancellable: active Stripe sub that isn't already scheduled to end. Once
  // cancel_at_period_end=true the UI suppresses the Cancel button and shows
  // an "ends MM/DD" pill instead of re-prompting.
  const activeStripeSub = proModeEnabled
    ? subscriptions.find((s) => s.source === 'stripe' && s.status === 'active' && !s.cancel_at_period_end)
    : undefined
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelPending, setCancelPending] = useState(false)
  const cancelPlanCfg = activeStripeSub ? plans.find((pl) => pl.stripe_price_id === activeStripeSub.product_id) : undefined
  const cancelPlanLabel = activeStripeSub ? planLookup(plans, proModeEnabled)(activeStripeSub.product_id) : null
  // Trial cancel is immediate (no charge yet); paid-period cancel is graceful
  // (keeps access through current period). Derived from the matched plan's
  // trial_days vs how far the sub's expiry is from today.
  const cancelInTrial = (() => {
    if (!activeStripeSub || !cancelPlanCfg?.trial_days) return false
    const daysLeft = Math.round((new Date(activeStripeSub.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    return daysLeft <= cancelPlanCfg.trial_days
  })()

  const confirmCancelSubscription = async () => {
    if (!activeStripeSub) { setCancelOpen(false); return }
    setCancelPending(true)
    try {
      const res = await api<{ mode: string; access_until: string }>('/v1/billing/cancel', { method: 'POST' })
      if (res.mode === 'immediate') {
        setMessage('Trial ended. You are now on Free.')
        setCancelOpen(false)
        // Reload so the nav badge, Tier metric, and pricing CTAs all reflect
        // the downgrade immediately rather than waiting for the next fetch.
        setTimeout(() => window.location.reload(), 600)
      } else {
        const until = new Date(res.access_until).toLocaleDateString()
        setMessage(`Subscription cancelled. Pro access continues until ${until}, then drops to Free.`)
        setCancelOpen(false)
      }
    } catch (err) {
      setMessage((err as Error).message)
    } finally {
      setCancelPending(false)
    }
  }

  const submitPromo = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = promoCode.trim()
    if (!trimmed) return
    setPromoPending(true)
    setPromoMsg(null)
    try {
      const res = await api<{ pro_days: number; expires_at: string }>('/v1/promo/redeem', {
        method: 'POST',
        body: JSON.stringify({ code: trimmed }),
      })
      const until = new Date(res.expires_at).toLocaleDateString()
      setPromoMsg({ kind: 'ok', text: `Code applied — ${res.pro_days} days of Pro through ${until}.` })
      setPromoCode('')
      // Reload to refresh tier metric, subscription history, and nav badge.
      setTimeout(() => window.location.reload(), 800)
    } catch (err) {
      setPromoMsg({ kind: 'err', text: (err as ApiError).message || 'Could not apply this code.' })
    } finally {
      setPromoPending(false)
    }
  }

  const signOut = async () => {
    setMessage(t('account.signingOut'))
    try {
      await api('/v1/auth/signout', { method: 'POST' })
    } catch {
      // Local marker still clears; cookie expiry failure should not trap the user in the UI.
    }
    onAuthChange(false)
    navigate('/')
  }

  return (
    <section className="account">
      <div className="account__head">
        <div>
          <span className="eyebrow">{t('account.eyebrow')}</span>
          <h1>{t('account.heading')}</h1>
          <p>Manage your subscription, saved candidates, presets, alerts, and income-trade positions.</p>
        </div>
        <button className="button button--secondary" onClick={signOut}>Sign out</button>
      </div>
      <div className="account-grid">
        <Metric label="Email" value={me?.email || '-'} meta="Shared login" />
        <Metric
          label="Tier"
          value={proModeEnabled ? (me?.tier || 'free').toUpperCase() : 'FULL'}
          meta={(() => {
            if (!proModeEnabled) return 'Paid plans paused'
            if (!me?.tier || me.tier === 'free') return 'Free access'
            const activeStripe = subscriptions.find((s) => s.source === 'stripe' && s.status === 'active')
            const activeAny = subscriptions.find((s) => s.status === 'active')
            const lookup = planLookup(plans, proModeEnabled)
            if (activeStripe) {
              const p = lookup(activeStripe.product_id)
              const expires = new Date(activeStripe.expires_at)
              const today = new Date()
              const daysLeft = Math.max(0, Math.round((expires.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)))
              const planMatch = plans.find((pl) => pl.stripe_price_id === activeStripe.product_id)
              const inTrial = !!(planMatch && planMatch.trial_days && daysLeft <= planMatch.trial_days)
              const dateStr = expires.toLocaleDateString()
              if (inTrial) {
                // Show: "Pro 3-Month · trial — 6 days left · auto-renews 5/23 at $49.99 every 3 months"
                const priceTail = planMatch && (planMatch.price_label || planMatch.price_subtext)
                  ? ` at ${planMatch.price_label}${planMatch.price_subtext ? ' ' + planMatch.price_subtext : ''}`
                  : ''
                return `${p.name} · trial — ${daysLeft} day${daysLeft === 1 ? '' : 's'} left · auto-renews ${dateStr}${priceTail}`
              }
              return `${p.name} · renews ${dateStr}`
            }
            if (activeAny) {
              const p = lookup(activeAny.product_id)
              return `${p.name} · through ${new Date(activeAny.expires_at).toLocaleDateString()}`
            }
            return me?.expires_at ? `Through ${new Date(me.expires_at).toLocaleDateString()}` : 'Active'
          })()}
        />
        <Metric label="Workflow" value="Saved" meta="Scanner + positions" />
      </div>
      <div className="account-grid account-grid--four">
        <Metric label="Watchlist" value={String(counts.watchlist)} meta="symbols" />
        <Metric label="Open positions" value={String(counts.positions)} meta="attention" />
        <Metric label="Active alerts" value={String(counts.alerts)} meta="levels" />
        <Metric label="Scan presets" value={String(counts.presets)} meta="filters" />
      </div>
      <div className="account-actions">
        <Link className="button button--primary" to="/scan">Scan candidates</Link>
        {proModeEnabled && me?.tier !== 'pro' && <Link className="button button--outline" to="/pricing">Upgrade to Pro</Link>}
      </div>
      {proModeEnabled && (
        <form className="promo-redeem" onSubmit={submitPromo}>
          <div className="promo-redeem__row">
            <input
              id="promo-code-input"
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="Promo code"
              aria-label="Promo code"
              autoComplete="off"
              spellCheck={false}
              disabled={promoPending}
            />
            <button type="submit" className="button button--outline" disabled={promoPending || !promoCode.trim()}>
              {promoPending ? 'Applying…' : 'Apply'}
            </button>
          </div>
          {promoMsg && (
            <p className={`promo-redeem__msg ${promoMsg.kind === 'ok' ? 'is-ok' : 'is-err'}`}>{promoMsg.text}</p>
          )}
        </form>
      )}
      <div className="account-history">
        <div>
          <h2>Subscription history</h2>
          {subscriptions.length === 0 ? (
            <p className="account-history__empty">{proModeEnabled ? 'No subscription records yet. Pro plans purchased on the App Store or via Stripe will show up here.' : 'No subscription records yet.'}</p>
          ) : (
            <table className="account-history__table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Period</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((sub) => {
                  const display = planLookup(plans, proModeEnabled)(sub.product_id)
                  const rowId = shortDisplayId(`${sub.source}|${sub.original_txn_id || sub.product_id}|${sub.expires_at}`)
                  const sourceLabel = (() => {
                    if (sub.source === 'stripe') return `Stripe${sub.environment === 'stripe_test' ? ' (test)' : ''}`
                    if (sub.environment === 'promo_code') return 'Promo code'
                    if (sub.environment === 'signup_trial') return 'Signup trial'
                    if (sub.environment === 'admin') return 'Admin grant'
                    return 'App Store'
                  })()
                  const periodLabel = (() => {
                    const when = new Date(sub.expires_at).toLocaleDateString()
                    const ended = new Date(sub.expires_at).getTime() <= Date.now()
                    if (sub.status === 'active' && !ended) {
                      if (sub.cancel_at_period_end) return `Ends ${when}`
                      if (sub.source === 'stripe') return `Renews ${when}`
                      return `Through ${when}`
                    }
                    return `Ended ${when}`
                  })()
                  return (
                    <tr key={`${sub.source}:${sub.original_txn_id || sub.product_id}:${sub.last_verified_at}`}>
                      <td className="mono" style={{ fontSize: '0.78rem' }}>{rowId}</td>
                      <td>
                        <strong>{display.name}</strong>
                        {display.subtext && <div className="muted" style={{ fontSize: '0.78rem' }}>{display.subtext}</div>}
                      </td>
                      <td>
                        {sub.cancel_at_period_end && sub.status === 'active' ? (
                          <span className="pill pill--mute">ending</span>
                        ) : (
                          <span className={`pill pill--${sub.status === 'active' || sub.status === 'trialing' ? 'good' : 'mute'}`}>{sub.status}</span>
                        )}
                        {sub === activeStripeSub && (
                          <button
                            type="button"
                            className="link-like-quiet sub-row-cancel"
                            onClick={() => setCancelOpen(true)}
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                      <td>{sourceLabel}</td>
                      <td>{periodLabel}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
        <div>
          <h2>Recent sign-ins</h2>
          {authEvents.length === 0 ? (
            <p className="account-history__empty">No sign-in history yet.</p>
          ) : (
            <table className="account-history__table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Provider</th>
                  <th>Type</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {authEvents.map((evt, i) => (
                  <tr key={`${evt.ts}-${i}`}>
                    <td>{formatHistoryDate(evt.ts)}</td>
                    <td>{evt.provider}</td>
                    <td>{evt.event_type}</td>
                    <td className="mono">{evt.ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <p className="form-message">{message}</p>
      {proModeEnabled && cancelOpen && activeStripeSub && cancelPlanLabel && (
        <div className="modal-backdrop" onClick={() => !cancelPending && setCancelOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel {cancelPlanLabel.name}?</h3>
            {cancelInTrial ? (
              <ul className="modal__points">
                <li><strong>You're still in your free trial</strong>, so cancellation takes effect <strong>immediately</strong>.</li>
                <li>Pro access ends now; you'll be on Free.</li>
                <li>You have not been charged. No charge will happen.</li>
                <li>You can subscribe again anytime from the Pricing page.</li>
              </ul>
            ) : (
              <ul className="modal__points">
                <li>Pro access continues until <strong>{new Date(activeStripeSub.expires_at).toLocaleDateString()}</strong> (end of your current billing period).</li>
                <li>No further charges; the subscription will not auto-renew.</li>
                <li>You can resubscribe anytime from the Pricing page.</li>
              </ul>
            )}
            <div className="modal__actions">
              <button type="button" className="button button--ghost" onClick={() => setCancelOpen(false)} disabled={cancelPending}>Keep subscription</button>
              <button type="button" className="button button--primary modal__danger" onClick={confirmCancelSubscription} disabled={cancelPending}>
                {cancelPending ? 'Cancelling...' : (cancelInTrial ? 'End trial now' : 'Confirm cancellation')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

function PricingPage({ isPro, signedIn, trialUsed, trialKnown, proModeEnabled }: { isPro: boolean; signedIn: boolean; trialUsed: boolean; trialKnown: boolean; proModeEnabled: boolean }) {
  // Visitors always see the marketing trial banner (drives signup). Signed-in
  // users only see it once /v1/me has resolved AND they haven't already used
  // a trial — prevents the brief flash of "7-day free trial" for users who
  // are about to find out they're not eligible.
  const showTrialOffer = !isPro && (!signedIn || (trialKnown && !trialUsed))
  const [message, setMessage] = useState('')
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [loading, setLoading] = useState(true)
  // Active sub: used to mark the user's current plan card and to enable the
  // "Manage subscription" CTA when they have an active Stripe checkout.
  const [activeSub, setActiveSub] = useState<SubscriptionEntry | null>(null)

  useEffect(() => {
    api<BillingPlansPayload>('/v1/billing/plans')
      .then((data) => setPlans(data.plans || []))
      .catch(() => setPlans(defaultBillingPlans()))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!signedIn) { setActiveSub(null); return }
    api<{ subscriptions?: SubscriptionEntry[] }>('/v1/subscriptions/me')
      .then((res) => {
        const subs = res.subscriptions || []
        const active = subs.find((s) => s.status === 'active') || null
        setActiveSub(active)
      })
      .catch(() => setActiveSub(null))
  }, [signedIn])

  const visiblePlans = plans.length ? plans : defaultBillingPlans()
  // Match the active sub's product_id (Stripe price_... or App Store id) to a
  // visible plan so we can render the "Current plan" badge + disable its CTA.
  const currentPlanId = (() => {
    if (!activeSub) return null
    // Try matching by stripe_price_id (web subs)
    const byPrice = visiblePlans.find((p) => p.stripe_price_id && p.stripe_price_id === activeSub.product_id)
    if (byPrice) return byPrice.id
    // App Store id starts with `com.navoption.app.pro.`; map to plan id by suffix
    if (activeSub.product_id.startsWith('com.navoption.')) {
      const suffix = activeSub.product_id.split('.').pop() || ''
      if (suffix.includes('month') && !suffix.includes('3')) return 'pro-monthly'
      if (suffix.includes('3') || suffix === 'pro.month') return 'pro-3-month'
      if (suffix.includes('year') || suffix.includes('annual')) return 'pro-annual'
    }
    return null
  })()
  const sourceIsStripe = activeSub?.source === 'stripe'

  if (!proModeEnabled) {
    return (
      <section className="container container--narrow" style={{ padding: '4rem 0' }}>
        <h1>Pricing</h1>
        <p className="muted" style={{ marginTop: 16, fontSize: 16, lineHeight: 1.6 }}>
          NavOption is currently free for every signed-in user. Paid plans may return later.
        </p>
      </section>
    )
  }

  return (
    <section className="pricing">
      <SectionHead
        kicker="Pricing"
        title={isPro ? "You're on Pro." : 'Start free. Upgrade when the workflow becomes daily.'}
        body={isPro ? undefined : 'Pro unlocks the full scanner, saved workflow, alerts, Analyze, and daily market context.'}
      />
      {isPro && !sourceIsStripe && activeSub && (
        <p className="fine-print" style={{ textAlign: 'center' }}>
          Your Pro plan is managed by the App Store (iOS). Use Settings → Subscriptions on your iPhone / iPad to update.
        </p>
      )}
      <div className="pricing-grid">
        {visiblePlans.map((plan) => {
          const isCurrent = plan.id === currentPlanId
          return (
            <article className={`price-card ${plan.highlighted ? 'price-card--pro' : ''} ${isCurrent ? 'price-card--current' : ''}`} key={plan.id}>
              <div className="price-card__top">
                <span>{plan.kicker || plan.name}</span>
                {isCurrent ? <em className="price-card__current-badge">Current plan</em> : plan.badge && <em>{plan.badge}</em>}
              </div>
              <strong>{plan.price_label}<small>{plan.price_subtext ? ` ${plan.price_subtext}` : ''}</small></strong>
              {plan.trial_days && plan.trial_days > 0 && showTrialOffer ? (
                <span className="plan-trial">{plan.trial_days}-day free trial</span>
              ) : null}
              <h3>{plan.name}</h3>
              {plan.description && <p>{plan.description}</p>}
              {!!plan.features?.length && (
                <ul>
                  {plan.features.map((feature) => <li key={feature}>{feature}</li>)}
                </ul>
              )}
              {!isPro && (
                <div className="price-card__cta">
                  {plan.paid ? (
                    <button
                      className={`button ${plan.highlighted ? 'button--primary' : 'button--outline'}`}
                      onClick={() => checkout(setMessage, plan.id)}
                    >
                      {plan.cta_label || 'Upgrade'}
                    </button>
                  ) : (
                    <button className="button button--ghost" type="button" onClick={() => setMessage('Free is the default plan. Create an account only when you want to save your workspace.')}>
                      {plan.cta_label || 'Current free plan'}
                    </button>
                  )}
                  {plan.footnote && <small className="plan-footnote">{plan.footnote}</small>}
                </div>
              )}
            </article>
          )
        })}
      </div>
      {loading && <p className="fine-print">Loading live pricing...</p>}
      <p className="form-message">{message}</p>
    </section>
  )
}

function defaultBillingPlans(): BillingPlan[] {
  return [
    {
      id: 'free',
      name: 'Free',
      kicker: 'Start here',
      price_label: '$0',
      price_subtext: 'No card required',
      cta_label: 'Current free plan',
      description: 'The core research workflow, free.',
      features: [
        'Full scanner, earnings calendar, and news',
        'Unlimited fast analyses · 3 deep analyses/month',
        'Watchlist up to 10 symbols',
        'Full Wheel curriculum in Learn',
      ],
      footnote: 'Free is the default plan. No checkout required.',
      paid: false,
    },
    {
      id: 'pro-monthly',
      name: 'Pro Monthly',
      kicker: 'Flexible',
      badge: 'Monthly',
      price_label: '$19.99',
      price_subtext: 'per month',
      cta_label: 'Start monthly',
      description: 'Full NavOption access for active options income research without a long commitment.',
      features: [
        'Full scanner and symbol snapshots',
        'Saved watchlists, presets, alerts, and positions',
        'Analyze, market context, and higher limits',
        'Position-aware earnings prep (earnings card in Analyze)',
        'Shared Web and iOS account access',
      ],
      footnote: 'Cancel anytime.',
      paid: true,
    },
    {
      id: 'pro-3-month',
      name: 'Pro 3-Month',
      kicker: 'Recommended',
      badge: 'Save 17%',
      price_label: '$49.99',
      price_subtext: 'every 3 months',
      cta_label: 'Start 7-day free trial',
      description: 'Best for testing NavOption across a complete Wheel cycle.',
      features: [
        'Everything in Pro Monthly',
        'Better fit for multi-week Wheel workflows',
        'Lower effective monthly cost',
        'Shared Web and iOS account access',
      ],
      footnote: 'Cancel before day 7 to avoid the charge.',
      trial_days: 7,
      highlighted: true,
      paid: true,
    },
    {
      id: 'pro-annual',
      name: 'Pro Annual',
      kicker: 'Best value',
      badge: 'Save 29%',
      price_label: '$169.99',
      price_subtext: 'per year',
      cta_label: 'Start 7-day free trial',
      description: 'Annual access for users making NavOption part of their daily options workflow.',
      features: [
        'Everything in Pro Monthly',
        'Best long-term price',
        'Position-aware earnings prep across full Wheel cycles',
        'Shared Web and iOS account access',
      ],
      footnote: 'Equivalent to about $14.17/month. Cancel before day 7 to avoid the charge.',
      trial_days: 7,
      paid: true,
    },
  ]
}

async function checkout(setMessage: (value: string) => void, planID?: string) {
  if (!isSignedIn()) {
    navigate('/login?next=pricing')
    return
  }
  setMessage('Opening Stripe Checkout...')
  try {
    const data = await api<{ url: string }>('/v1/billing/checkout', {
      method: 'POST',
      body: JSON.stringify(planID ? { plan_id: planID } : {}),
    })
    window.location.href = data.url
  } catch (err) {
    if ((err as ApiError).status === 401) {
      navigate('/login?next=pricing')
      return
    }
    setMessage((err as Error).message)
  }
}

function SectionHead({ kicker, title, body }: { kicker: string; title: string; body?: string }) {
  return (
    <div className="section-head">
      <span className="eyebrow">{kicker}</span>
      <h2>{title}</h2>
      {body && <p>{body}</p>}
    </div>
  )
}

function Feature({ title, body, icon }: { title: string; body: string; icon: string }) {
  return <article className="feature"><i className={`fa-solid ${icon}`} /><h3>{title}</h3><p>{body}</p></article>
}

function Stage({ n, icon, title, body }: { n: string; icon: string; title: string; body: string }) {
  return <article className="stage"><i className={`fa-solid ${icon}`} /><span>{n}</span><h3>{title}</h3><p>{body}</p></article>
}

function Module({ title, body, accent, items }: { title: string; body: string; accent: string; items?: string[] }) {
  return (
    <article className={`module module--${accent}`}>
      <h3>{title}</h3>
      <p>{body}</p>
      {items && <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>}
    </article>
  )
}

function Proof({ label, value, body }: { label: string; value: string; body: string }) {
  return <article className="proof"><span>{label}</span><strong>{value}</strong><p>{body}</p></article>
}

function Metric({ label, value, meta }: { label: string; value: string; meta: string }) {
  return <div className="metric"><span>{label}</span><strong>{value}</strong><em>{meta}</em></div>
}

function PreviewRow({ label, value, meta, hot }: { label: string; value: string; meta: string; hot?: boolean }) {
  return <div className={`preview-row${hot ? ' preview-row--hot' : ''}`}><div><span>{label}</span><strong>{value}</strong></div><em>{meta}</em></div>
}

function TextPage({ title, body }: { title: string; body: string }) {
  return <section className="text-page"><span className="eyebrow">NavOption</span><h1>{title}</h1><p>{body}</p></section>
}

const LEGAL_EFFECTIVE_DATE = 'May 16, 2026'
const CONTACT_EMAIL = 'contact@navoption.com'

function LegalPage({ eyebrow, title, intro, children }: { eyebrow: string; title: string; intro?: string; children: React.ReactNode }) {
  return (
    <section className="legal-page">
      <span className="eyebrow">{eyebrow}</span>
      <h1>{title}</h1>
      <p className="legal-effective">Effective {LEGAL_EFFECTIVE_DATE}</p>
      {intro && <p className="legal-intro">{intro}</p>}
      <div className="legal-body">{children}</div>
    </section>
  )
}

function LegalSection({ id, heading, children }: { id: string; heading: string; children: React.ReactNode }) {
  return (
    <section className="legal-section" id={id}>
      <h2>{heading}</h2>
      {children}
    </section>
  )
}

// ─── /learn — Structured wheel curriculum ────────────────────────────
// ─── Field notes ────────────────────────────────────────────────────────

type Insight = {
  id: string
  title: string
  category: string
  evidence: string
  summary: string
  body: string
}

const INSIGHT_CATEGORIES: { id: string; label: string }[] = [
  { id: 'capital', label: 'Capital' },
  { id: 'method', label: 'Methods' },
  { id: 'traps', label: 'Traps' },
  { id: 'evidence', label: 'Evidence' },
]

// The evidence level is rendered on every row and every open card. The library
// mixes capital constraints with methods NavOption has never tested, and a
// precise-looking untested rule shown bare reads as a recommendation.
const INSIGHT_EVIDENCE: Record<string, { label: string; explain: string }> = {
  structure: {
    label: 'CONSTRAINT',
    explain: 'A limit on capital or behaviour. It predicts nothing, so there is nothing to backtest.',
  },
  practice: {
    label: 'CORROBORATED',
    explain: 'Corroborated by independent sources, with its mechanism stated. Not validated by NavOption research.',
  },
  hypothesis: {
    label: 'UNTESTED',
    explain: 'A specific rule NavOption has not tested. Treat it as a question, not an answer.',
  },
}

// Renders the Markdown subset the field notes are written in: ATX headings,
// `-` bullets, `1.` ordered items, indented code blocks, and paragraphs with
// bold/italic spans. The source lives in this repo, so the subset is closed and
// a CommonMark dependency would buy nothing.
function InsightBody({ source }: { source: string }) {
  const blocks: React.ReactNode[] = []
  let paragraph: string[] = []
  let list: { ordered: boolean; items: string[] } | null = null

  const inline = (text: string): React.ReactNode[] =>
    text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean).map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>
      if (part.startsWith('*') && part.endsWith('*')) return <em key={i}>{part.slice(1, -1)}</em>
      return <Fragment key={i}>{part}</Fragment>
    })

  const flushList = () => {
    if (!list) return
    const items = list.items.map((it, i) => <li key={i}>{inline(it)}</li>)
    blocks.push(list.ordered ? <ol key={blocks.length}>{items}</ol> : <ul key={blocks.length}>{items}</ul>)
    list = null
  }
  const flushParagraph = () => {
    if (!paragraph.length) return
    blocks.push(<p key={blocks.length}>{inline(paragraph.join(' '))}</p>)
    paragraph = []
  }
  const flushAll = () => { flushParagraph(); flushList() }

  for (const raw of source.split('\n')) {
    if (raw.startsWith('    ') && raw.trim()) {
      flushAll()
      blocks.push(<pre key={blocks.length}>{raw.slice(4)}</pre>)
      continue
    }
    const line = raw.trim()
    const ordered = /^(\d+)\.\s+(.*)$/.exec(line)
    if (!line) {
      flushAll()
    } else if (line.startsWith('### ')) {
      flushAll(); blocks.push(<h4 key={blocks.length}>{line.slice(4)}</h4>)
    } else if (line.startsWith('## ')) {
      flushAll(); blocks.push(<h3 key={blocks.length}>{line.slice(3)}</h3>)
    } else if (line.startsWith('# ')) {
      flushAll(); blocks.push(<h2 key={blocks.length}>{line.slice(2)}</h2>)
    } else if (line.startsWith('- ')) {
      flushParagraph()
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] } }
      list.items.push(line.slice(2))
    } else if (ordered) {
      flushParagraph()
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] } }
      list.items.push(ordered[2])
    } else {
      flushList()
      paragraph.push(line)
    }
  }
  flushAll()
  return <div className="insight-body">{blocks}</div>
}

function InsightsPage() {
  const [insights, setInsights] = useState<Insight[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [category, setCategory] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(() => {
    const hash = window.location.hash.replace('#', '')
    return hash || null
  })

  useEffect(() => {
    // Compiled-in library — cache hard, it changes on deploy not on request.
    apiCached<{ insights: Insight[] }>(`/v1/insights?lang=${getLanguage()}`, {}, 30 * 60_000)
      .then((r) => setInsights(r.insights || []))
      .catch(() => setFailed(true))
  }, [])

  const visible = useMemo(
    () => (insights || []).filter((i) => !category || i.category === category),
    [insights, category],
  )

  return (
    <div className="insights-page">
      <header className="insights-hero">
        <h1>Field notes</h1>
        <p>
          Short notes on methods worth knowing and the ways they go wrong. Every note
          states how much evidence stands behind it — some are constraints that need no
          testing, others are specific rules we have not tested at all.
        </p>
      </header>

      {failed && <p className="insights-empty">Field notes are unavailable right now.</p>}
      {!insights && !failed && <p className="insights-empty">Loading…</p>}

      {insights && (
        <>
          <div className="insights-filter" role="group" aria-label="Filter by category">
            <button
              type="button"
              className={category === null ? 'is-active' : undefined}
              onClick={() => setCategory(null)}
            >All</button>
            {INSIGHT_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className={category === c.id ? 'is-active' : undefined}
                onClick={() => setCategory(category === c.id ? null : c.id)}
              >{c.label}</button>
            ))}
          </div>

          <div className="insights-list">
            {visible.map((insight) => {
              const ev = INSIGHT_EVIDENCE[insight.evidence]
              const open = openId === insight.id
              return (
                <article key={insight.id} id={insight.id} className={`insight-card ${open ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="insight-card__head"
                    aria-expanded={open}
                    onClick={() => setOpenId(open ? null : insight.id)}
                  >
                    <span className="insight-card__title">{insight.title}</span>
                    {ev && <span className={`insight-badge insight-badge--${insight.evidence}`}>{ev.label}</span>}
                  </button>
                  <p className="insight-card__summary">{insight.summary}</p>
                  {open && (
                    <div className="insight-card__full">
                      {ev && <p className="insight-card__evidence">{ev.explain}</p>}
                      <InsightBody source={insight.body} />
                    </div>
                  )}
                </article>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// GET /v1/learn/index?lang= — the course index the server owns, article bodies
// included. Web used to carry its own hardcoded copy of the curriculum, which
// meant two sources for one body of text and an English-only page in a Chinese
// UI. One request serves both the list and the article: there is no per-slug
// route to call.
type LearnArticle = {
  id: string
  title: string
  minutes: number
  level: string
  summary: string
  body: string
}
type LearnIndexSection = { id: string; title: string; items: LearnArticle[] }
type LearnIndexResponse = { version: string; sections: LearnIndexSection[] }

function LearnPage() {
  const { language, t } = useI18n()
  const [index, setIndex] = useState<LearnIndexResponse | null>(null)
  const [failed, setFailed] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setFailed(false)
    api<LearnIndexResponse>(`/v1/learn/index?lang=${language}`)
      .then((res) => {
        if (cancelled) return
        setIndex(res)
        setActiveId((prev) => prev || res.sections?.[0]?.items?.[0]?.id || null)
      })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [language])

  const sections = index?.sections || []
  const article = sections.flatMap((sec) => sec.items).find((it) => it.id === activeId) || null

  return (
    <div className="learn-page">
      <header className="learn-hero">
        <h1>{t('learn.title')}</h1>
        <p>{t('learn.subtitle')}</p>
      </header>

      {failed && <p className="muted">{t('learn.unavailable')}</p>}
      {!index && !failed && <p className="muted">{t('learn.loading')}</p>}

      {index && (
        <div className="learn-layout">
          <aside className="learn-nav">
            <nav>
              {sections.map((sec) => (
                <div key={sec.id} className="learn-nav__chapter">
                  <div className="learn-nav__chapter-title">{sec.title}</div>
                  <ul>
                    {sec.items.map((it) => (
                      <li key={it.id}>
                        <button
                          type="button"
                          className={activeId === it.id ? 'is-active' : undefined}
                          onClick={() => setActiveId(it.id)}
                        >
                          <span>{it.title}</span>
                          <span className="learn-nav__num">{t('learn.minutes').replace('{n}', String(it.minutes))}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <div className="learn-nav__chapter">
                <div className="learn-nav__chapter-title">{t('learn.more')}</div>
                <ul>
                  <li><a href="#glossary">{t('learn.glossary')}</a></li>
                </ul>
              </div>
            </nav>
          </aside>

          <article className="learn-body">
            {article && (
              <section className="learn-chapter">
                <header>
                  <h2>{article.title}</h2>
                  <p className="muted">{t('learn.minutes').replace('{n}', String(article.minutes))} · {article.level}</p>
                </header>
                <div className="learn-module__body"><InsightBody source={article.body} /></div>
              </section>
            )}

            <section id="glossary" className="learn-chapter">
              <header>
                <h2>{t('learn.glossary')}</h2>
                <p className="muted">{t('learn.glossary.note')}</p>
              </header>
              <dl className="learn-glossary">
                {Object.entries(GLOSSARY).map(([k, v]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </article>
        </div>
      )}
    </div>
  )
}

function TermsPage({ proModeEnabled }: { proModeEnabled: boolean }) {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms of Service"
      intro="These Terms of Service (the “Terms”) govern your access to and use of NavOption, including the navoption.com website, the admin console, the iOS application, and all related software, content, and APIs (collectively, the “Service”). By creating an account or using the Service you agree to these Terms."
    >
      <LegalSection id="acceptance" heading="1. Acceptance of Terms">
        <p>If you do not agree with these Terms, do not use the Service. We may update these Terms from time to time; we will indicate the effective date at the top of this page and, where the changes are material, give you reasonable notice in-app or by email before they take effect.</p>
      </LegalSection>

      <LegalSection id="eligibility" heading="2. Eligibility">
        <p>You must be at least 18 years old and have the legal capacity to enter into a contract in your jurisdiction. The Service is intended only for residents of jurisdictions where U.S. equity and options market data may lawfully be received. You are responsible for verifying that your use complies with local law and any rules imposed by your broker.</p>
      </LegalSection>

      <LegalSection id="account" heading="3. Your Account">
        <p>You may create an account using an email and password, Sign in with Apple, or Sign in with Google. You agree to keep your credentials secure, to provide accurate information, and to be responsible for everything that happens under your account. We may suspend or terminate accounts that we reasonably believe are shared across multiple users, are used to scrape the Service, or otherwise violate these Terms.</p>
      </LegalSection>

      <LegalSection id="subscriptions" heading="4. Subscriptions and Billing">
        {proModeEnabled ? (
          <>
            <p>The Service is offered with a Free tier and one or more paid tiers (currently Pro Monthly, Pro 3-Month, and Pro Annual). Current titles, pricing, billing periods, and trial terms are listed on the <Link to="/pricing">Pricing page</Link>.</p>
            <p><strong>Auto-renewal.</strong> Paid subscriptions automatically renew at the end of each billing period at the then-current price unless you cancel at least 24 hours before the end of the current period. Your payment method will be charged for renewal within the 24-hour period prior to the end of the current period.</p>
            <p><strong>Free trial (7 days, Pro 3-Month and Pro Annual only).</strong> If you start a 7-day free trial, you will not be charged during the trial. The trial converts automatically to a paid subscription at the end of day 7 unless you cancel at least 24 hours before the trial ends. You are entitled to at most one free trial per Apple ID, Google account, or email address. Pro Monthly does not include a free trial.</p>
            <p><strong>Managing or cancelling a subscription.</strong></p>
            <ul>
              <li><strong>iOS App Store purchases:</strong> manage or cancel in your Apple ID account settings (Settings → [Your Name] → Subscriptions). Deleting the app does not cancel the subscription.</li>
              <li><strong>Web purchases (Stripe):</strong> manage or cancel in the Stripe billing portal accessible from your <Link to="/account">Account</Link> page.</li>
            </ul>
          </>
        ) : (
          <>
            <p>Paid plans are currently paused. Signed-in users can use the core NavOption workflow without choosing a paid tier.</p>
            <p>If paid plans return later, current titles, pricing, billing periods, trial terms, renewal behavior, and cancellation instructions will be shown before checkout.</p>
            <p>Deleting your NavOption account does not automatically cancel an external subscription that may have been created before paid plans were paused.</p>
          </>
        )}
      </LegalSection>

      <LegalSection id="refunds" heading="5. Refunds">
        <p>Except where required by law, all payments are non-refundable and we do not issue refunds or credits for partial subscription periods, unused trial days, or features you did not use. App Store purchases are subject to Apple’s refund policy; you may request a refund from Apple directly. Web purchases are subject to Stripe’s standard chargeback and refund processes.</p>
      </LegalSection>

      <LegalSection id="acceptable-use" heading="6. Acceptable Use">
        <p>You agree not to (a) reverse engineer or scrape the Service, (b) resell or sublicense access, (c) attempt to bypass tier limits, rate limits, or security mechanisms, (d) upload unlawful content, or (e) use the Service to provide investment advice to third parties as a regulated activity.</p>
      </LegalSection>

      <LegalSection id="market-data" heading="7. Market Data and Educational Use">
        <p>NavOption is an educational research workspace. Market data, options quotes, scanner output, Analyze reports, Wheel suggestions, position metrics, and any other Service output are provided for informational and educational purposes only and are not personalized investment advice, an offer to buy or sell any security, or a recommendation. Data may be delayed, incomplete, or incorrect. Free-tier users see quote data delayed by at least 15 minutes. You are solely responsible for your trading decisions.</p>
      </LegalSection>

      <LegalSection id="ip" heading="8. Intellectual Property">
        <p>The Service, including all software, text, graphics, audio, video, and other content (other than user content) is owned by or licensed to NavOption and is protected by intellectual property law. Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to use the Service for your personal, non-commercial research workflow.</p>
      </LegalSection>

      <LegalSection id="user-content" heading="9. Your Content">
        <p>You retain ownership of the watchlists, presets, alerts, positions, notes, and other content you create in NavOption (“User Content”). You grant NavOption a worldwide, non-exclusive license to host, store, process, and transmit your User Content solely to operate the Service for you, to back it up, and to display it on devices you sign in to.</p>
      </LegalSection>

      <LegalSection id="termination" heading="10. Termination">
        <p>You may stop using the Service at any time and request account deletion by contacting <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We may suspend or terminate access if we reasonably believe you have violated these Terms or if continued operation poses legal, security, or commercial risk.</p>
      </LegalSection>

      <LegalSection id="warranty" heading="11. Disclaimer of Warranties">
        <p>THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, OR UNINTERRUPTED OPERATION.</p>
      </LegalSection>

      <LegalSection id="liability" heading="12. Limitation of Liability">
        <p>TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, NAVOPTION AND ITS AFFILIATES WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUES, DATA, OR TRADING LOSSES, ARISING FROM OR IN CONNECTION WITH THE SERVICE. OUR AGGREGATE LIABILITY FOR ANY CLAIM RELATED TO THE SERVICE WILL NOT EXCEED THE AMOUNTS YOU PAID US FOR THE SERVICE IN THE TWELVE MONTHS BEFORE THE CLAIM.</p>
      </LegalSection>

      <LegalSection id="indemnify" heading="13. Indemnification">
        <p>You agree to indemnify and hold harmless NavOption and its affiliates from any claim or liability arising out of your use of the Service or your violation of these Terms.</p>
      </LegalSection>

      <LegalSection id="governing-law" heading="14. Governing Law and Disputes">
        <p>These Terms are governed by the laws of the State of California, United States, without regard to its conflict-of-law principles. Any dispute will be resolved in the state or federal courts located in San Francisco County, California, except where applicable consumer protection law gives you the right to bring a claim in your local courts.</p>
      </LegalSection>

      <LegalSection id="contact" heading="15. Contact">
        <p>Questions about these Terms? Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
      </LegalSection>
    </LegalPage>
  )
}

function PrivacyPage() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      intro="This Privacy Policy explains what information NavOption collects, how we use it, and the choices you have. It applies to the NavOption website, iOS application, admin console, and APIs."
    >
      <LegalSection id="collect" heading="1. Information We Collect">
        <p>We collect only what we need to provide the Service:</p>
        <ul>
          <li><strong>Account identifiers.</strong> Email address (from email/password sign-up, Sign in with Apple, or Sign in with Google), a NavOption user ID, and an opaque subject identifier from your identity provider. Sign in with Apple may return a private relay email. Sign in with Google returns your verified email, name, and Google subject identifier; we do not request any additional Google scopes.</li>
          <li><strong>Authentication and session.</strong> Hashed passwords (email auth only), short-lived session and refresh tokens, the device fingerprint your browser sends with each request, and approximate sign-in timestamps used to detect abuse.</li>
          <li><strong>Subscription and billing.</strong> Subscription tier, plan ID, trial status, renewal/expiry dates, and the Stripe customer ID or Apple original transaction ID associated with your account. We do <em>not</em> receive or store full card numbers or banking details; payment data is handled by Apple (App Store) or Stripe.</li>
          <li><strong>Workspace data.</strong> Watchlists, scan presets, alerts, positions, and notes you create. We use this only to provide the Service and sync it across devices you sign in to.</li>
          <li><strong>Device and app data.</strong> A randomly generated install ID, app version, OS version, push notification token (if you grant permission), and crash diagnostics.</li>
          <li><strong>Server logs.</strong> IP address, user-agent, request path, and response status, used for security, abuse prevention, and debugging. Logs are typically retained for up to 90 days.</li>
        </ul>
      </LegalSection>

      <LegalSection id="use" heading="2. How We Use Information">
        <ul>
          <li>To create and operate your account and sync your workspace.</li>
          <li>To process subscriptions, trials, and renewals.</li>
          <li>To send transactional and product notifications (e.g., position roll candidates, alerts you set up). Push notifications can be disabled in your device settings or in-app.</li>
          <li>To prevent fraud, abuse, account sharing, and security incidents.</li>
          <li>To improve features, fix bugs, and produce aggregated, de-identified usage statistics.</li>
        </ul>
        <p>We do <strong>not</strong> sell or rent your personal information, and we do not use Sign in with Google data for advertising or any purpose beyond the features you sign in for. NavOption’s use of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</p>
      </LegalSection>

      <LegalSection id="third-parties" heading="3. Third-Party Services">
        <p>We rely on the following processors and providers:</p>
        <ul>
          <li><strong>Apple Inc.</strong> — Sign in with Apple, App Store, StoreKit (subscription verification), Apple Push Notification service (APNs).</li>
          <li><strong>Google LLC</strong> — Sign in with Google (OpenID Connect identity verification only).</li>
          <li><strong>Stripe, Inc.</strong> — payments, subscription management, billing portal.</li>
          <li><strong>Cloudflare, Inc.</strong> — content delivery, edge caching, DDoS protection.</li>
          <li><strong>Charles Schwab</strong> — upstream U.S. equity and options market data.</li>
          <li><strong>Our backend host</strong> — a dedicated server that runs the NavOption API, analyze service, collector, PostgreSQL, and Redis.</li>
        </ul>
      </LegalSection>

      <LegalSection id="sharing" heading="4. Sharing">
        <p>We share information with the providers above only as needed to operate the Service, and with law enforcement when we have a good-faith belief we are legally required to do so. We do not share workspace content with third parties for marketing.</p>
      </LegalSection>

      <LegalSection id="retention" heading="5. Data Retention">
        <p>We retain account and workspace data while your account is active. If you delete your account, we delete or anonymize personal data within 30 days, except records we are required to keep for tax, accounting, fraud-prevention, or legal-compliance reasons (typically up to 7 years for billing records).</p>
      </LegalSection>

      <LegalSection id="rights" heading="6. Your Rights">
        <p>Depending on where you live, you may have rights to access, correct, export, or delete your personal data, to object to or restrict processing, and to withdraw consent. To exercise these rights, email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will respond within 30 days. EU/UK (GDPR), California (CCPA/CPRA), and China (PIPL) residents have additional statutory rights that we will honor as required by law.</p>
      </LegalSection>

      <LegalSection id="children" heading="7. Children">
        <p>NavOption is not directed to anyone under 18 and we do not knowingly collect personal information from children. If you believe a child has provided us information, contact us and we will delete it.</p>
      </LegalSection>

      <LegalSection id="push" heading="8. Push Notifications">
        <p>We send push notifications only after you grant permission. You may turn them off at any time in your device settings or in-app under Settings → Notifications. Disabling notifications does not delete the underlying push token; we keep it for re-enable until you sign out or delete your account.</p>
      </LegalSection>

      <LegalSection id="security" heading="9. Security">
        <p>We protect data with TLS in transit, encryption at rest where the underlying storage supports it, scoped API tokens, password hashing (bcrypt-class), audit logging of admin actions, and least-privilege access controls. No system is perfectly secure; you are responsible for keeping your account credentials safe.</p>
      </LegalSection>

      <LegalSection id="changes" heading="10. Changes to this Policy">
        <p>We will update the effective date above when we change this Policy. For material changes, we will give you reasonable advance notice in-app or by email.</p>
      </LegalSection>

      <LegalSection id="contact-privacy" heading="11. Contact">
        <p>Privacy questions or rights requests? Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
      </LegalSection>
    </LegalPage>
  )
}

function SupportPage() {
  return (
    <LegalPage
      eyebrow="Support"
      title="Get help"
      intro={`We try to keep NavOption simple, but if anything is unclear or broken, email ${CONTACT_EMAIL} and we usually reply within one business day.`}
    >
      <LegalSection id="contact" heading="Contact">
        <p>Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with a short description of the issue, the page or screen you were on, and your account email. Screenshots help.</p>
      </LegalSection>

      <LegalSection id="subscription-help" heading="Subscriptions, trials, and refunds">
        <ul>
          <li><strong>Cancel an iOS subscription.</strong> On your iPhone, open Settings → [Your Name] → Subscriptions → NavOption → Cancel. Deleting the app alone does not cancel the subscription.</li>
          <li><strong>Cancel a web subscription.</strong> Sign in at <Link to="/account">navoption.com/account</Link>, open the Stripe billing portal, and choose Cancel.</li>
          <li><strong>Free trial.</strong> The 7-day trial on Pro 3-Month and Pro Annual converts automatically to a paid subscription unless cancelled at least 24 hours before day 7 ends.</li>
          <li><strong>Refunds.</strong> App Store purchases are subject to Apple’s refund process at <a href="https://reportaproblem.apple.com" target="_blank" rel="noreferrer">reportaproblem.apple.com</a>. For web purchases, email us and we will work with you within Stripe’s policies.</li>
        </ul>
      </LegalSection>

      <LegalSection id="account-help" heading="Account">
        <ul>
          <li>You can sign in with email and password, Sign in with Apple, or Sign in with Google. The same email address always maps to the same NavOption account.</li>
          <li>Forgot your password? Email us — password reset is currently handled by support.</li>
          <li>Want to delete your account? Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> from the email tied to the account. We will confirm before deleting.</li>
        </ul>
      </LegalSection>

      <LegalSection id="data-help" heading="Workspace data">
        <ul>
          <li>Watchlist, presets, alerts, positions, and notes sync automatically across web and iOS once you sign in.</li>
          <li>Need an export of your data? Email us — we’ll send you a JSON archive of your workspace.</li>
        </ul>
      </LegalSection>

      <LegalSection id="ios-help" heading="iOS app issues">
        <ul>
          <li>If push notifications stop arriving, turn them off and back on at iOS Settings → NavOption → Notifications, then open the app once to refresh the token.</li>
          <li>If you joined TestFlight and the build is missing, make sure you accepted the latest tester invite email.</li>
        </ul>
      </LegalSection>

      <LegalSection id="more" heading="More">
        <p>For legal questions, see our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.</p>
      </LegalSection>
    </LegalPage>
  )
}

function RedirectToHome() {
  useEffect(() => {
    const qs = window.location.search
    window.history.replaceState(null, '', '/' + qs)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [])
  return null
}

// ─── Wheel: Screeners, Calculator suite ────────────────────────────────────

// Item 5: screener landing pages
function CoveredCallsLanding({ signedIn }: { signedIn?: boolean }) {
  useEffect(() => {
    document.title = 'Covered Call Screener - NavOption'
    const meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null
    if (meta) meta.content = 'Screen covered calls on stocks you already own. Filter by yield, risk, and expiry to find income opportunities across liquid US equities.'
  }, [])
  return (
    <div className="page screener-landing">
      <div className="screener-landing__intro">
        <p className="kicker">COVERED CALLS</p>
        <h1>Covered Call Screener</h1>
        <p className="muted">
          Compare covered calls on stocks you already own. Selling a call brings in premium but caps upside above the strike and can result in assignment. If the call expires worthless, you can evaluate another trade.
        </p>
        <ul className="screener-landing__bullets">
          <li>Ranked by quality score — premium yield, liquidity, and distance from price.</li>
          <li>Delta and IV shown for every contract so you can calibrate risk at a glance.</li>
          <li>Filter by sector, DTE, or search a specific symbol.</li>
        </ul>
        {!signedIn && (
          <p style={{ marginTop: 12 }}>
            <Link to="/register" className="button button--primary">Create a free account</Link>
            <span style={{ marginLeft: 8 }}><Link to="/login" className="button button--ghost">Sign in</Link></span>
          </p>
        )}
      </div>
      <ScanPage initialSide="call" signedIn={signedIn} />
    </div>
  )
}

function CashSecuredPutsLanding({ signedIn }: { signedIn?: boolean }) {
  useEffect(() => {
    document.title = 'Cash-Secured Put Screener - NavOption'
    const meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null
    if (meta) meta.content = 'Screen cash-secured puts on stocks you want to own at a discount. Get paid premium while waiting to buy at your target price.'
  }, [])
  return (
    <div className="page screener-landing">
      <div className="screener-landing__intro">
        <p className="kicker">CASH-SECURED PUTS</p>
        <h1>Cash-Secured Put Screener</h1>
        <p className="muted">
          Get paid to wait for the price you already wanted. Sell a put below today's price, collect premium upfront, and either keep the cash when it expires worthless or buy the stock at the discount you had in mind.
        </p>
        <ul className="screener-landing__bullets">
          <li>Ranked by quality score — premium yield, collateral efficiency, and assignment risk.</li>
          <li>Gap percentage shows how far the underlying must fall before you are assigned.</li>
          <li>Filter by sector, DTE, or search a specific symbol.</li>
        </ul>
        {!signedIn && (
          <p style={{ marginTop: 12 }}>
            <Link to="/register" className="button button--primary">Create a free account</Link>
            <span style={{ marginLeft: 8 }}><Link to="/login" className="button button--ghost">Sign in</Link></span>
          </p>
        )}
      </div>
      <ScanPage initialSide="put" signedIn={signedIn} />
    </div>
  )
}

// tvVerdict maps the normalized technical-read score to a short label + CSS
// class so the UI can render a comparison pill alongside our own score.
function tvVerdict(v: number | undefined | null): { label: string; cls: string; title: string } | null {
  if (v == null || Number.isNaN(v)) return null
  if (v >= 0.5) return { label: 'Strong Buy', cls: 'pill pill--green', title: `Independent technical score ${v.toFixed(2)}`}
  if (v >= 0.1) return { label: 'Buy', cls: 'pill pill--green', title: `Independent technical score ${v.toFixed(2)}`}
  if (v <= -0.5) return { label: 'Strong Sell', cls: 'pill pill--rose', title: `Independent technical score ${v.toFixed(2)}`}
  if (v <= -0.1) return { label: 'Sell', cls: 'pill pill--rose', title: `Independent technical score ${v.toFixed(2)}`}
  return { label: 'Neutral', cls: 'pill pill--amber', title: `Independent technical score ${v.toFixed(2)}`}
}

function fmtExpShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function fmtPct(v: number, digits = 1): string {
  if (!Number.isFinite(v)) return '—'
  return `${v.toFixed(digits)}%`
}

function fmtMoney(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return '—'
  return `$${v.toFixed(digits)}`
}

function formatFundamental(v: number, unit: string): string {
  const abs = Math.abs(v)
  if (unit === 'shares') {
    if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B sh`
    if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M sh`
    return v.toLocaleString()
  }
  if (unit === 'USD/shares') return `$${v.toFixed(2)}`
  // USD: trillions / billions / millions
  if (abs >= 1e12) return `$${(v / 1e12).toFixed(2)}T`
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`
  return `$${v.toFixed(2)}`
}

// ─── Calculator suite ──────────────────────────────────────────────────────

type CalcSlug =
  | 'premium-yield' | 'csp-cash' | 'assignment-probability' | 'cost-basis-after-assignment'
  | 'iv-rank' | 'delta-pop' | 'theta-decay' | 'wheel-capital'
  | 'covered-call-max-profit' | 'wheel-position-size' | 'covered-call-vs-dividend' | 'wheel-expectancy'

const CALC_INDEX: { slug: CalcSlug; title: string; blurb: string }[] = [
  { slug: 'premium-yield', title: 'Premium Yield', blurb: 'Premium yield and annualized return on collateral.' },
  { slug: 'csp-cash', title: 'CSP Cash Required', blurb: 'Cash you must set aside to sell a cash-secured put.' },
  { slug: 'assignment-probability', title: 'Assignment Probability', blurb: 'Convert delta to an approximate assignment probability.' },
  { slug: 'cost-basis-after-assignment', title: 'Cost Basis After Assignment', blurb: 'Effective cost basis when a CSP gets assigned.' },
  { slug: 'iv-rank', title: 'IV Rank', blurb: 'Where current IV sits inside the last-year range (0–100).' },
  { slug: 'delta-pop', title: 'Delta → Probability of Profit', blurb: 'Approximate PoP for a sold option.' },
  { slug: 'theta-decay', title: 'Theta Decay', blurb: 'Estimate daily theta as a % of the credit collected.' },
  { slug: 'wheel-capital', title: 'Wheel Capital', blurb: 'Capital required to run the wheel across multiple symbols.' },
  { slug: 'covered-call-max-profit', title: 'Covered Call Max Profit', blurb: 'Max profit if the call gets assigned.' },
  { slug: 'wheel-position-size', title: 'Position Size', blurb: 'Number of contracts based on portfolio and risk per trade.' },
  { slug: 'covered-call-vs-dividend', title: 'Covered Call vs Dividend', blurb: 'Compare CC premium yield to dividend yield.' },
  { slug: 'wheel-expectancy', title: 'Wheel Expectancy', blurb: 'Expected return per wheel cycle given win-rate and outcomes.' },
]

function ToolsIndexPage() {
  return (
    <div className="page tools-index">
      <div className="tools-inner">
        <header className="page-header">
          <div className="eyebrow">WHEEL TOOLS</div>
          <h2>Wheel-strategy calculators</h2>
          <p className="page-sub">Small, focused calculators for premium yield, assignment, cost basis, and capital planning. All inputs are local — nothing is uploaded.</p>
        </header>
        <div className="tools-grid">
          {CALC_INDEX.map((c) => (
            <Link key={c.slug} to={`/tools/${c.slug}`} className="tool-card">
              <strong>{c.title}</strong>
              <p>{c.blurb}</p>
              <span className="tool-card__cta">Open →</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

function CalcField({ label, value, onChange, step = 0.01, suffix, min, max }: {
  label: string; value: number; onChange: (v: number) => void; step?: number; suffix?: string; min?: number; max?: number
}) {
  return (
    <label className="calc-field">
      <span>{label}</span>
      <span className="calc-input-wrap">
        <input type="number" step={step} min={min} max={max} value={Number.isFinite(value) ? value : ''}
          onChange={(e) => onChange(parseFloat(e.target.value || '0'))} />
        {suffix && <span className="calc-suffix">{suffix}</span>}
      </span>
    </label>
  )
}

function CalcResult({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="calc-result">
      <span className="calc-result__label">{label}</span>
      <strong className="calc-result__value">{value}</strong>
      {hint && <em className="calc-result__hint">{hint}</em>}
    </div>
  )
}

function CalcShell({ slug, title, blurb, children, formula }: {
  slug: CalcSlug; title: string; blurb: string; children: React.ReactNode; formula?: string
}) {
  const others = CALC_INDEX.filter((c) => c.slug !== slug).slice(0, 4)
  return (
    <div className="page calc-page">
      <div className="calc-inner">
        <header className="page-header">
          <div className="eyebrow">WHEEL TOOLS · <Link to="/tools">all calculators</Link></div>
          <h2>{title}</h2>
          <p className="page-sub">{blurb}</p>
        </header>
        <div className="calc-body">{children}</div>
        {formula && (
          <div className="calc-formula">
            <span className="calc-formula__label">Formula</span>
            <code>{formula}</code>
          </div>
        )}
        <div className="calc-related">
          <span className="calc-related__label">Related</span>
          <div className="calc-related__list">
            {others.map((c) => (
              <Link key={c.slug} to={`/tools/${c.slug}`} className="calc-related__chip">{c.title}</Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function CalcPremiumYield() {
  const [premium, setPremium] = useState(2.15)
  const [strike, setStrike] = useState(175)
  const [dte, setDte] = useState(30)
  const yieldPct = (premium / strike) * 100
  const apy = Number.isFinite(yieldPct) && dte > 0 ? yieldPct * (365 / dte) : NaN
  return (
    <CalcShell slug="premium-yield" title="Premium Yield Calculator" blurb="Yield = premium ÷ strike. Annualized = yield × (365 / DTE)."
      formula="APY = (premium / strike) × (365 / DTE)">
      <div className="calc-form">
        <CalcField label="Premium ($)" value={premium} onChange={setPremium} suffix="$" />
        <CalcField label="Strike ($)" value={strike} onChange={setStrike} suffix="$" />
        <CalcField label="DTE (days)" value={dte} onChange={setDte} step={1} min={1} />
      </div>
      <div className="calc-results">
        <CalcResult label="Premium yield" value={fmtPct(yieldPct, 2)} />
        <CalcResult label="Annualized" value={fmtPct(apy, 1)} hint="Linear extrapolation; assumes premium is repeated each cycle." />
      </div>
    </CalcShell>
  )
}

function CalcCspCash() {
  const [strike, setStrike] = useState(175)
  const [contracts, setContracts] = useState(1)
  const [premium, setPremium] = useState(2.15)
  const cashGross = strike * 100 * contracts
  const cashNet = cashGross - premium * 100 * contracts
  return (
    <CalcShell slug="csp-cash" title="CSP Cash Calculator" blurb="Cash a broker holds to back a cash-secured put."
      formula="Cash = strike × 100 × contracts; Net cost basis if assigned = strike − premium">
      <div className="calc-form">
        <CalcField label="Strike ($)" value={strike} onChange={setStrike} suffix="$" />
        <CalcField label="Premium ($)" value={premium} onChange={setPremium} suffix="$" />
        <CalcField label="Contracts" value={contracts} onChange={setContracts} step={1} min={1} />
      </div>
      <div className="calc-results">
        <CalcResult label="Cash required (collateral)" value={fmtMoney(cashGross, 0)} />
        <CalcResult label="Net cash if assigned" value={fmtMoney(cashNet, 0)} hint="Collateral minus credit collected." />
      </div>
    </CalcShell>
  )
}

function CalcAssignment() {
  const [delta, setDelta] = useState(0.27)
  const prob = Math.min(99, Math.abs(delta) * 100)
  return (
    <CalcShell slug="assignment-probability" title="Assignment Probability" blurb="The absolute value of delta is a standard approximation of expiration assignment probability."
      formula="P(assignment) ≈ |delta| × 100%">
      <div className="calc-form">
        <CalcField label="Delta (decimal)" value={delta} onChange={setDelta} step={0.01} min={-1} max={1} />
      </div>
      <div className="calc-results">
        <CalcResult label="Approx. assignment probability" value={fmtPct(prob, 1)} hint="Rule of thumb. Real prob also depends on IV skew, dividend, early exercise." />
      </div>
    </CalcShell>
  )
}

function CalcCostBasis() {
  const [strike, setStrike] = useState(175)
  const [premium, setPremium] = useState(2.15)
  const basis = strike - premium
  const breakdown = strike > 0 ? ((basis / strike) - 1) * 100 : 0
  return (
    <CalcShell slug="cost-basis-after-assignment" title="Cost Basis After Assignment" blurb="If a CSP is assigned, your effective cost basis is the strike minus the premium you kept."
      formula="Cost basis = strike − premium">
      <div className="calc-form">
        <CalcField label="Strike ($)" value={strike} onChange={setStrike} suffix="$" />
        <CalcField label="Premium kept ($)" value={premium} onChange={setPremium} suffix="$" />
      </div>
      <div className="calc-results">
        <CalcResult label="Effective cost basis" value={fmtMoney(basis)} />
        <CalcResult label="Discount vs strike" value={fmtPct(breakdown, 2)} />
      </div>
    </CalcShell>
  )
}

function CalcIVRank() {
  const [iv, setIv] = useState(32)
  const [low, setLow] = useState(18)
  const [high, setHigh] = useState(64)
  const denom = high - low
  const rank = denom > 0 ? Math.max(0, Math.min(100, ((iv - low) / denom) * 100)) : NaN
  return (
    <CalcShell slug="iv-rank" title="IV Rank Calculator" blurb="Where current IV sits inside the trailing 1-year range. ≥50 is the conventional 'high enough to sell' threshold."
      formula="IV Rank = (IV − low) ÷ (high − low) × 100">
      <div className="calc-form">
        <CalcField label="Current IV (%)" value={iv} onChange={setIv} suffix="%" />
        <CalcField label="1Y low IV (%)" value={low} onChange={setLow} suffix="%" />
        <CalcField label="1Y high IV (%)" value={high} onChange={setHigh} suffix="%" />
      </div>
      <div className="calc-results">
        <CalcResult label="IV Rank" value={Number.isFinite(rank) ? `${rank.toFixed(0)} / 100` : '—'} />
      </div>
    </CalcShell>
  )
}

function CalcDeltaPop() {
  const [delta, setDelta] = useState(0.27)
  const pop = (1 - Math.abs(delta)) * 100
  return (
    <CalcShell slug="delta-pop" title="Delta → Probability of Profit" blurb="For a sold option, PoP ≈ 1 − |delta|."
      formula="PoP ≈ (1 − |delta|) × 100%">
      <div className="calc-form">
        <CalcField label="Delta (decimal)" value={delta} onChange={setDelta} step={0.01} min={-1} max={1} />
      </div>
      <div className="calc-results">
        <CalcResult label="Approx. probability of profit" value={fmtPct(pop, 1)} hint="Assumes you sold the option. Buyers invert the result." />
      </div>
    </CalcShell>
  )
}

function CalcThetaDecay() {
  const [premium, setPremium] = useState(2.15)
  const [dte, setDte] = useState(30)
  const dailyTheta = dte > 0 ? premium / dte : NaN
  const dailyPct = premium > 0 && dte > 0 ? (1 / dte) * 100 : NaN
  return (
    <CalcShell slug="theta-decay" title="Theta Decay (Rough)" blurb="A linear approximation. Real theta accelerates as expiration nears."
      formula="Avg daily decay ≈ premium / DTE">
      <div className="calc-form">
        <CalcField label="Premium collected ($)" value={premium} onChange={setPremium} suffix="$" />
        <CalcField label="DTE (days)" value={dte} onChange={setDte} step={1} min={1} />
      </div>
      <div className="calc-results">
        <CalcResult label="Avg daily theta (linear)" value={fmtMoney(dailyTheta, 3)} />
        <CalcResult label="As % of premium / day" value={fmtPct(dailyPct, 2)} />
      </div>
    </CalcShell>
  )
}

function CalcWheelCapital() {
  const [symbols, setSymbols] = useState(4)
  const [avgStrike, setAvgStrike] = useState(125)
  const [contractsEach, setContractsEach] = useState(1)
  const [cushion, setCushion] = useState(15)
  const base = symbols * avgStrike * 100 * contractsEach
  const total = base * (1 + cushion / 100)
  return (
    <CalcShell slug="wheel-capital" title="Wheel Capital" blurb="Capital needed to keep wheel positions running simultaneously, including a buffer for slippage and roll cash."
      formula="Total = (symbols × avg strike × 100 × contracts) × (1 + cushion%)">
      <div className="calc-form">
        <CalcField label="# Symbols in rotation" value={symbols} onChange={setSymbols} step={1} min={1} />
        <CalcField label="Avg strike ($)" value={avgStrike} onChange={setAvgStrike} suffix="$" />
        <CalcField label="Contracts each" value={contractsEach} onChange={setContractsEach} step={1} min={1} />
        <CalcField label="Cushion (%)" value={cushion} onChange={setCushion} suffix="%" />
      </div>
      <div className="calc-results">
        <CalcResult label="Base collateral" value={fmtMoney(base, 0)} />
        <CalcResult label="With cushion" value={fmtMoney(total, 0)} />
      </div>
    </CalcShell>
  )
}

function CalcCcMaxProfit() {
  const [costBasis, setCostBasis] = useState(170)
  const [strike, setStrike] = useState(180)
  const [premium, setPremium] = useState(2.0)
  const [shares, setShares] = useState(100)
  const perShare = (strike - costBasis) + premium
  const total = perShare * shares
  const returnPct = costBasis > 0 ? (perShare / costBasis) * 100 : NaN
  return (
    <CalcShell slug="covered-call-max-profit" title="Covered Call Max Profit" blurb="If the call gets assigned at expiration, the max profit is appreciation to the strike plus the premium kept."
      formula="Max profit/share = (strike − cost basis) + premium">
      <div className="calc-form">
        <CalcField label="Cost basis ($)" value={costBasis} onChange={setCostBasis} suffix="$" />
        <CalcField label="Strike ($)" value={strike} onChange={setStrike} suffix="$" />
        <CalcField label="Premium ($)" value={premium} onChange={setPremium} suffix="$" />
        <CalcField label="Shares" value={shares} onChange={setShares} step={1} min={1} />
      </div>
      <div className="calc-results">
        <CalcResult label="Max profit / share" value={fmtMoney(perShare, 2)} />
        <CalcResult label="Total" value={fmtMoney(total, 2)} />
        <CalcResult label="Return on cost basis" value={fmtPct(returnPct, 2)} />
      </div>
    </CalcShell>
  )
}

function CalcPositionSize() {
  const [portfolio, setPortfolio] = useState(50000)
  const [riskPct, setRiskPct] = useState(2)
  const [strike, setStrike] = useState(175)
  const dollarRisk = portfolio * (riskPct / 100)
  const collateralPerContract = strike * 100
  const contracts = collateralPerContract > 0 ? Math.floor(dollarRisk / collateralPerContract) : 0
  const allocated = contracts * collateralPerContract
  return (
    <CalcShell slug="wheel-position-size" title="Position Size" blurb="How many contracts can you sell while keeping risk per trade within your portfolio budget."
      formula="contracts = floor( (portfolio × risk%) / (strike × 100) )">
      <div className="calc-form">
        <CalcField label="Portfolio ($)" value={portfolio} onChange={setPortfolio} suffix="$" />
        <CalcField label="Risk per trade (%)" value={riskPct} onChange={setRiskPct} suffix="%" />
        <CalcField label="Strike ($)" value={strike} onChange={setStrike} suffix="$" />
      </div>
      <div className="calc-results">
        <CalcResult label="Dollar risk budget" value={fmtMoney(dollarRisk, 0)} />
        <CalcResult label="Suggested contracts" value={`${contracts}`} />
        <CalcResult label="Collateral allocated" value={fmtMoney(allocated, 0)} />
      </div>
    </CalcShell>
  )
}

function CalcCcVsDividend() {
  const [stockPrice, setStockPrice] = useState(180)
  const [premium, setPremium] = useState(2.0)
  const [dte, setDte] = useState(30)
  const [dividend, setDividend] = useState(2.4)
  const ccYieldAnnual = dte > 0 ? (premium / stockPrice) * (365 / dte) * 100 : NaN
  const divYieldAnnual = stockPrice > 0 ? (dividend / stockPrice) * 100 : NaN
  const ratio = Number.isFinite(divYieldAnnual) && divYieldAnnual > 0 ? ccYieldAnnual / divYieldAnnual : NaN
  return (
    <CalcShell slug="covered-call-vs-dividend" title="Covered Call vs Dividend" blurb="Compare annualized covered-call yield to the stock's dividend yield to see how much you're getting paid for the cap on upside."
      formula="CC APY = (premium / price) × (365 / DTE); Div yield = annual_div / price">
      <div className="calc-form">
        <CalcField label="Stock price ($)" value={stockPrice} onChange={setStockPrice} suffix="$" />
        <CalcField label="Premium ($)" value={premium} onChange={setPremium} suffix="$" />
        <CalcField label="DTE (days)" value={dte} onChange={setDte} step={1} min={1} />
        <CalcField label="Annual dividend / share ($)" value={dividend} onChange={setDividend} suffix="$" />
      </div>
      <div className="calc-results">
        <CalcResult label="CC annualized yield" value={fmtPct(ccYieldAnnual, 2)} />
        <CalcResult label="Dividend yield" value={fmtPct(divYieldAnnual, 2)} />
        <CalcResult label="CC ÷ dividend" value={Number.isFinite(ratio) ? `${ratio.toFixed(2)}×` : '—'} />
      </div>
    </CalcShell>
  )
}

function CalcWheelExpectancy() {
  const [winRate, setWinRate] = useState(75)
  const [avgWin, setAvgWin] = useState(150)
  const [avgLoss, setAvgLoss] = useState(450)
  const ev = (winRate / 100) * avgWin - (1 - winRate / 100) * avgLoss
  const rr = avgLoss > 0 ? avgWin / avgLoss : NaN
  const breakevenWin = Number.isFinite(rr) && (1 + rr) > 0 ? (1 / (1 + rr)) * 100 : NaN
  return (
    <CalcShell slug="wheel-expectancy" title="Wheel Expectancy" blurb="Expected profit per cycle given a win-rate and average win/loss sizes. The breakeven win-rate shows the minimum you need to be profitable at your current win-loss ratio."
      formula="EV = P(win) × avg_win − (1 − P(win)) × avg_loss">
      <div className="calc-form">
        <CalcField label="Win rate (%)" value={winRate} onChange={setWinRate} suffix="%" />
        <CalcField label="Avg win ($)" value={avgWin} onChange={setAvgWin} suffix="$" />
        <CalcField label="Avg loss ($)" value={avgLoss} onChange={setAvgLoss} suffix="$" />
      </div>
      <div className="calc-results">
        <CalcResult label="Expected value / cycle" value={fmtMoney(ev, 2)} />
        <CalcResult label="Win:Loss ratio" value={Number.isFinite(rr) ? rr.toFixed(2) : '—'} />
        <CalcResult label="Breakeven win-rate" value={fmtPct(breakevenWin, 1)} />
      </div>
    </CalcShell>
  )
}

const CALC_COMPONENT: Record<CalcSlug, () => JSX.Element> = {
  'premium-yield': CalcPremiumYield,
  'csp-cash': CalcCspCash,
  'assignment-probability': CalcAssignment,
  'cost-basis-after-assignment': CalcCostBasis,
  'iv-rank': CalcIVRank,
  'delta-pop': CalcDeltaPop,
  'theta-decay': CalcThetaDecay,
  'wheel-capital': CalcWheelCapital,
  'covered-call-max-profit': CalcCcMaxProfit,
  'wheel-position-size': CalcPositionSize,
  'covered-call-vs-dividend': CalcCcVsDividend,
  'wheel-expectancy': CalcWheelExpectancy,
}

function CalculatorRoute({ slug }: { slug: string }) {
  const Comp = CALC_COMPONENT[slug as CalcSlug]
  if (!Comp) return <NotFound />
  return <Comp />
}

function NotFound() {
  const { t } = useI18n()
  return <section className="text-page"><h1>{t('notFound.title')}</h1><p>{t('notFound.body')}</p><Link className="button button--primary" to="/">{t('common.backHome')}</Link></section>
}

function Footer({ proModeEnabled }: { proModeEnabled: boolean }) {
  const { t } = useI18n()
  const year = new Date().getFullYear()
  return (
    <footer className="footer">
      <nav className="footer__row" aria-label={t('aria.footer')}>
        <Link to="/" className="footer__brand">NavOption</Link>
        <span className="footer__dot" aria-hidden="true">·</span>
        <Link to="/scan">{t('nav.scan')}</Link>
        <Link to="/analyze">{t('nav.analyze')}</Link>
        <Link to="/earnings">{t('nav.earnings')}</Link>
        <Link to="/uoa">{t('nav.uoa')}</Link>
        <Link to="/tools">{t('nav.tools')}</Link>
        {proModeEnabled && <Link to="/pricing">{t('nav.pricing')}</Link>}
        <Link to="/terms">{t('nav.terms')}</Link>
        <Link to="/privacy">{t('nav.privacy')}</Link>
        <Link to="/contact">{t('nav.support')}</Link>
      </nav>
      <p className="footer__legal">© {year} NavOption. {t('footer.legal')}</p>
    </footer>
  )
}

type RootElement = HTMLElement & { __navoptionRoot?: ReturnType<typeof createRoot> }

const rootElement = document.getElementById('root')! as RootElement
const root = rootElement.__navoptionRoot ?? createRoot(rootElement)
rootElement.__navoptionRoot = root
root.render(
  <React.StrictMode>
    <I18nProvider>
      <App />
    </I18nProvider>
  </React.StrictMode>,
)
